// @ts-nocheck
import React, { useState, useEffect } from "react";
import { api } from "@/api";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, ArrowRight, Check, AlertCircle, CheckCircle2, Users, Database, FileText, ClipboardCheck, CheckSquare, TrendingUp, Link2, RefreshCw,
  Mail, Phone, Bell, Send, Hash, ExternalLink, Upload, ChevronDown, ChevronRight, Edit3, FolderOpen, Tags, GitBranch, Shield,
  MessageSquare, Layers, ArrowRight as ArrowRightIcon, XCircle
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getTodayInTimezone, formatCalendarDate } from "@/components/utils/timezone";
import OliBrandIcon from "@/components/OliBrandIcon";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { createPageUrl } from "@/utils";
import { DatePicker, GradientButton, toDateOnlyString, PageHeader } from "@/components/common";
import FormRenderer from "@/components/forms/FormRenderer";

const BULK_CLIENT_REQUIRED_FIELDS = [
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "email", label: "Email" },
];

const parseBulkClientCsv = (text = "") => {
  const rows = [];
  let row = [];
  let value = "";
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"' && quoted && next === '"') {
      value += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      row.push(value.trim());
      value = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(value.trim());
      if (row.some((cell) => cell !== "")) rows.push(row);
      row = [];
      value = "";
    } else {
      value += char;
    }
  }

  row.push(value.trim());
  if (row.some((cell) => cell !== "")) rows.push(row);
  const headers = (rows[0] || []).map((header) => String(header || "").trim()).filter(Boolean);
  const dataRows = rows.slice(1).map((cells, idx) => {
    const item = { __rowNumber: idx + 2 };
    headers.forEach((header, headerIdx) => { item[header] = cells[headerIdx] || ""; });
    return item;
  });
  return { headers, rows: dataRows };
};

const buildBulkClientRecords = (rows = [], fieldMappings = [], customFieldMappings = []) => rows.map((row) => {
  const record = { custom_fields: {}, __rowNumber: row.__rowNumber };
  fieldMappings.forEach((mapping) => {
    if (mapping.csv_column && mapping.target_field) record[mapping.target_field] = row[mapping.csv_column] || "";
  });
  customFieldMappings.forEach((mapping) => {
    const csvColumn = mapping.csv_column || mapping.source_field_path;
    const targetField = mapping.custom_field || mapping.target_field || mapping.target_field_path;
    if (csvColumn && targetField) {
      const targetEntity = (mapping.target_entity || mapping.target_type) === "custom_entity" ? mapping.custom_entity : (mapping.target_entity || mapping.target_type || "client");
      if (targetEntity === "custom_field" || mapping.target_type === "custom_field") {
        record.custom_fields[targetField] = row[csvColumn] || "";
      } else if (targetEntity === "client") {
        record[targetField] = row[csvColumn] || "";
      } else {
        record.entity_mappings = {
          ...(record.entity_mappings || {}),
          [targetEntity]: {
            ...((record.entity_mappings || {})[targetEntity] || {}),
            [targetField]: row[csvColumn] || "",
          },
        };
      }
    }
  });
  return record;
});

const validateBulkClientRecords = (records = []) => {
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const seenEmails = new Set();
  const rowResults = records.map((record, idx) => {
    const errors = [];
    BULK_CLIENT_REQUIRED_FIELDS.forEach((field) => {
      if (!String(record[field.key] || "").trim()) errors.push(`${field.label} is required`);
    });
    const email = String(record.email || "").trim().toLowerCase();
    if (email && !emailRe.test(email)) errors.push("Email format is invalid");
    if (email && seenEmails.has(email)) errors.push(`Duplicate email in CSV: ${email}`);
    if (email) seenEmails.add(email);
    return {
      rowNumber: record.__rowNumber || idx + 2,
      clientName: `${record.firstName || ""} ${record.lastName || ""}`.trim(),
      email,
      errors,
    };
  });
  const failedRows = rowResults.filter((row) => row.errors.length > 0);
  return { totalRows: records.length, validRows: records.length - failedRows.length, failedRows: failedRows.length, rowResults, isValid: records.length > 0 && failedRows.length === 0 };
};

const getMissingBulkMappedColumns = (headers = [], fieldMappings = [], customFieldMappings = []) => {
  const headerSet = new Set((headers || []).map((header) => String(header || "").trim()).filter(Boolean));
  return [...fieldMappings, ...customFieldMappings]
    .map((mapping) => mapping.csv_column || mapping.source_field_path)
    .filter(Boolean)
    .filter((column, idx, arr) => arr.indexOf(column) === idx)
    .filter((column) => !headerSet.has(column));
};

const getClientDisplayName = (client) => client?.full_name || client?.fullName || `${client?.firstName || client?.first_name || ""} ${client?.lastName || client?.last_name || ""}`.trim() || client?.name || client?.email || "Unnamed client";

const normalizeSelectedClient = (client) => ({
  client_id: client.id,
  client_name: getClientDisplayName(client),
  client_email: client.email || "",
  email: client.email || "",
  phone: client.phone || client.mobile_phone || client.mobilePhone || "",
  contact_details: {
    email: client.email || "",
    phone: client.phone || client.mobile_phone || client.mobilePhone || "",
  },
  status: client.status || "",
  advisor: client.assigned_to || client.assignedTo || client.advisorId || client.advisor_id || "",
  raw_client: client,
});

const NON_CLIENT_OBJECT_ENTITY_MAP = {
  case: "Case",
  task: "Task",
  goal: "Goal",
  review: "ReviewSession",
  lead: "Lead",
  form: "Form",
  document_request: "Task",
  note: "Note",
  appointment: "Appointment",
};

const normalizeNonClientObjectType = (type = "") => {
  const value = String(type || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (["cases", "case"].includes(value)) return "case";
  if (["tasks", "task"].includes(value)) return "task";
  if (["goals", "goal"].includes(value)) return "goal";
  if (["reviews", "review", "review_session", "reviewsession"].includes(value)) return "review";
  if (["leads", "lead"].includes(value)) return "lead";
  if (["forms", "form"].includes(value)) return "form";
  if (["document_request", "document_requests", "documentrequest", "doc_request", "docreq"].includes(value)) return "document_request";
  if (["notes", "note"].includes(value)) return "note";
  if (["appointments", "appointment"].includes(value)) return "appointment";
  return value || "case";
};

const getNonClientObjectTitle = (record) => record?.title || record?.name || record?.full_name || record?.fullName || [record?.first_name || record?.firstName, record?.last_name || record?.lastName].filter(Boolean).join(" ") || record?.email || record?.id || "Selected object";
const getNonClientObjectClientId = (record) => record?.client_id || record?.clientId || record?.associated_client_id || record?.associatedClientId || record?.data?.associated_client_id || record?.metadata?.associated_client_id || "";
const getNonClientObjectAssigneeId = (record) => record?.assigned_to || record?.assignedTo || record?.assignee_id || record?.assigneeId || record?.owner_id || record?.ownerId || record?.data?.assigned_user_id || record?.metadata?.assigned_user_id || "";

const buildSelectedObjectOutput = ({ record, objectType, useAsWorkflowTarget = false, client = null }) => {
  if (!record) return {};
  const normalizedType = normalizeNonClientObjectType(objectType);
  const clientId = getNonClientObjectClientId(record);
  const assigneeId = getNonClientObjectAssigneeId(record);
  const output = {
    object_id: record.id,
    object_type: normalizedType,
    object_title: getNonClientObjectTitle(record),
    object_status: record.status || record.state || "",
    assigned_user_id: assigneeId,
    resolved_assignee_id: assigneeId,
    associated_client_id: clientId,
    client_id: clientId,
    related_object_ids: {
      client_id: clientId,
      case_id: record.case_id || record.caseId || record.data?.case_id || record.metadata?.case_id || "",
      task_id: record.task_id || record.taskId || record.data?.task_id || record.metadata?.task_id || "",
      goal_id: record.goal_id || record.goalId || record.data?.goal_id || record.metadata?.goal_id || "",
      form_id: record.form_id || record.formId || record.data?.form_id || record.metadata?.form_id || "",
    },
    selected_object_fields: record,
    raw_object: record,
    is_workflow_target: !!useAsWorkflowTarget,
    target_object_id: useAsWorkflowTarget ? record.id : "",
    target_object_type: useAsWorkflowTarget ? normalizedType : "",
  };
  output[`${normalizedType}_id`] = record.id;
  if (normalizedType === "document_request") {
    output.document_request_id = record.id;
    output.task_id = record.id;
  }
  if (client) {
    output.client_name = getClientDisplayName(client);
    output.client_email = client.email || "";
    output.client = client;
  }
  return output;
};

const hasOwnValue = (data, key) => Object.prototype.hasOwnProperty.call(data || {}, key);

const normalizeGoalRuntimeStatus = (status) => {
  const value = String(status || '').trim();
  if (!value) return '';
  const normalized = value.toLowerCase();
  const map = {
    active: 'in_progress',
    in_progress: 'in_progress',
    on_track: 'on_track',
    at_risk: 'at_risk',
    completed: 'completed',
    not_started: 'not_started',
    cancelled: 'cancelled',
  };
  return map[normalized] || normalized;
};

const buildGoalVariableContext = ({ currentData = {}, allStepData = {}, clients = [], workflowClientId = '', workflowClient = null, config = {} }) => {
  const previousClientData = Object.values(allStepData || {}).find(data => data?.client_id) || {};
  const resolvedClientId = currentData.client_id || previousClientData.client_id || workflowClientId;
  const client = clients.find(c => c.id === resolvedClientId) || workflowClient || previousClientData.raw_client || null;
  const clientName = client ? getClientDisplayName(client) : (previousClientData.client_name || currentData.client_name || '');
  const firstName = client?.first_name || client?.firstName || previousClientData.first_name || previousClientData.firstName || String(clientName).split(' ')[0] || '';
  const lastName = client?.last_name || client?.lastName || previousClientData.last_name || previousClientData.lastName || String(clientName).split(' ').slice(1).join(' ') || '';
  const targetDate = currentData.goal_target_date || config.target_date || config.inline_template?.target_date || '';
  const targetYear = targetDate ? String(new Date(targetDate).getFullYear()) : '';

  return {
    client_id: resolvedClientId || '',
    client_name: clientName || '',
    first_name: firstName || '',
    last_name: lastName || '',
    target_year: targetYear,
    'client.id': resolvedClientId || '',
    'client.name': clientName || '',
    'client.full_name': clientName || '',
    'client.first_name': firstName || '',
    'client.last_name': lastName || '',
    'client.email': client?.email || previousClientData.client_email || previousClientData.email || '',
  };
};

const interpolateRuntimeValue = (value, variables = {}) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_, key) => {
    const normalizedKey = String(key || '').trim();
    return variables[normalizedKey] ?? variables[normalizedKey.replace(/^client\./, 'client_')] ?? '';
  });
};

const getGoalRuntimeDefaults = ({ step, currentData = {}, allStepData = {}, clients = [], workflowClientId = '', workflowClient = null, preferConfiguredText = false }) => {
  const config = step?.configuration || step?.config || {};
  const inlineTemplate = config.inline_template || {};
  const variables = buildGoalVariableContext({ currentData, allStepData, clients, workflowClientId, workflowClient, config });
  const configuredTitle = config.default_title || config.title || inlineTemplate.title || inlineTemplate.name || '';
  const configuredDescription = config.default_description || config.description || inlineTemplate.description || '';
  const titleTemplate = preferConfiguredText || !hasOwnValue(currentData, 'goal_title') ? configuredTitle : currentData.goal_title;
  const descriptionTemplate = preferConfiguredText || !hasOwnValue(currentData, 'goal_description') ? configuredDescription : currentData.goal_description;

  return {
    goal_title: interpolateRuntimeValue(titleTemplate, variables),
    goal_description: interpolateRuntimeValue(descriptionTemplate, variables),
    goal_category: hasOwnValue(currentData, 'goal_category') ? currentData.goal_category : (config.category || inlineTemplate.category || ''),
    goal_priority: hasOwnValue(currentData, 'goal_priority') ? currentData.goal_priority : (config.priority || inlineTemplate.priority || ''),
    goal_target_amount: hasOwnValue(currentData, 'goal_target_amount') ? currentData.goal_target_amount : (config.target_amount || inlineTemplate.target_amount || ''),
    goal_target_date: hasOwnValue(currentData, 'goal_target_date') ? currentData.goal_target_date : (config.target_date || inlineTemplate.target_date || ''),
    goal_status: hasOwnValue(currentData, 'goal_status') ? currentData.goal_status : normalizeGoalRuntimeStatus(config.status || inlineTemplate.status || ''),
    client_id: currentData.client_id || variables.client_id || '',
    client_name: currentData.client_name || variables.client_name || '',
  };
};

const getGoalTargetClients = ({ currentData = {}, allStepData = {}, clients = [], workflowClientId = '', workflowClient = null }) => {
  const stepValues = Object.values(allStepData || {});
  const selectionData = [currentData, ...stepValues].find((data: any) => Array.isArray(data?.selected_clients) && data.selected_clients.length > 0) as any;
  if (selectionData?.selected_clients?.length) return selectionData.selected_clients;

  const selectedIds = currentData.selected_client_ids || stepValues.find((data: any) => Array.isArray(data?.selected_client_ids) && data.selected_client_ids.length > 0)?.selected_client_ids || [];
  if (selectedIds.length) {
    return selectedIds.map((clientId: string) => {
      const client = clients.find(c => c.id === clientId) || {};
      return {
        ...normalizeSelectedClient(client.id ? client : { id: clientId }),
        client_id: clientId,
        client_name: client.id ? getClientDisplayName(client) : clientId,
      };
    });
  }

  const resolvedClientId = currentData.client_id || stepValues.find((data: any) => data?.client_id)?.client_id || workflowClientId;
  if (resolvedClientId) {
    const client = clients.find(c => c.id === resolvedClientId) || workflowClient || { id: resolvedClientId };
    return [{
      ...normalizeSelectedClient(client),
      client_id: resolvedClientId,
      client_name: getClientDisplayName(client),
    }];
  }

  return [];
};

const getClientPathValue = (client, path) => {
  if (!path) return undefined;
  return String(path).split('.').reduce((current, key) => current?.[key], client);
};

const getClientFilterValue = (client, filter) => {
  const field = filter.field || "status";
  if (field === "organization") return client.organization || client.organization_name || client.organizationName || client.organization_id || client.organizationId;
  if (field === "advisor") return client.assigned_to || client.assignedTo || client.advisorId || client.advisor_id || client.owner || client.owner_id;
  if (field === "net_worth") return client.net_worth || client.netWorth || client.total_net_worth || client.totalNetWorth || client.data?.net_worth || client.data?.total_net_worth;
  if (field === "client_type") return client.client_type || client.clientType || client.type || client.account_type || client.accountType;
  if (field === "tags") return client.tags || client.data?.tags || [];
  if (field === "custom_field") return client.custom_fields?.[filter.attribute] || client.data?.custom_fields?.[filter.attribute] || client.data?.[filter.attribute];
  if (field === "attribute") return getClientPathValue(client, filter.attribute);
  return client[field] || client.data?.[field];
};

const clientMatchesFilter = (client, filter) => {
  const value = getClientFilterValue(client, filter);
  const expected = filter.value;
  const operator = filter.operator || "equals";
  if (operator === "is_not_empty") return value !== undefined && value !== null && String(value).trim() !== "";
  if (operator === "greater_than") return Number(value || 0) > Number(expected || 0);
  if (operator === "less_than") return Number(value || 0) < Number(expected || 0);
  if (Array.isArray(value)) {
    return operator === "contains"
      ? value.some((item) => String(item).toLowerCase().includes(String(expected || "").toLowerCase()))
      : value.map((item) => String(item).toLowerCase()).includes(String(expected || "").toLowerCase());
  }
  if (operator === "contains") return String(value || "").toLowerCase().includes(String(expected || "").toLowerCase());
  return String(value || "").toLowerCase() === String(expected || "").toLowerCase();
};

const getSegmentValueKeys = (value) => {
  if (!value) return [];
  if (typeof value === 'object') return [value.id, value.name, value.title, value.segment_id, value.segmentId].filter(Boolean).map((item) => String(item).toLowerCase());
  return [String(value).toLowerCase()];
};

const clientMatchesSegment = (client, segmentId, segmentMembers = [], segmentOptions = []) => {
  if (!segmentId) return false;
  const selectedSegment = segmentOptions.find((segment) => String(segment.id) === String(segmentId) || String(segment.name || segment.title || '').toLowerCase() === String(segmentId).toLowerCase());
  const segmentKeys = [segmentId, selectedSegment?.id, selectedSegment?.name, selectedSegment?.title]
    .filter(Boolean)
    .map((item) => String(item).toLowerCase());
  const rawClientSegments = [
    ...(client.segments || []),
    ...(client.segment_ids || []),
    ...(client.segmentIds || []),
    ...(client.segment_names || []),
    ...(client.segmentNames || []),
    ...(client.data?.segments || []),
    ...(client.data?.segment_ids || []),
    ...(client.data?.segmentIds || []),
    ...(client.data?.segment_names || []),
    ...(client.data?.segmentNames || []),
  ];
  const clientSegments = rawClientSegments.flatMap((item) => {
    const keys = getSegmentValueKeys(item);
    const matchingSegment = segmentOptions.find((segment) => keys.includes(String(segment.id).toLowerCase()));
    return [...keys, matchingSegment?.name, matchingSegment?.title].filter(Boolean).map((value) => String(value).toLowerCase());
  });
  return clientSegments.some((clientSegment) => segmentKeys.includes(clientSegment)) || segmentMembers.some((member) => segmentKeys.includes(String(member.segmentId || member.segment_id).toLowerCase()) && String(member.objectId || member.object_id || member.client_id) === String(client.id));
};

const resolveSelectedClients = (config = {}, stepData = {}, clients = [], segmentMembers = [], segmentOptions = []) => {
  const source = config.selection_source || config.selection_mode || "manual";
  let selected = [];
  if (source === "segment") {
    selected = clients.filter((client) => clientMatchesSegment(client, stepData.segment_id || config.segment_id || stepData.segment_name || config.segment_name, segmentMembers, segmentOptions));
  } else if (source === "filters") {
    const filters = stepData.filters || config.filters || [];
    selected = filters.length ? clients.filter((client) => filters.every((filter) => clientMatchesFilter(client, filter))) : [];
  } else {
    const ids = stepData.selected_client_ids || config.selected_client_ids || (stepData.client_id ? [stepData.client_id] : config.client_id ? [config.client_id] : []);
    selected = clients.filter((client) => ids.includes(client.id));
  }
  if (config.allow_multiple === false) selected = selected.slice(0, 1);
  return selected.map(normalizeSelectedClient);
};

const getUserDisplayName = (user) => user?.full_name || user?.fullName || user?.name || user?.email || "Unnamed user";

const normalizeSelectedUser = (user) => {
  if (!user) return {};
  const userName = getUserDisplayName(user);
  const role = user.role || user.user_role || user.data?.role || "";
  const team = user.team || user.team_name || user.teamName || user.team_id || user.teamId || user.data?.team || "";
  return {
    user_id: user.id,
    user_name: userName,
    user_email: user.email || "",
    user_role: role,
    user_team: team,
    assigned_user_id: user.id,
    assigned_user_name: userName,
    assigned_user_email: user.email || "",
    assigned_user_role: role,
    assigned_user_team: team,
    assignee: {
      user_id: user.id,
      name: userName,
      email: user.email || "",
      role,
      team,
    },
  };
};

const findPriorAssigneeData = (allStepData = {}) => {
  const entries = Object.entries(allStepData || {}).sort(([a], [b]) => {
    const ai = Number(String(a).match(/step_(\d+)/)?.[1] ?? -1);
    const bi = Number(String(b).match(/step_(\d+)/)?.[1] ?? -1);
    return bi - ai;
  });
  return entries.map(([, data]) => data).find((data) => data?.user_id || data?.assigned_user_id || data?.assigned_user_email || data?.assigned_to);
};

const findPriorCaseData = (allStepData = {}) => {
  const entries = Object.entries(allStepData || {}).sort(([a], [b]) => {
    const ai = Number(String(a).match(/step_(\d+)/)?.[1] ?? -1);
    const bi = Number(String(b).match(/step_(\d+)/)?.[1] ?? -1);
    return bi - ai;
  });
  return entries.map(([, data]) => data).find((data) => data?.case_id || data?.caseId);
};

const resolveAssignableUser = ({ assignment = {}, currentData = {}, allStepData = {}, users = [], workflowClient = null }) => {
  const strategy = currentData.assignment_strategy || assignment.assignment_strategy || assignment.assignee_source || (currentData.assigned_to || assignment.assigned_to ? "specific_user" : "none");
  const priorAssignee = findPriorAssigneeData(allStepData);
  if (strategy === "previous_assignee" || strategy === "prior_assignee") {
    const priorId = priorAssignee?.user_id || priorAssignee?.assigned_user_id || priorAssignee?.assigned_to;
    return users.find((user) => user.id === priorId || user.email === priorAssignee?.assigned_user_email) || null;
  }
  if (strategy === "client_owner") {
    const owner = workflowClient?.assigned_to || workflowClient?.assignedTo || workflowClient?.advisorId || workflowClient?.advisor_id || workflowClient?.owner_id || workflowClient?.owner;
    return users.find((user) => user.id === owner || user.email === owner) || null;
  }
  if (strategy === "role") {
    const role = currentData.assigned_role || assignment.assigned_role;
    return users.find((user) => String(user.role || user.user_role || "").toLowerCase() === String(role || "").toLowerCase()) || null;
  }
  if (strategy === "team") {
    const teamId = currentData.assigned_team_id || assignment.assigned_team_id;
    return users.find((user) => String(user.team_id || user.teamId || user.data?.team_id || "") === String(teamId || "")) || null;
  }
  if (strategy === "round_robin") {
    const role = currentData.round_robin_role || assignment.round_robin_role || currentData.assigned_role || assignment.assigned_role;
    const candidates = role ? users.filter((user) => String(user.role || user.user_role || "").toLowerCase() === String(role).toLowerCase()) : users;
    return candidates[0] || null;
  }
  const selected = currentData.assigned_to || currentData.assigned_user_id || currentData.user_id || assignment.assigned_to || assignment.assigned_user_id || assignment.user_id;
  return users.find((user) => user.id === selected || user.email === selected) || null;
};

// Order a form template's questions using build_structure.flow.questionOrder
// when present, then falling back to per-question build_layout.order /
// display_order.
function orderTemplateQuestionsForRunner(questions: any[] = [], buildStructure?: any): any[] {
  if (!Array.isArray(questions) || questions.length === 0) return [];

  const explicitOrder: string[] = Array.isArray(buildStructure?.flow?.questionOrder)
    ? buildStructure.flow.questionOrder
    : Array.isArray(buildStructure?.questionOrder)
      ? buildStructure.questionOrder
      : [];

  if (explicitOrder.length > 0) {
    const byId = new Map(questions.map((q, i) => [q.id ?? q.name ?? String(i), q]));
    const seen = new Set<string>();
    const ordered: any[] = [];
    for (const id of explicitOrder) {
      const q = byId.get(id);
      if (q) { ordered.push(q); seen.add(id); }
    }
    questions.forEach((q, i) => {
      const id = q.id ?? q.name ?? String(i);
      if (!seen.has(id)) ordered.push(q);
    });
    return ordered;
  }

  const withIndex = questions.map((q, i) => ({ q, i }));
  const hasLayout = withIndex.some(({ q }) => q?.build_layout?.order != null || q?.display_order != null || q?.order != null);
  if (!hasLayout) return questions;

  return withIndex
    .sort((a, b) => {
      const aOrder = a.q?.build_layout?.order ?? a.q?.display_order ?? a.q?.order ?? a.i;
      const bOrder = b.q?.build_layout?.order ?? b.q?.display_order ?? b.q?.order ?? b.i;
      if (aOrder !== bOrder) return Number(aOrder) - Number(bOrder);
      return a.i - b.i;
    })
    .map(({ q }) => q);
}

// Renders the actual form template inline in the manual runner so the walker
// can fill and submit it. Submitted answers are captured via onSubmit and
// stored on currentStepData; the parent uses that to enable Next Step.
function FormCreateAssignRunnerTemplatePreview({
  templateId,
  initialAnswers,
  submitted,
  onSubmitResponse,
}: {
  templateId: string;
  initialAnswers?: Record<string, any>;
  submitted?: boolean;
  onSubmitResponse: (answers: Record<string, any>) => void;
}) {
  const { data: template, isLoading, error } = useQuery({
    queryKey: ["wf-runner-form-template", templateId],
    queryFn: () => api.entities.Form.get(templateId),
    enabled: !!templateId,
  });

  if (!templateId) return null;

  const rawQuestions =
    template?.questions?.length ? template.questions
    : Array.isArray(template?.schema) && template.schema.length ? template.schema
    : Array.isArray(template?.data?.questions) ? template.data.questions
    : Array.isArray(template?.data?.schema) ? template.data.schema
    : [];

  const buildStructure = template?.build_structure || template?.buildStructure || template?.data?.build_structure;
  const questions = orderTemplateQuestionsForRunner(rawQuestions, buildStructure);

  const normalizedForm = template ? {
    title: template.title || template.name || "Form",
    description: template.description || template.data?.description || "",
    questions,
    calculation_groups: template.calculation_groups || template.data?.calculation_groups || [],
  } : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-800">
          {submitted ? "Response submitted" : "Complete the assigned form"}
        </p>
        {template && (
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
            {questions.length} question{questions.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {isLoading && <p className="text-xs text-slate-500">Loading form…</p>}
      {error && <p className="text-xs text-red-600">Couldn't load the form template.</p>}
      {normalizedForm && questions.length === 0 && (
        <p className="text-xs text-amber-700">This template has no questions yet. Add questions in the Form Builder.</p>
      )}
      {normalizedForm && questions.length > 0 && (
        <div className="border border-slate-100 rounded-lg p-3 bg-white max-h-[520px] overflow-y-auto">
          <FormRenderer
            form={normalizedForm as any}
            readOnly={!!submitted}
            initialAnswers={initialAnswers}
            submitLabel="Submit response"
            onSubmit={async (answers) => onSubmitResponse(answers)}
            pageSize={0}
          />
        </div>
      )}
    </div>
  );
}

export default function WorkflowExecutor({ template: propTemplate, onComplete, onCancel, executionId: propsExecutionId, householdId: propsHouseholdId, clientId: propsClientId }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isCompleting, setIsCompleting] = useState(false);
  const [isEditingCompleted, setIsEditingCompleted] = useState(false);
  
  // Get current user - MUST be at top before any conditionals
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => api.auth.me(),
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.entities.Client.list(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.entities.User.list(),
  });

  const { data: caseTemplates = [] } = useQuery({
    queryKey: ['case-templates'],
    queryFn: () => api.entities.CaseTemplate.list(),
  });

  const { data: cases = [] } = useQuery({
    queryKey: ['cases'],
    queryFn: () => api.entities.Case.list(),
  });

  const { data: selectableTasks = [] } = useQuery({
    queryKey: ['workflow-selectable-tasks'],
    queryFn: () => api.entities.Task.list('-updated_date', 300).catch(() => []),
  });

  const { data: selectableGoals = [] } = useQuery({
    queryKey: ['workflow-selectable-goals'],
    queryFn: () => api.entities.Goal.list('-updated_date', 300).catch(() => []),
  });

  const { data: selectableReviews = [] } = useQuery({
    queryKey: ['workflow-selectable-reviews'],
    queryFn: () => api.entities.ReviewSession.list('-updated_date', 300).catch(() => []),
  });

  const { data: selectableLeads = [] } = useQuery({
    queryKey: ['workflow-selectable-leads'],
    queryFn: () => api.entities.Lead.list('-updated_date', 300).catch(() => []),
  });

  const { data: selectableForms = [] } = useQuery({
    queryKey: ['workflow-selectable-forms'],
    queryFn: () => api.entities.Form.list('-updated_date', 300).catch(() => []),
  });

  const { data: selectableNotes = [] } = useQuery({
    queryKey: ['workflow-selectable-notes'],
    queryFn: () => api.entities.Note.list('-updated_date', 300).catch(() => []),
  });

  const { data: selectableAppointments = [] } = useQuery({
    queryKey: ['workflow-selectable-appointments'],
    queryFn: () => api.entities.Appointment.list('-updated_date', 300).catch(() => []),
  });

  const { data: taskTemplates = [] } = useQuery({
    queryKey: ['task-templates'],
    queryFn: () => api.entities.TaskTemplate.list(),
  });

  const { data: customFields = [] } = useQuery({
    queryKey: ['custom-client-fields'],
    queryFn: () => api.entities.CustomClientField.filter({ is_active: true }),
  });

  const { data: investmentModels = [] } = useQuery({
    queryKey: ['investment-models'],
    queryFn: () => api.entities.InvestmentModel.list(),
  });

  const selectableObjectLists = React.useMemo(() => ({
    case: cases || [],
    task: selectableTasks || [],
    goal: selectableGoals || [],
    review: selectableReviews || [],
    lead: selectableLeads || [],
    form: selectableForms || [],
    note: selectableNotes || [],
    appointment: selectableAppointments || [],
    document_request: (selectableTasks || []).filter((task) => String(task.category || task.type || task.metadata?.object_type || task.data?.object_type || '').toLowerCase().includes('document')),
  }), [cases, selectableTasks, selectableGoals, selectableReviews, selectableLeads, selectableForms, selectableNotes, selectableAppointments]);
  
  // Get executionId from props first, then fallback to URL parsing
  const urlExecutionId = React.useMemo(() => {
    if (propsExecutionId) return propsExecutionId;
    
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
    
    return searchParams.get('id') || searchParams.get('executionId') || 
           hashParams.get('id') || hashParams.get('executionId');
  }, [propsExecutionId]);
  
  const urlHouseholdId = React.useMemo(() => {
    if (propsHouseholdId) return propsHouseholdId;
    
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
    
    return searchParams.get('householdId') || hashParams.get('householdId');
  }, [propsHouseholdId]);
  
  const urlClientId = React.useMemo(() => {
    if (propsClientId) return propsClientId;
    
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
    
    return searchParams.get('clientId') || hashParams.get('clientId');
  }, [propsClientId]);
  
  // Get referrer from URL to know where to navigate back
  const referrerPage = React.useMemo(() => {
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
    
    return searchParams.get('referrer') || hashParams.get('referrer');
  }, []);
  
  // Normalize step_data keys: convert "step0" → "step_0" for consistency
  const normalizeStepDataKeys = (data) => {
    if (!data || typeof data !== 'object') return data || {};
    const normalized = {};
    for (const [key, value] of Object.entries(data)) {
      // Convert "step0", "step1" etc. to "step_0", "step_1"
      const match = key.match(/^step(\d+)$/);
      if (match) {
        normalized[`step_${match[1]}`] = value;
      } else {
        normalized[key] = value;
      }
    }
    return normalized;
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepData, setStepData] = useState({});
  const [executionId, setExecutionId] = useState(urlExecutionId || null);
  const [oliResponse, setOliResponse] = useState("");
  const [oliLoading, setOliLoading] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState({ current: 0, total: 0, currentStepLabel: '' });
  const [expandedReviewSteps, setExpandedReviewSteps] = useState<Record<string, boolean>>({});
  const [createdEntities, setCreatedEntities] = useState({
    clients: [],
    cases: [],
    tasks: [],
    goals: [],
    documents: [],
  });
  const [segmentSearch, setSegmentSearch] = useState('');

  // Fetch workflow templates if we're starting a new workflow
  const { data: workflowTemplates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['workflow-templates'],
    queryFn: () => api.entities.WorkflowTemplate.list(),
    enabled: !urlExecutionId && (!!urlHouseholdId || !!urlClientId),
  });

  // Fetch execution if we have an executionId from URL (but not if propTemplate provided)
  const { data: execution, isLoading: isLoadingExecution, error: executionError } = useQuery({
    queryKey: ['workflow-execution', urlExecutionId],
    queryFn: async () => {
      if (!urlExecutionId) {
        throw new Error('No execution ID provided');
      }
      const execs = await api.entities.WorkflowExecution.filter({ id: urlExecutionId });
      if (!execs || execs.length === 0) {
        throw new Error('Workflow execution not found');
      }
      return execs[0];
    },
    enabled: !!urlExecutionId && !propTemplate,
    retry: 1,
    retryDelay: 1000,
  });

  // Resolve the client linked to this workflow execution (needed for custom field values query)
  const workflowClientId = execution?.client_id || execution?.clientId || propTemplate?.execution?.client_id || propTemplate?.execution?.clientId || urlClientId;

  const { data: customFieldValues = [] } = useQuery({
    queryKey: ['custom-field-values', workflowClientId],
    queryFn: () => api.entities.CustomClientFieldValue.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  // Fetch related entity data for data mapping pre-fill
  const { data: clientFinancialData } = useQuery({
    queryKey: ['client-financial-data', workflowClientId],
    queryFn: async () => {
      const data = await api.entities.ClientFinancialData.filter({ client_id: workflowClientId, is_current: true });
      return data?.[0] || null;
    },
    enabled: !!workflowClientId,
  });

  const { data: clientHousehold } = useQuery({
    queryKey: ['client-household-workflow', workflowClientId],
    queryFn: async () => {
      const client = clients.find(c => c.id === workflowClientId);
      if (!client?.household_id) return null;
      const households = await api.entities.Household.filter({ id: client.household_id });
      return households?.[0] || null;
    },
    enabled: !!workflowClientId && clients.length > 0,
  });

  const { data: clientPlanningProfile } = useQuery({
    queryKey: ['client-planning-profile', workflowClientId],
    queryFn: async () => {
      const profiles = await api.entities.ClientPlanningProfile.filter({ client_id: workflowClientId, is_base_profile: true });
      return profiles?.[0] || null;
    },
    enabled: !!workflowClientId,
  });

  const { data: clientInsurancePolicies = [] } = useQuery({
    queryKey: ['client-insurance-policies', workflowClientId],
    queryFn: () => api.entities.InsurancePolicy.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  // Fetch multi-record entities for data mapping pre-fill (most recent record)
  const { data: clientGoals = [] } = useQuery({
    queryKey: ['client-goals-workflow', workflowClientId],
    queryFn: () => api.entities.Goal.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  const { data: clientTasks = [] } = useQuery({
    queryKey: ['client-tasks-workflow', workflowClientId],
    queryFn: () => api.entities.Task.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  const { data: clientNotes = [] } = useQuery({
    queryKey: ['client-notes-workflow', workflowClientId],
    queryFn: () => api.entities.Note.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  const { data: clientAppointments = [] } = useQuery({
    queryKey: ['client-appointments-workflow', workflowClientId],
    queryFn: () => api.entities.Appointment.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  const { data: clientCommunications = [] } = useQuery({
    queryKey: ['client-communications-workflow', workflowClientId],
    queryFn: () => api.entities.Communication.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  const { data: clientDocuments = [] } = useQuery({
    queryKey: ['client-documents-workflow', workflowClientId],
    queryFn: () => api.entities.Document.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  const { data: clientCases = [] } = useQuery({
    queryKey: ['client-cases-workflow', workflowClientId],
    queryFn: () => api.entities.Case.filter({ client_id: workflowClientId }),
    enabled: !!workflowClientId,
  });

  const { data: allSegments = [] } = useQuery({
    queryKey: ['client-segments-workflow'],
    queryFn: () => api.entities.ClientSegment.list(),
  });

  const { data: objectSegments = [] } = useQuery({
    queryKey: ['object-segments-workflow'],
    queryFn: () => api.entities.ObjectSegment.list().catch(() => []),
  });

  const { data: objectSegmentMembers = [] } = useQuery({
    queryKey: ['object-segment-members-workflow'],
    queryFn: () => api.entities.ObjectSegmentMember.list().catch(() => []),
  });

  const clientSegmentOptions = React.useMemo(() => [
    ...allSegments.map((segment) => ({ ...segment, source: 'client_segment' })),
    ...objectSegments.filter((segment) => String(segment.objectType || segment.object_type || '').toLowerCase() === 'client').map((segment) => ({ ...segment, source: 'object_segment' })),
  ], [allSegments, objectSegments]);

  // Fetch template if we have an execution (but not if propTemplate provided)
  const { data: template, isLoading: isLoadingTemplate, error: templateError } = useQuery({
    queryKey: ['workflow-template', execution?.template_id],
    queryFn: async () => {
      // If execution already has steps, use them directly
      if (execution?.steps && execution.steps.length > 0) {
        return {
          id: execution.template_id,
          name: execution.workflow_name,
          description: execution.workflow_description,
          steps: execution.steps
        };
      }
      // Otherwise fetch from template
      if (!execution?.template_id) {
        throw new Error('No template ID found in execution');
      }
      const templates = await api.entities.WorkflowTemplate.filter({ id: execution.template_id });
      if (!templates || templates.length === 0) {
        throw new Error('Template not found');
      }
      return templates[0];
    },
    enabled: !!execution && (!!execution?.template_id || (!!execution?.steps && execution.steps.length > 0)) && !propTemplate,
    retry: 1,
    retryDelay: 1000,
  });

  const updateExecutionMutation = useMutation({
    mutationFn: ({ id, data }) => api.entities.WorkflowExecution.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
    },
  });

  const createExecutionMutation = useMutation({
    mutationFn: (data) => api.entities.WorkflowExecution.create(data),
    onSuccess: (execution) => {
      setExecutionId(execution.id);
      queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
    },
  });

  // Initialize state from loaded execution — only once per execution load
  const hasInitializedRef = React.useRef(null); // tracks which execution ID we initialized from
  useEffect(() => {
    if (execution && !propTemplate && template) {
      // Skip re-initialization if we already initialized this execution
      if (hasInitializedRef.current === execution.id) return;
      hasInitializedRef.current = execution.id;
      
      // Cap the step index to the last valid step
      const maxStepIndex = (template.steps?.length || 1) - 1;
      const validStepIndex = Math.min(execution.current_step_index || 0, maxStepIndex);
      
      setCurrentStepIndex(validStepIndex);
      setStepData(normalizeStepDataKeys(execution.step_data));
      setExecutionId(execution.id);
      setBranchDecisions(execution.branch_decisions || {});
      setCreatedEntities(execution.created_entities || {
        clients: [],
        cases: [],
        tasks: [],
        goals: [],
        documents: [],
      });
      
      // Update status to in_progress when starting
      if (execution.status === 'not_started' && execution.id) {
        updateExecutionMutation.mutate({
          id: execution.id,
          data: { 
            status: 'in_progress',
            started_date: new Date().toISOString()
          }
        });
      }
    } else if (propTemplate?.execution && propTemplate?.steps) {
      // Skip re-initialization if we already initialized this execution
      const propExecId = propTemplate.execution.id || 'prop';
      if (hasInitializedRef.current === propExecId) return;
      hasInitializedRef.current = propExecId;
      
      // Cap the step index to the last valid step
      const maxStepIndex = (propTemplate.steps?.length || 1) - 1;
      const validStepIndex = Math.min(propTemplate.execution.current_step_index || 0, maxStepIndex);
      
      setCurrentStepIndex(validStepIndex);
      setStepData(normalizeStepDataKeys(propTemplate.execution.step_data));
      setExecutionId(propTemplate.execution.id || null);
      setBranchDecisions(propTemplate.execution.branch_decisions || {});
      setCreatedEntities(propTemplate.execution.created_entities || {
        clients: [],
        cases: [],
        tasks: [],
        goals: [],
        documents: [],
      });
      
      // Update status to in_progress when starting
      if (propTemplate.execution.status === 'not_started' && propTemplate.execution.id) {
        updateExecutionMutation.mutate({
          id: propTemplate.execution.id,
          data: { 
            status: 'in_progress',
            started_date: new Date().toISOString()
          }
        });
      }
    }
  }, [execution, propTemplate, template]);

  // Use propTemplate if provided, otherwise use the fetched template
  const activeTemplate = propTemplate || template;

  // Find the client record
  const workflowClient = clients.find(c => c.id === workflowClientId);

  const findClientForUser = React.useCallback((user) => {
    if (!user) return null;
    const userEmail = String(user.email || user.user_email || '').toLowerCase();
    const userId = String(user.id || user.user_id || '');
    return clients.find((client) => {
      const clientEmail = String(client.email || '').toLowerCase();
      const linkedUserId = String(client.user_id || client.userId || client.portal_user_id || client.portalUserId || '');
      return (userEmail && clientEmail === userEmail) || (userId && linkedUserId === userId);
    }) || null;
  }, [clients]);

  const resolveTaskClientId = React.useCallback(({ currentData = {}, allStepData = {}, assignee = null } = {}) => {
    const previousClientData = Object.values(allStepData || {}).find(data => data?.client_id);
    const assigneeClient = findClientForUser(assignee);
    return currentData.client_id || previousClientData?.client_id || workflowClientId || assigneeClient?.id || '';
  }, [findClientForUser, workflowClientId]);

  const resolveCaseClientId = React.useCallback(({ currentData = {}, allStepData = {}, assignee = null } = {}) => {
    return resolveTaskClientId({ currentData, allStepData, assignee });
  }, [resolveTaskClientId]);

  const resolveCaseId = React.useCallback(({ currentData = {}, stepConfig = {}, allStepData = {}, clientId = '' } = {}) => {
    const priorCase = findPriorCaseData(allStepData);
    const caseLinkMode = currentData.case_link_mode || stepConfig.case_link_mode || (currentData.case_id || stepConfig.case_id ? 'specific_case' : 'previous_case');
    if (caseLinkMode === 'none') return '';
    const selectedCaseId = currentData.case_id || currentData.caseId || stepConfig.case_id || stepConfig.caseId || '';
    if (selectedCaseId) return selectedCaseId;
    if (caseLinkMode === 'previous_case' || caseLinkMode === 'auto') return priorCase?.case_id || priorCase?.caseId || '';
    return '';
  }, []);

  const resolveWorkflowText = React.useCallback((value, allStepData = {}) => {
    if (!value || typeof value !== 'string') return value || '';
    const getPathValue = (source, path) => String(path || '').split('.').reduce((acc, part) => acc?.[part], source);
    return value.replace(/\{\{([^}]+)\}\}/g, (_, rawPath) => {
      const path = String(rawPath || '').trim();
      if (!path) return '';
      if (path.startsWith('client.')) return getPathValue(workflowClient, path.replace(/^client\./, '')) ?? '';
      if (path === 'client.id') return workflowClientId || '';
      if (path.startsWith('steps.')) {
        const [, stepId, ...fieldParts] = path.split('.');
        const stepIndex = (activeTemplate?.steps || []).findIndex((step) => step.id === stepId);
        const stepOutput = stepIndex >= 0 ? allStepData[`step_${stepIndex}`] : null;
        return getPathValue(stepOutput, fieldParts.join('.')) ?? '';
      }
      return getPathValue({ workflow: activeTemplate, enrolled_object: workflowClient, client: workflowClient }, path) ?? '';
    });
  }, [activeTemplate, workflowClient, workflowClientId]);

  // Helper to get field value by data_mapping target_type and target_field path
  const getClientFieldValue = React.useCallback((targetField, targetType = 'client') => {
    if (targetType === 'custom_field') {
      const fieldValue = customFieldValues.find(cfv => cfv.field_key === targetField);
      return fieldValue?.value;
    }

    // Resolve the source entity data based on target_type
    let sourceData = null;
    switch (targetType) {
      case 'client':
        sourceData = workflowClient;
        break;
      case 'financial_data':
        sourceData = clientFinancialData;
        break;
      case 'household':
        sourceData = clientHousehold;
        break;
      case 'planning_profile':
        sourceData = clientPlanningProfile;
        break;
      case 'insurance_policy':
        sourceData = clientInsurancePolicies?.[0];
        break;
      case 'goal':
        sourceData = clientGoals?.[0];
        break;
      case 'task':
        sourceData = clientTasks?.[0];
        break;
      case 'note':
        sourceData = clientNotes?.[0];
        break;
      case 'appointment':
        sourceData = clientAppointments?.[0];
        break;
      case 'communication':
        sourceData = clientCommunications?.[0];
        break;
      case 'document':
        sourceData = clientDocuments?.[0];
        break;
      case 'case':
        sourceData = clientCases?.[0];
        break;
      default:
        return undefined;
    }

    if (!sourceData) return undefined;
    if (targetField.includes('.')) {
      const [parent, child] = targetField.split('.');
      return sourceData[parent]?.[child];
    }
    return sourceData[targetField];
  }, [workflowClient, customFieldValues, clientFinancialData, clientHousehold, clientPlanningProfile, clientInsurancePolicies, clientGoals, clientTasks, clientNotes, clientAppointments, clientCommunications, clientDocuments, clientCases]);

  // Track which step index we've already resolved DB bindings for (to prevent infinite re-renders)
  const lastResolvedStepRef = React.useRef<number>(-1);

  // Pre-fill mapped fields from client data and input bindings when entering a step
  useEffect(() => {
    if (!activeTemplate?.steps || activeTemplate.steps.length === 0) return;
    
    // Don't pre-fill from DB if workflow is completed (unless editing)
    const isViewingCompleted = ((execution?.status === 'completed' && execution?.completed_date) || 
                                (propTemplate?.execution?.status === 'completed' && propTemplate?.execution?.completed_date)) &&
                               !isEditingCompleted;
    if (isViewingCompleted) return;
    
    const currentStep = activeTemplate.steps[currentStepIndex];
    if (!currentStep) return;

    const stepKey = `step_${currentStepIndex}`;
    const config = currentStep.configuration || currentStep.config || {};
    const allFields = config.fields || [];

    // Always resolve previous_step bindings (source may have changed)
    setStepData(prev => {
      const existingData = prev[stepKey] || {};
      const bindingUpdates = {};

      // Resolve previous_step bindings from latest state
      allFields.forEach(f => {
        if (f.input_source === 'previous_step' && f.input_step_id && f.input_field) {
          const sourceStepIndex = activeTemplate.steps.findIndex(s => s.id === f.input_step_id);
          if (sourceStepIndex !== -1) {
            const sourceData = prev[`step_${sourceStepIndex}`];
            if (sourceData) {
              const value = sourceData[f.input_field];
              if (value !== undefined && value !== null && value !== '') {
                const fieldId = f.name || f.id;
                bindingUpdates[fieldId] = typeof value === 'object' ? JSON.stringify(value) : String(value);
              }
            }
          }
        } else if (f.input_source === 'static' && f.input_static_value) {
          const fieldId = f.name || f.id;
          bindingUpdates[fieldId] = f.input_static_value;
        }
      });

      // For previous_step bindings, ALWAYS overwrite (source value may have changed)
      const merged = { ...existingData, ...bindingUpdates };
      
      if (Object.keys(bindingUpdates).length === 0 && lastResolvedStepRef.current === currentStepIndex) {
        return prev; // No changes needed
      }

      return { ...prev, [stepKey]: merged };
    });

    // Only resolve DB/entity bindings once per step (not on every navigation)
    if (lastResolvedStepRef.current === currentStepIndex) return;
    lastResolvedStepRef.current = currentStepIndex;

    const dbPrefilled = {};
    let hasDbPrefills = false;

    // Resolve entity bindings
    allFields.forEach(f => {
      if (f.input_source === 'entity' && f.input_entity && f.input_field && workflowClient) {
        const fieldId = f.name || f.id;
        const dbValue = getClientFieldValue(f.input_field, f.input_entity);
        if (dbValue !== undefined && dbValue !== null && dbValue !== '') {
          dbPrefilled[fieldId] = String(dbValue);
          hasDbPrefills = true;
        }
      }
    });

    // Handle form_fields questions with data_mapping (for form_input/data_entry)
    const shouldPrefillFromDb = workflowClient && 
                          (currentStep.step_type === 'form_input' || 
                           currentStep.step_type === 'data_entry' || 
                           currentStep.step_type === 'client_creation');
    
    if (shouldPrefillFromDb) {
      const formFields = config.form_fields || config.formFields || {};
      const questions = formFields.questions || [];
      questions.forEach(q => {
        const mapping = q.data_mapping || q.dataMapping;
        if (mapping && (mapping.target_field || mapping.targetField)) {
          const fieldId = q.id || q.name;
          const targetField = mapping.target_field || mapping.targetField;
          const targetType = mapping.target_type || mapping.targetType || 'client';
          const dbValue = getClientFieldValue(targetField, targetType);
          if (dbValue !== undefined && dbValue !== null && dbValue !== '') {
            dbPrefilled[fieldId] = String(dbValue);
            hasDbPrefills = true;
          }
        }
      });

      // Handle older-style fields with maps_to
      allFields.forEach(f => {
        if (f.maps_to) {
          const fieldId = f.name || f.id;
          const dbValue = getClientFieldValue(f.maps_to, 'client');
          if (dbValue !== undefined && dbValue !== null && dbValue !== '') {
            dbPrefilled[fieldId] = String(dbValue);
            hasDbPrefills = true;
          }
        }
      });
    }

    if (hasDbPrefills) {
      setStepData(prev => {
        const existingData = prev[stepKey] || {};
        // Only pre-fill DB fields that the user hasn't already filled in
        const merged = { ...dbPrefilled, ...existingData };
        return { ...prev, [stepKey]: merged };
      });
    }
  }, [currentStepIndex, workflowClient, activeTemplate, getClientFieldValue, isEditingCompleted]);

  // Derived values after all hooks
  // A workflow is truly completed only if status is 'completed' AND we have a completed_date AND not in edit mode
  const isWorkflowCompleted = ((execution?.status === 'completed' && execution?.completed_date) || 
                               (propTemplate?.execution?.status === 'completed' && propTemplate?.execution?.completed_date)) &&
                               !isEditingCompleted;
  
  // For truly completed workflows, show all steps as completed
  const activeSteps = React.useMemo(() => {
    if (!activeTemplate?.steps) return [];
    
    // Sort steps by position_y (canvas Y position) so execution order matches visual order
    const steps = [...activeTemplate.steps];
    const rootSteps = steps.filter(s => !s.branch_parent_id);
    const branchSteps = steps.filter(s => s.branch_parent_id);
    
    // Only sort by position if at least one step has an explicit position set.
    // This means the user has interacted with the canvas layout.
    const hasAnyPosition = rootSteps.some(s => s.position_y != null);
    
    if (hasAnyPosition) {
      // Compute fallback positions for steps without explicit position:
      // Use the layout Y (index * GAP_Y) as their implicit position.
      const GAP_Y = 140;
      
      // First pass: assign implicit positions to steps without explicit ones
      const positionMap = new Map<string, number>();
      rootSteps.forEach((s, idx) => {
        positionMap.set(s.id, s.position_y ?? (idx * GAP_Y));
      });
      
      rootSteps.sort((a, b) => {
        const ay = positionMap.get(a.id) ?? 0;
        const by = positionMap.get(b.id) ?? 0;
        if (ay === by) return steps.indexOf(a) - steps.indexOf(b);
        return ay - by;
      });
    }
    // If no positions set, keep original array order
    
    // Re-assemble: root steps sorted by Y, branch steps after their parent
    const sorted: any[] = [];
    for (const root of rootSteps) {
      sorted.push(root);
      const children = branchSteps.filter(bs => bs.branch_parent_id === root.id);
      sorted.push(...children);
    }
    // Orphaned branch steps at the end
    const placedIds = new Set(sorted.map(s => s.id));
    for (const bs of branchSteps) {
      if (!placedIds.has(bs.id)) sorted.push(bs);
    }
    
    if (!isWorkflowCompleted) return sorted;
    
    // Mark all steps as completed for completed workflows
    return sorted.map(step => ({
      ...step,
      status: 'completed'
    }));
  }, [activeTemplate?.steps, isWorkflowCompleted]);

  // ─── Conditional Branch Logic ───────────────────────────────────────────────
  // Track which branch was chosen for each conditional_branch step
  const [branchDecisions, setBranchDecisions] = useState<Record<string, string>>({});

  // Evaluate a branch condition against collected step data
  const evaluateCondition = React.useCallback((condition: any, allStepData: any) => {
    if (!condition || typeof condition !== 'object') return false;
    const { source_type, source_field, source_step_id, output_field, segment_id, operator, value } = condition;

    const allStepsArr = activeTemplate?.steps || [];
    let actualValue: any;

    if (source_type === 'step_output' || (!source_type && source_step_id)) {
      const sourceStepIndex = allStepsArr.findIndex(s => s.id === source_step_id);
      if (sourceStepIndex === -1) return false;
      const stepData: any = allStepData[`step_${sourceStepIndex}`] || {};
      const field = output_field || 'branch_value';
      actualValue = stepData[field] ?? stepData.branch_value;
    } else if (source_type === 'enrolled_object') {
      for (const sd of Object.values(allStepData)) {
        if (sd && typeof sd === 'object') {
          const v = (sd as any)[source_field];
          if (v !== undefined) { actualValue = v; break; }
        }
      }
    } else if (source_type === 'segment_membership') {
      for (const sd of Object.values(allStepData)) {
        if (sd && typeof sd === 'object') {
          const segs = (sd as any).segments || (sd as any).client_segments || (sd as any).segment_ids;
          if (Array.isArray(segs)) { actualValue = segs; break; }
        }
      }
    } else if (source_type === 'date_field') {
      for (const sd of Object.values(allStepData)) {
        if (sd && typeof sd === 'object') {
          const v = (sd as any)[source_field];
          if (v) { actualValue = v; break; }
        }
      }
    }

    // Segment membership operators
    if (operator === 'is_in_segment') {
      if (!actualValue) return false;
      const segs = Array.isArray(actualValue) ? actualValue : [actualValue];
      return segs.some(s => String(s).toLowerCase() === String(segment_id || value || '').toLowerCase());
    }
    if (operator === 'is_not_in_segment') {
      if (!actualValue) return true;
      const segs = Array.isArray(actualValue) ? actualValue : [actualValue];
      return !segs.some(s => String(s).toLowerCase() === String(segment_id || value || '').toLowerCase());
    }

    const actualStr = String(actualValue ?? '').toLowerCase().trim();
    const compareStr = String(value ?? '').toLowerCase().trim();

    switch (operator) {
      case 'equals': case 'status_is': return actualStr === compareStr;
      case 'not_equals': case 'status_is_not': return actualStr !== compareStr;
      case 'contains': return actualStr.includes(compareStr);
      case 'not_contains': return !actualStr.includes(compareStr);
      case 'starts_with': return actualStr.startsWith(compareStr);
      case 'ends_with': return actualStr.endsWith(compareStr);
      case 'greater_than': return parseFloat(actualStr) > parseFloat(compareStr);
      case 'less_than': return parseFloat(actualStr) < parseFloat(compareStr);
      case 'greater_than_or_equal': return parseFloat(actualStr) >= parseFloat(compareStr);
      case 'less_than_or_equal': return parseFloat(actualStr) <= parseFloat(compareStr);
      case 'is_empty': return actualStr === '' || actualValue === null || actualValue === undefined;
      case 'is_not_empty': return actualStr !== '' && actualValue !== null && actualValue !== undefined;
      case 'date_before': return !isNaN(Date.parse(actualStr)) && !isNaN(Date.parse(compareStr)) && new Date(actualStr) < new Date(compareStr);
      case 'date_after': return !isNaN(Date.parse(actualStr)) && !isNaN(Date.parse(compareStr)) && new Date(actualStr) > new Date(compareStr);
      default: return false;
    }
  }, [activeTemplate?.steps]);

  // Compute visible steps: filter out branch steps that aren't on the chosen path
  const visibleSteps = React.useMemo(() => {
    if (!activeSteps || activeSteps.length === 0) return [];

    const result: any[] = [];
    for (const step of activeSteps) {
      // If step belongs to a branch, check if it's on the chosen path
      if (step.branch_parent_id && step.branch_id) {
        const chosenBranch = branchDecisions[step.branch_parent_id];
        if (!chosenBranch) continue;
        if (step.branch_id !== chosenBranch) continue;
      }
      result.push(step);
    }
    return result;
  }, [activeSteps, branchDecisions]);

  // Map from visibleSteps index to actual activeSteps index
  const visibleToActualIndex = React.useCallback((visIdx: number) => {
    if (visIdx < 0 || visIdx >= visibleSteps.length) return -1;
    const step = visibleSteps[visIdx];
    return activeSteps.findIndex(s => s.id === step.id);
  }, [visibleSteps, activeSteps]);

  const actualToVisibleIndex = React.useCallback((actIdx: number) => {
    if (actIdx < 0 || actIdx >= activeSteps.length) return -1;
    const step = activeSteps[actIdx];
    return visibleSteps.findIndex(s => s.id === step.id);
  }, [visibleSteps, activeSteps]);

  // Current visible step index
  const currentVisibleIndex = actualToVisibleIndex(currentStepIndex);
  
  const currentStep = activeSteps?.[currentStepIndex];
  const progress = visibleSteps.length > 0 ? (((currentVisibleIndex >= 0 ? currentVisibleIndex : 0) + 1) / visibleSteps.length) * 100 : 0;

  // Show loading state
  if (isLoadingExecution || isLoadingTemplate || isLoadingTemplates) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading workflow...</p>
        </div>
      </div>
    );
  }

  // Show template selector if we're starting a new workflow
  if (!urlExecutionId && !propTemplate && workflowTemplates.length > 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Select a Workflow</h1>
        <div className="grid gap-4">
          {workflowTemplates.map((template) => (
            <Card key={template.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={async () => {
              // Create a new execution
              const newExecution = await api.entities.WorkflowExecution.create({
                template_id: template.id,
                workflow_name: template.name,
                workflow_description: template.description,
                steps: template.steps || [],
                household_id: urlHouseholdId,
                client_id: urlClientId,
                status: 'not_started',
                current_step_index: 0,
                total_steps: template.steps?.length || 0,
                step_data: {},
                started_by: currentUser?.email || 'system',
                started_by_name: currentUser?.full_name || 'System',
                created_entities: {
                  clients: [],
                  cases: [],
                  tasks: [],
                  goals: [],
                  documents: [],
                },
              });
              
              // Redirect to the execution
              navigate(`/workflowexecutor?executionId=${newExecution.id}`);
            }}>
              <CardHeader>
                <CardTitle>{template.name}</CardTitle>
                {template.description && <p className="text-sm text-slate-600 mt-2">{template.description}</p>}
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckSquare className="w-4 h-4" />
                  {template.steps?.length || 0} steps
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show error if execution ID was provided but execution not found (only if no propTemplate)
  if (urlExecutionId && !execution && !isLoadingExecution && !propTemplate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-slate-900 font-semibold mb-2">Execution not found</p>
          <p className="text-slate-600 mb-2">The workflow execution with ID {urlExecutionId} could not be found.</p>
          {executionError && (
            <p className="text-xs text-slate-500 mt-2">Error: {executionError.message}</p>
          )}
          {onCancel && (
            <Button onClick={onCancel} variant="outline" className="mt-4">
              Go Back
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Show error if template could not be loaded (only if no propTemplate)
  if (urlExecutionId && execution && !template && !isLoadingTemplate && templateError && !propTemplate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <p className="text-slate-900 font-semibold mb-2">Template not found</p>
          <p className="text-slate-600 mb-2">Could not load the workflow template.</p>
          <p className="text-xs text-slate-500">Template ID: {execution.template_id}</p>
          {templateError && (
            <p className="text-xs text-red-500 mt-2">Error: {templateError.message}</p>
          )}
          {onCancel && (
            <Button onClick={onCancel} variant="outline" className="mt-4">
              Go Back
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!activeTemplate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <p className="text-slate-900 font-semibold mb-2">No Workflows Available</p>
          <p className="text-slate-600 mb-4">There are no workflow templates configured yet.</p>
          {onCancel && (
            <Button onClick={onCancel} variant="outline">
              Go Back
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  if (!activeTemplate.steps || activeTemplate.steps.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <p className="text-slate-900 font-semibold mb-2">Workflow has no steps</p>
          <p className="text-slate-600">This workflow template is empty or not configured properly.</p>
          <p className="text-xs text-slate-500 mt-2">Template ID: {activeTemplate.id}</p>
        </div>
      </div>
    );
  }
  
  // Safety check for invalid step index
  if (!currentStep) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-slate-900 font-semibold mb-2">Invalid workflow state</p>
          <p className="text-slate-600">Step index {currentStepIndex + 1} doesn't exist in this workflow.</p>
          <Button onClick={() => setCurrentStepIndex(0)} className="mt-4">
            Reset to First Step
          </Button>
        </div>
      </div>
    );
  }

  const askOli = async () => {
    setOliLoading(true);
    try {
      const prompt = currentStep.oli_prompt || 
        `Help the user complete this step: ${currentStep.title}. ${currentStep.description || ''}. 
         Previous data collected: ${JSON.stringify(stepData)}`;

      const response = await api.integrations.Core.InvokeLLM({
        prompt,
        add_context_from_internet: false,
      });

      setOliResponse(response);
    } catch (error) {
      setOliResponse("I'm having trouble connecting right now. Please continue manually.");
    } finally {
      setOliLoading(false);
    }
  };

  const handleNext = async () => {
    // Check if workflow is completed - prevent further updates (unless we're in edit mode)
    const isWorkflowCompletedAndNotEditing = (execution?.status === 'completed' || propTemplate?.execution?.status === 'completed') && !isEditingCompleted;
    if (isWorkflowCompletedAndNotEditing) {
      toast.info('This workflow is already completed and cannot be modified');
      return;
    }
    
    let currentStepData = getCurrentStepData();
    if (currentStep?.step_type === 'client_select') {
      const selectedClients = resolveSelectedClients(currentStep.configuration || {}, currentStepData, clients, objectSegmentMembers, clientSegmentOptions);
      if (selectedClients.length === 0) {
        toast.error('Select at least one existing client before continuing.');
        return;
      }
      const primaryClient = selectedClients[0];
      currentStepData = {
        ...currentStepData,
        ...primaryClient,
        selected_clients: selectedClients,
        selected_client_ids: selectedClients.map((client) => client.client_id),
        selection_count: selectedClients.length,
        continuation_mode: selectedClients.length > 1 ? 'independent_client_executions' : 'single_client_context',
      };
    }
    if (currentStep?.step_type === 'user_select') {
      const config = currentStep.configuration || {};
      const selectedId = currentStepData.user_id || currentStepData.assigned_user_id || config.default_user_id || config.user_id;
      const selectedUser = users.find((user) => user.id === selectedId || user.email === currentStepData.assigned_user_email);
      if (!selectedUser) {
        toast.error('Select an internal workspace user before continuing.');
        return;
      }
      currentStepData = {
        ...currentStepData,
        ...normalizeSelectedUser(selectedUser),
      };
    }
    if (currentStep?.step_type === 'case_creation') {
      const config = currentStep.configuration || {};
      const selectedTemplateId = currentStepData.case_template_id || config.case_template_id;
      const selectedTemplate = caseTemplates.find((template) => template.id === selectedTemplateId);
      const caseAssignmentStrategy = currentStepData.assignment_strategy || currentStep.assignment_strategy || 'none';
      const resolvedAssignee = resolveAssignableUser({ assignment: currentStep, currentData: { ...currentStepData, assignment_strategy: caseAssignmentStrategy }, allStepData: stepData, users, workflowClient });
      const resolvedClientId = resolveCaseClientId({ currentData: currentStepData, allStepData: stepData, assignee: resolvedAssignee });
      currentStepData = {
        ...currentStepData,
        case_template_id: selectedTemplateId || '',
        case_template_name: selectedTemplate?.title || selectedTemplate?.name || config.case_template_name || '',
        case_title: currentStepData.case_title || config.default_title || selectedTemplate?.title || selectedTemplate?.name || '',
        case_description: currentStepData.case_description || config.notes || selectedTemplate?.description || '',
        case_type: currentStepData.case_type || config.case_type || selectedTemplate?.case_type || selectedTemplate?.type || 'custom',
        case_priority: currentStepData.case_priority || config.priority || selectedTemplate?.default_priority || selectedTemplate?.priority || 'medium',
        client_id: resolvedClientId || currentStepData.client_id || '',
        assignment_strategy: caseAssignmentStrategy,
        assigned_user_id: resolvedAssignee?.id || '',
        assigned_user_name: resolvedAssignee ? getUserDisplayName(resolvedAssignee) : '',
        assigned_user_email: resolvedAssignee?.email || '',
        case_id: currentStepData.case_id || 'Will be generated after confirmation',
      };
    }
    if (currentStep?.step_type === 'goal_creation') {
      currentStepData = {
        ...currentStepData,
        ...getGoalRuntimeDefaults({
          step: currentStep,
          currentData: currentStepData,
          allStepData: stepData,
          clients,
          workflowClientId,
          workflowClient,
        }),
      };
    }
    
    // Keep step data keyed by field IDs (not labels) for proper form re-hydration
    const updatedStepData = { ...stepData, [`step_${currentStepIndex}`]: currentStepData };
    setStepData(updatedStepData);

    // ─── Conditional Branch: auto-evaluate any conditional_branch steps ahead ───
    let branchDecisionsUpdate = { ...branchDecisions };
    
    const autoEvaluateBranches = (fromIndex: number, forceReevaluate: boolean) => {
      for (let i = fromIndex; i < activeSteps.length; i++) {
        const s = activeSteps[i];
        const isBranchStep = s.step_type === 'conditional_branch' || s.step_type === 'branch_condition' || s.step_type === 'approval';
        if (!isBranchStep) continue;
        if (branchDecisionsUpdate[s.id] && !forceReevaluate) continue;

        // Approval steps: route based on approval_status in step data, not conditions
        if (s.step_type === 'approval') {
          const stepIdx = activeSteps.findIndex(ss => ss.id === s.id);
          const approvalStepData = updatedStepData[`step_${stepIdx}`] || {};
          const approvalStatus = approvalStepData.approval_status;
          const approvalBranches = s.configuration?.branches || [];
          if (approvalStatus) {
            const matchingBranch = approvalBranches.find((b: any) => b.id === approvalStatus);
            branchDecisionsUpdate[s.id] = matchingBranch ? matchingBranch.id : (approvalBranches[0]?.id || approvalStatus);
          }
          continue;
        }

        const branches = s.configuration?.branches || [];
        let chosenBranchId: string | null = null;

        for (let bi = 0; bi < branches.length; bi++) {
          const branch = branches[bi];
          const isLast = bi === branches.length - 1;
          if (isLast) continue; // Skip default/fallback branch during evaluation

          // New format: conditions array with logic
          const conditions: any[] = branch.conditions || [];
          const logic: string = branch.condition_logic || 'AND';

          let matched = false;
          if (conditions.length > 0) {
            if (logic === 'OR') {
              matched = conditions.some(c => evaluateCondition(c, updatedStepData));
            } else {
              matched = conditions.every(c => evaluateCondition(c, updatedStepData));
            }
          }

          // Legacy format fallback: single branch.condition object
          if (!matched && branch.condition && typeof branch.condition === 'object') {
            matched = evaluateCondition(branch.condition, updatedStepData);
          }

          if (matched) {
            chosenBranchId = branch.id || String(bi);
            break;
          }
        }

        // Fall through to default/fallback (last branch)
        if (!chosenBranchId && branches.length > 0) {
          const defaultBranch = branches[branches.length - 1];
          chosenBranchId = defaultBranch.id || String(branches.length - 1);
        }

        if (chosenBranchId) {
          branchDecisionsUpdate[s.id] = chosenBranchId;
        }
      }
    };

    if (currentStep.step_type === 'conditional_branch' || currentStep.step_type === 'branch_condition' || currentStep.step_type === 'approval') {
      autoEvaluateBranches(currentStepIndex, true);
    }
    autoEvaluateBranches(currentStepIndex + 1, false);

    setBranchDecisions(branchDecisionsUpdate);
    const updatedBranchDecisions = branchDecisionsUpdate;

    // Recompute visible steps with updated decisions
    const updatedVisibleSteps = activeSteps.filter(step => {
      if (step.branch_parent_id && step.branch_id) {
        const chosenBranch = updatedBranchDecisions[step.branch_parent_id];
        if (!chosenBranch) return false;
        if (step.branch_id !== chosenBranch) return false;
      }
      return true;
    });

    // Find current step in visible list and get next
    const currentVisIdx = updatedVisibleSteps.findIndex(s => s.id === currentStep.id);
    const isLastStep = currentVisIdx >= updatedVisibleSteps.length - 1;

    if (!isLastStep) {
      const nextVisibleStep = updatedVisibleSteps[currentVisIdx + 1];
      const nextActualIndex = activeSteps.findIndex(s => s.id === nextVisibleStep.id);

      if (currentStep?.step_type === 'client_select' && currentStepData.selected_clients?.length > 1 && !currentStepData.spawned_independent_executions) {
        const selectedClients = currentStepData.selected_clients || [];
        const extraClients = selectedClients.slice(1);
        for (const selectedClient of extraClients) {
          // Build this client's own step data so branch conditions are evaluated against their fields
          const clientStepData = {
            ...updatedStepData,
            [`step_${currentStepIndex}`]: {
              ...currentStepData,
              ...selectedClient,
              selected_clients: [selectedClient],
              selected_client_ids: [selectedClient.client_id],
              selection_count: 1,
              parent_selection_execution_id: executionId || execution?.id || null,
            },
          };

          // Re-evaluate branch decisions for this specific client so each client
          // is routed by their own data, not client 1's branch decisions.
          const clientBranchDecisions: Record<string, string> = {};
          for (let bi = nextActualIndex; bi < activeSteps.length; bi++) {
            const bs = activeSteps[bi];
            const isBranchStep = bs.step_type === 'conditional_branch' || bs.step_type === 'branch_condition' || bs.step_type === 'approval';
            if (!isBranchStep) continue;

            const bBranches = bs.configuration?.branches || [];
            let chosenId: string | null = null;

            for (let bIdx = 0; bIdx < bBranches.length; bIdx++) {
              const bBranch = bBranches[bIdx];
              if (bIdx === bBranches.length - 1) continue; // skip default
              const bConds = bBranch.conditions || [];
              const bLogic = bBranch.condition_logic || 'AND';
              let matched = bConds.length > 0 && (
                bLogic === 'OR'
                  ? bConds.some((c: any) => evaluateCondition(c, clientStepData))
                  : bConds.every((c: any) => evaluateCondition(c, clientStepData))
              );
              if (!matched && bBranch.condition && typeof bBranch.condition === 'object') {
                matched = evaluateCondition(bBranch.condition, clientStepData);
              }
              if (matched) { chosenId = bBranch.id || String(bIdx); break; }
            }

            if (!chosenId && bBranches.length > 0) {
              const def = bBranches[bBranches.length - 1];
              chosenId = def.id || String(bBranches.length - 1);
            }
            if (chosenId) clientBranchDecisions[bs.id] = chosenId;
          }

          await api.entities.WorkflowExecution.create({
            template_id: activeTemplate?.id || execution?.template_id || propTemplate?.id,
            workflow_name: activeTemplate?.name || execution?.workflow_name || propTemplate?.name || 'Workflow',
            workflow_description: activeTemplate?.description || execution?.workflow_description || propTemplate?.description || '',
            steps: activeTemplate?.steps || propTemplate?.steps || [],
            client_id: selectedClient.client_id,
            client_name: selectedClient.client_name,
            status: 'in_progress',
            current_step_index: nextActualIndex,
            total_steps: activeSteps.length,
            step_data: clientStepData,
            branch_decisions: clientBranchDecisions,
            started_by: currentUser?.email || 'system',
            started_by_name: currentUser?.full_name || currentUser?.name || currentUser?.email || 'System',
            created_entities: { clients: [], cases: [], tasks: [], goals: [], documents: [] },
          });
        }
        currentStepData = { ...currentStepData, spawned_independent_executions: true, spawned_execution_count: extraClients.length };
        updatedStepData[`step_${currentStepIndex}`] = currentStepData;
        toast.success(`${selectedClients.length} selected clients will continue independently`, {
          description: extraClients.length > 0 ? `${extraClients.length} additional client workflow run${extraClients.length === 1 ? '' : 's'} created.` : undefined,
        });
      }

      if (executionId) {
        await updateExecutionMutation.mutateAsync({
          id: executionId,
          data: {
            current_step_index: nextActualIndex,
            step_data: updatedStepData,
            branch_decisions: updatedBranchDecisions,
          },
        });
      }

      setCurrentStepIndex(nextActualIndex);
      setOliResponse("");
    } else {
      // Last step reached — show the review page instead of executing
      setShowReview(true);
    }
  };

  // ─── Execute all steps in order after user confirms on review page ─────────
  const handleConfirmExecution = async () => {
    setIsExecuting(true);
    setIsCompleting(true);
    
    const allStepData = { ...stepData };
    let newCreatedEntities = { ...createdEntities };

    // Determine which steps are visible (respecting branch decisions)
    const stepsToExecute = activeSteps.filter(step => {
      if (step.branch_parent_id && step.branch_id) {
        const chosenBranch = branchDecisions[step.branch_parent_id];
        if (!chosenBranch) return false;
        if (step.branch_id !== chosenBranch) return false;
      }
      return true;
    });

    setExecutionProgress({ current: 0, total: stepsToExecute.length, currentStepLabel: '' });

    try {
      for (let i = 0; i < stepsToExecute.length; i++) {
        const step = stepsToExecute[i];
        const stepIndex = activeSteps.findIndex(s => s.id === step.id);
        const stepKey = `step_${stepIndex}`;
        const currentStepData = allStepData[`step_${stepIndex}`] || {};
        const stepConfig = step.configuration || step.config || {};
        const objectCreateType = step.step_type === 'object_create'
          ? String(stepConfig.object_type || stepConfig.entity_type || stepConfig.objectType || '').toLowerCase()
          : '';
        const executionStepType = step.step_type === 'object_create'
          ? ({ case: 'case_creation', task: 'task_creation', goal: 'goal_creation' }[objectCreateType] || step.step_type)
          : step.step_type;
        
        setExecutionProgress({ current: i + 1, total: stepsToExecute.length, currentStepLabel: step.title || step.step_type });

        // ─── Client Selection (existing clients only) ───
        if (step.step_type === 'client_select') {
          const selectedClients = currentStepData.selected_clients?.length
            ? currentStepData.selected_clients
            : resolveSelectedClients(step.configuration || {}, currentStepData, clients, objectSegmentMembers, clientSegmentOptions);

          if (!selectedClients.length) {
            toast.error('Select Client requires at least one existing client.');
            setIsExecuting(false);
            setIsCompleting(false);
            return;
          }

          const primaryClient = selectedClients[0];
          allStepData[`step_${stepIndex}`] = {
            ...currentStepData,
            ...primaryClient,
            selected_clients: selectedClients,
            selected_client_ids: selectedClients.map((client) => client.client_id),
            selection_count: selectedClients.length,
            continuation_mode: selectedClients.length > 1 ? 'independent_client_executions' : 'single_client_context',
          };
        }

        // ─── Apply data_mapping for form_input/data_entry fields ───
        if ((step.step_type === 'form_input' || step.step_type === 'data_entry') && workflowClientId) {
          try {
            const questions = getStepFormFields(step);
            const entityUpdates = {};
            const customFieldUpdates = {};

            questions.forEach(q => {
              const qMapping = q.data_mapping || q.dataMapping;
              if (!qMapping) return;
              const fieldId = q.id || q.name;
              const value = currentStepData[fieldId];
              if (value === undefined || value === null || value === '') return;

              const target_type = qMapping.target_type || qMapping.targetType;
              const target_field = qMapping.target_field || qMapping.targetField;
              if (!target_type || !target_field) return;

              if (target_type === 'custom_field') {
                customFieldUpdates[target_field] = value;
              } else {
                if (!entityUpdates[target_type]) entityUpdates[target_type] = {};
                if (target_field.includes('.')) {
                  const [parent, child] = target_field.split('.');
                  const sourceMap = {
                    client: workflowClient,
                    financial_data: clientFinancialData,
                    household: clientHousehold,
                    planning_profile: clientPlanningProfile,
                  };
                  if (!entityUpdates[target_type][parent]) {
                    entityUpdates[target_type][parent] = { ...(sourceMap[target_type]?.[parent] || {}) };
                  }
                  entityUpdates[target_type][parent][child] = value;
                } else {
                  entityUpdates[target_type][target_field] = value;
                }
              }
            });

            // Also handle older-style fields with maps_to
            const stepCfg = step.configuration || step.config || {};
            const legacyFields = stepCfg.fields || [];
            legacyFields.forEach(f => {
              if (f.maps_to) {
                const value = currentStepData[f.name || f.id];
                if (value === undefined || value === null || value === '') return;
                if (f.maps_to.includes('.')) {
                  const [parent, child] = f.maps_to.split('.');
                  if (!entityUpdates['client']) entityUpdates['client'] = {};
                  if (!entityUpdates['client'][parent]) entityUpdates['client'][parent] = { ...(workflowClient?.[parent] || {}) };
                  entityUpdates['client'][parent][child] = value;
                } else {
                  if (!entityUpdates['client']) entityUpdates['client'] = {};
                  entityUpdates['client'][f.maps_to] = value;
                }
              }
            });

            // Persist entity updates
            for (const [entityType, updates] of Object.entries(entityUpdates)) {
              switch (entityType) {
                case 'client':
                  if (workflowClientId) {
                    await api.entities.Client.update(workflowClientId, updates);
                    queryClient.invalidateQueries({ queryKey: ['client', workflowClientId] });
                  }
                  break;
                case 'financial_data':
                  if (clientFinancialData?.id) {
                    await api.entities.ClientFinancialData.update(clientFinancialData.id, updates);
                  }
                  break;
                case 'household':
                  if (clientHousehold?.id) {
                    await api.entities.Household.update(clientHousehold.id, updates);
                  }
                  break;
                case 'planning_profile':
                  if (clientPlanningProfile?.id) {
                    await api.entities.ClientPlanningProfile.update(clientPlanningProfile.id, updates);
                  }
                  break;
              }
            }

            if (Object.keys(customFieldUpdates).length > 0) {
              for (const [fieldKey, value] of Object.entries(customFieldUpdates)) {
                const customField = customFields.find(f => f.field_key === fieldKey);
                if (!customField) continue;
                const existingValues = await api.entities.CustomClientFieldValue.filter({
                  client_id: workflowClientId,
                  field_key: fieldKey
                });
                if (existingValues.length > 0) {
                  await api.entities.CustomClientFieldValue.update(existingValues[0].id, { value: String(value) });
                } else {
                  await api.entities.CustomClientFieldValue.create({
                    client_id: workflowClientId,
                    field_id: customField.id,
                    field_key: fieldKey,
                    field_name: customField.field_name,
                    value: String(value)
                  });
                }
              }
            }
          } catch (error) {
            console.error('Failed to apply data mappings:', error);
          }
        }

        // ─── Client Creation ───
        if (step.step_type === 'client_creation') {
          try {
            const DEFAULT_CLIENT_FIELD_MAPPINGS = [
              { name: 'first_name', maps_to: 'first_name' },
              { name: 'last_name', maps_to: 'last_name' },
              { name: 'email', maps_to: 'email' },
              { name: 'phone', maps_to: 'phone' },
              { name: 'date_of_birth', maps_to: 'date_of_birth' },
              { name: 'street', maps_to: 'address.street' },
              { name: 'city', maps_to: 'address.city' },
              { name: 'state', maps_to: 'address.state' },
              { name: 'zip_code', maps_to: 'address.zip_code' },
              { name: 'status', maps_to: 'status' },
              { name: 'risk_tolerance', maps_to: 'risk_tolerance' },
              { name: 'advisor_notes', maps_to: 'advisor_notes' },
            ];

            const clientData: Record<string, any> = {};
            const fields = (step.configuration?.fields && step.configuration.fields.length > 0)
              ? step.configuration.fields
              : DEFAULT_CLIENT_FIELD_MAPPINGS;
            
            fields.forEach(field => {
              const value = currentStepData[field.name];
              if (value && field.maps_to) {
                if (field.maps_to.includes('.')) {
                  const [parent, child] = field.maps_to.split('.');
                  if (!clientData[parent]) clientData[parent] = {};
                  clientData[parent][child] = value;
                } else {
                  clientData[field.maps_to] = value;
                }
              }
            });

            if (currentStepData.first_name || currentStepData.last_name) {
              clientData.full_name = `${currentStepData.first_name || ''} ${currentStepData.last_name || ''}`.trim();
            }
            if (!clientData.status) clientData.status = 'active';
            if (currentUser?.email) {
              clientData.assigned_to = currentUser.email;
            }

            const newClient = await api.entities.Client.create(clientData);
            newCreatedEntities.clients = [...(newCreatedEntities.clients || []), newClient.id];
            
            // Store client_id in step data so subsequent steps can reference it
            allStepData[`step_${stepIndex}`] = { ...currentStepData, client_id: newClient.id };
            
            toast.success(`Client "${clientData.full_name || clientData.email}" created successfully`);
            queryClient.invalidateQueries({ queryKey: ['clients'] });
          } catch (error) {
            console.error('Failed to create client:', error);
            toast.error('Failed to create client. Please try again.');
            setIsExecuting(false);
            setIsCompleting(false);
            return;
          }
        }

        // ─── Bulk Client Creation ───
        if (step.step_type === 'bulk_client_creation') {
          try {
            const cfg = step.configuration || {};
            const csvText = currentStepData.csv_text ?? cfg.csv_text ?? "";
            const parsed = parseBulkClientCsv(csvText);
            const records = currentStepData.records || cfg.records || buildBulkClientRecords(parsed.rows, cfg.field_mappings || [], cfg.custom_field_mappings || []);
            const validation = validateBulkClientRecords(records);
            const missingMappedColumns = getMissingBulkMappedColumns(parsed.headers, cfg.field_mappings || [], cfg.custom_field_mappings || []);

            if (!validation.isValid || missingMappedColumns.length > 0) {
              toast.error(missingMappedColumns.length > 0
                ? `CSV headers changed. Missing mapped column(s): ${missingMappedColumns.join(', ')}`
                : 'Bulk client import has missing, invalid, or duplicate rows. Fix the rows before continuing.');
              setIsExecuting(false);
              setIsCompleting(false);
              return;
            }

            let segmentId = cfg.segment_id || null;
            if (cfg.segment_mode === 'new' && cfg.new_segment_name) {
              const newSegment = await api.entities.ObjectSegment.create({
                name: cfg.new_segment_name,
                description: `Created from workflow bulk import: ${activeTemplate?.name || step.title}`,
                objectType: 'Client',
                segmentType: 'STATIC',
                criteria: [],
                color: 'indigo',
                isActive: true,
              });
              segmentId = newSegment.id;
            }

            const createdClients = [];
            const duplicateEmails = [];

            for (const record of records) {
              const email = String(record.email || '').trim().toLowerCase();
              const existing = await api.entities.Client.filter({ email }, undefined, 1).catch(() => []);
              if (existing?.length > 0) {
                duplicateEmails.push(email);
                continue;
              }

              const clientPayload = {
                firstName: record.firstName,
                lastName: record.lastName,
                email,
                phone: record.phone || undefined,
                status: String(record.status || 'LEAD').toUpperCase(),
                assignedTo: record.assignedTo || record.assigned_to || undefined,
                advisorId: record.assignedTo || record.assigned_to || undefined,
                leadSource: record.leadSource || record.lead_source || 'bulk_import',
                dateOfBirth: record.dateOfBirth || record.date_of_birth || undefined,
                riskTolerance: record.riskTolerance || record.risk_tolerance || undefined,
                notes: record.notes || undefined,
                data: {
                  custom_fields: record.custom_fields || {},
                  entity_mappings: record.entity_mappings || {},
                  bulk_import: { workflow_id: activeTemplate?.id, step_id: step.id, source: cfg.csv_file_name || 'workflow_runner' },
                },
              };

              const client = await api.entities.Client.create(clientPayload);
              newCreatedEntities.clients = [...(newCreatedEntities.clients || []), client.id];

              if (segmentId) {
                await api.entities.ObjectSegmentMember.create({
                  segmentId,
                  objectId: client.id,
                  objectType: 'Client',
                  addedBy: currentUser?.id || currentUser?.email || 'SYSTEM',
                }).catch(() => null);
              }

              createdClients.push({
                client_id: client.id,
                client_name: `${client.firstName || record.firstName || ''} ${client.lastName || record.lastName || ''}`.trim(),
                email: client.email || email,
                assigned_advisor: client.assignedTo || record.assignedTo || record.assigned_to || null,
                segment_id: segmentId,
              });
            }

            if (duplicateEmails.length > 0) {
              toast.error(`${duplicateEmails.length} duplicate email(s) already exist. Resolve them before continuing.`);
              setIsExecuting(false);
              setIsCompleting(false);
              return;
            }

            allStepData[`step_${stepIndex}`] = {
              ...currentStepData,
              total_created: createdClients.length,
              clients: createdClients,
              client_id: createdClients[0]?.client_id,
              client_name: createdClients[0]?.client_name,
              email: createdClients[0]?.email,
              assigned_advisor: createdClients[0]?.assigned_advisor,
              segment_id: segmentId,
            };

            toast.success(`${createdClients.length} client${createdClients.length === 1 ? '' : 's'} created successfully`);
            queryClient.invalidateQueries({ queryKey: ['clients'] });
            queryClient.invalidateQueries({ queryKey: ['client-segments'] });
          } catch (error) {
            console.error('Failed to bulk create clients:', error);
            toast.error('Failed to bulk create clients. Please review the CSV and try again.');
            setIsExecuting(false);
            setIsCompleting(false);
            return;
          }
        }

        if (step.step_type === 'object_select') {
          try {
            const configuredType = stepConfig.object_type || stepConfig.objectType || stepConfig.entity_type || stepConfig.entityType || currentStepData.object_type || 'case';
            const objectType = normalizeNonClientObjectType(configuredType);
            const configuredObjectId = currentStepData.object_id || currentStepData.selected_object_id || stepConfig.object_id || stepConfig.selected_object_id || stepConfig.entityId || stepConfig.entity_id;
            const entityName = NON_CLIENT_OBJECT_ENTITY_MAP[objectType];
            const entity = entityName ? api.entities[entityName] : null;
            let record = null;

            if (configuredObjectId && entity?.filter) {
              const matches = await entity.filter({ id: configuredObjectId }, undefined, 1).catch(() => []);
              record = matches?.[0] || null;
            }

            if (!record && configuredObjectId) {
              record = (selectableObjectLists[objectType] || []).find((item) => item.id === configuredObjectId) || null;
            }

            if (!record && stepConfig.filterField && stepConfig.filterValue && entity?.filter) {
              const value = typeof stepConfig.filterValue === 'string' ? resolveWorkflowText(stepConfig.filterValue, allStepData) : stepConfig.filterValue;
              const matches = await entity.filter({ [stepConfig.filterField]: value }, undefined, 1).catch(() => []);
              record = matches?.[0] || null;
            }

            if (!record) {
              const availableRecords = selectableObjectLists[objectType] || [];
              record = availableRecords.find((item) => {
                if (stepConfig.filter_status && String(item.status || '').toLowerCase() !== String(stepConfig.filter_status).toLowerCase()) return false;
                if (stepConfig.filter_client_id && getNonClientObjectClientId(item) !== stepConfig.filter_client_id) return false;
                if (stepConfig.filter_assignee_id && getNonClientObjectAssigneeId(item) !== stepConfig.filter_assignee_id) return false;
                return true;
              }) || null;
            }

            if (!record) {
              toast.error(`Select Non-Client Object could not resolve an existing ${objectType.replace(/_/g, ' ')}.`);
              setIsExecuting(false);
              setIsCompleting(false);
              return;
            }

            const associatedClientId = getNonClientObjectClientId(record);
            const relatedClient = associatedClientId ? clients.find((client) => client.id === associatedClientId) : null;
            allStepData[stepKey] = {
              ...currentStepData,
              ...buildSelectedObjectOutput({
                record,
                objectType,
                useAsWorkflowTarget: stepConfig.use_as_workflow_target || stepConfig.is_workflow_target || stepIndex === 0,
                client: stepConfig.expose_client_context === false ? null : relatedClient,
              }),
            };

            toast.success(`Selected ${objectType.replace(/_/g, ' ')}: ${getNonClientObjectTitle(record)}`);
          } catch (error) {
            console.error('Failed to select object:', error);
            toast.error('Failed to select non-client object. Please review the object configuration and try again.');
            setIsExecuting(false);
            setIsCompleting(false);
            return;
          }
        }

        // ─── Case Creation ───
        if (step.step_type === 'object_create' && ['lead', 'form', 'document_request'].includes(objectCreateType)) {
          try {
            const fields = stepConfig.field_mappings || stepConfig.fields || [];
            const mappedData = {};
            fields.forEach((field) => {
              const key = field.field_name || field.target_field || field.target || field.name;
              if (!key) return;
              const rawValue = field.value ?? '';
              mappedData[key] = typeof rawValue === 'string' ? resolveWorkflowText(rawValue, allStepData) : rawValue;
            });
            const previousClientData = Object.entries(allStepData || {})
              .filter(([key, data]) => Number(String(key).match(/step_(\d+)/)?.[1] ?? -1) < stepIndex && (data as any)?.client_id)
              .sort(([a], [b]) => Number(String(b).match(/step_(\d+)/)?.[1] ?? -1) - Number(String(a).match(/step_(\d+)/)?.[1] ?? -1))
              .map(([, data]) => data)[0] || {};
            const mappedClientId = stepConfig.client_id ? resolveWorkflowText(stepConfig.client_id, allStepData) : '';
            const resolvedClientId = stepConfig.client_id_source === 'none'
              ? ''
              : (currentStepData.client_id || mappedClientId || previousClientData.client_id || workflowClientId);
            const resolvedAssignee = resolveAssignableUser({ assignment: stepConfig, currentData: currentStepData, allStepData, users, workflowClient });
            const relatedClient = clients.find((client) => client.id === resolvedClientId) || workflowClient || null;
            const normalizeLeadValue = (value) => (typeof value === 'string' ? resolveWorkflowText(value, allStepData) : value);
            const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');
            const getLeadField = (data, field) => firstPresent(
              data[field],
              data[field.replace(/_([a-z])/g, (_, char) => char.toUpperCase())],
              currentStepData[field],
              currentStepData[field.replace(/_([a-z])/g, (_, char) => char.toUpperCase())]
            );

            let createdObject = null;
            let relatedTask = null;
            let relatedForm = null;
            let duplicatePrevented = false;
            if (objectCreateType === 'lead') {
              const clientFullName = relatedClient ? getClientDisplayName(relatedClient) : '';
              const clientParts = String(clientFullName || '').split(' ').filter(Boolean);
              const leadData = {
                first_name: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'first_name'), stepConfig.autofill_from_client !== false ? relatedClient?.first_name || relatedClient?.firstName || clientParts[0] : '')),
                last_name: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'last_name'), stepConfig.autofill_from_client !== false ? relatedClient?.last_name || relatedClient?.lastName || clientParts.slice(1).join(' ') : '')),
                email: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'email'), stepConfig.autofill_from_client !== false ? relatedClient?.email : '')),
                phone: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'phone'), stepConfig.autofill_from_client !== false ? relatedClient?.phone || relatedClient?.mobile_phone || relatedClient?.mobilePhone : '')),
                source: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'source'), stepConfig.lead_source, stepConfig.autofill_from_client !== false ? relatedClient?.lead_source || relatedClient?.leadSource || relatedClient?.source_type || relatedClient?.sourceType : '', 'workflow')),
                status: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'status'), stepConfig.lead_status, 'new')),
                stage: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'stage'), stepConfig.lead_stage, stepConfig.autofill_from_client !== false ? relatedClient?.pipeline_stage_id || relatedClient?.pipelineStageId : '')),
                notes: normalizeLeadValue(firstPresent(getLeadField(mappedData, 'notes'), getLeadField(mappedData, 'description'), relatedClient?.notes)),
              };
              const fullName = normalizeLeadValue(firstPresent(getLeadField(mappedData, 'full_name'), getLeadField(mappedData, 'name'), clientFullName));
              if ((!leadData.first_name || !leadData.last_name) && fullName) {
                const parts = String(fullName).split(' ').filter(Boolean);
                leadData.first_name = leadData.first_name || parts[0];
                leadData.last_name = leadData.last_name || parts.slice(1).join(' ');
              }
              const requiredLeadFields = stepConfig.required_lead_fields || ['first_name', 'last_name', 'email'];
              const missingRequiredFields = requiredLeadFields.filter((field) => !firstPresent(leadData[field], leadData[field.replace(/_([a-z])/g, (_, char) => char.toUpperCase())]));

              if (missingRequiredFields.length > 0) {
                const formSchema = missingRequiredFields.map((field) => ({
                  id: field,
                  name: field,
                  label: field.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
                  type: field === 'email' ? 'email' : field === 'phone' ? 'phone' : 'text',
                  required: true,
                }));
                relatedForm = await api.entities.Form.create({
                  title: stepConfig.missing_lead_form_title || 'Complete Lead Details',
                  name: stepConfig.missing_lead_form_title || 'Complete Lead Details',
                  description: `Collect missing fields before creating lead: ${missingRequiredFields.join(', ')}`,
                  form_type: 'workflow_required_lead_fields',
                  type: 'workflow_required_lead_fields',
                  schema: formSchema,
                  fields: formSchema,
                  settings: { completion_logic: 'pause_until_submission', required_fields: missingRequiredFields, object_type: 'lead' },
                  data: {
                    workflow_execution_id: executionId || execution?.id,
                    workflow_step_id: step.id,
                    associated_client_id: resolvedClientId,
                    assigned_user_id: resolvedAssignee?.id,
                    required_fields: missingRequiredFields,
                    pending_object_type: 'lead',
                  },
                });
                await api.entities.FormAssignment.create({
                  form_id: relatedForm.id,
                  form_title: relatedForm.title || relatedForm.name || 'Complete Lead Details',
                  client_id: resolvedClientId || undefined,
                  client_name: relatedClient ? getClientDisplayName(relatedClient) : undefined,
                  assignee_id: resolvedAssignee?.id || undefined,
                  assigned_to: resolvedAssignee?.id || undefined,
                  assigned_by: currentUser?.email || currentUser?.id || 'Workflow',
                  status: 'pending',
                  data: { workflow_execution_id: executionId || execution?.id, workflow_step_id: step.id, required_form_id: relatedForm.id },
                }).catch(() => null);
                relatedTask = await api.entities.Task.create({
                  title: `Complete lead details${relatedClient ? ` for ${getClientDisplayName(relatedClient)}` : ''}`,
                  description: `Lead creation is waiting for required fields: ${missingRequiredFields.join(', ')}. Complete form ${relatedForm.id}.`,
                  client_id: resolvedClientId || undefined,
                  assigned_to: resolvedAssignee?.id || undefined,
                  assignee_id: resolvedAssignee?.id || undefined,
                  category: 'lead_required_fields',
                  status: 'pending',
                  priority: stepConfig.priority || 'medium',
                  metadata: { object_type: 'lead', required_form_id: relatedForm.id, completion_logic: 'wait_for_required_fields' },
                  data: { workflow_execution_id: executionId || execution?.id, workflow_step_id: step.id, missing_required_fields: missingRequiredFields },
                });

                allStepData[stepKey] = {
                  ...currentStepData,
                  object_type: 'lead',
                  lead_pending: true,
                  missing_required_fields: missingRequiredFields,
                  required_form_id: relatedForm.id,
                  form_id: relatedForm.id,
                  related_form_id: relatedForm.id,
                  related_task_id: relatedTask?.id || '',
                  task_id: relatedTask?.id || '',
                  associated_client_id: resolvedClientId || '',
                  client_id: resolvedClientId || '',
                  assigned_user_id: resolvedAssignee?.id || '',
                  resolved_assignee_id: resolvedAssignee?.id || '',
                  duplicate_check_key: leadData.email || '',
                  duplicate_check_result: 'not_checked_missing_required_fields',
                };
                toast.info('Lead creation is paused until required lead details are collected.');
                queryClient.invalidateQueries({ queryKey: ['tasks'] });
                queryClient.invalidateQueries({ queryKey: ['forms'] });
                setStepData(allStepData);
                setIsExecuting(false);
                setIsCompleting(false);
                return;
              }

              const email = leadData.email;
              if (stepConfig.duplicate_prevention !== false && email) {
                const existing = await api.entities.Lead.filter({ email }, undefined, 1).catch(() => []);
                if (existing?.length) {
                  createdObject = existing[0];
                  duplicatePrevented = true;
                }
              }
              if (!createdObject) {
                const leadPipelines = await api.entities.Pipeline.list('name', 100).catch(() => []);
                const leadPipeline = leadPipelines.find((pipeline) => pipeline.object_type === 'lead' && pipeline.is_default)
                  || leadPipelines.find((pipeline) => pipeline.object_type === 'lead')
                  || leadPipelines.find((pipeline) => pipeline.object_type === 'client' && pipeline.is_default)
                  || leadPipelines[0]
                  || null;
                const pipelineStages = await api.entities.LeadPipelineStage.list('order', 200).catch(() => []);
                const matchingStages = leadPipeline?.id
                  ? pipelineStages.filter((stage) => stage.pipeline_id === leadPipeline.id || stage.pipelineId === leadPipeline.id)
                  : pipelineStages;
                const firstStage = matchingStages[0] || pipelineStages[0] || null;
                const firstName = leadData.first_name || 'New';
                const lastName = leadData.last_name || 'Lead';
                createdObject = await api.entities.Lead.create({
                  first_name: firstName,
                  last_name: lastName,
                  email: leadData.email,
                  phone: leadData.phone,
                  source: leadData.source || 'workflow',
                  status: leadData.status || 'new',
                  stage: leadData.stage || firstStage?.name || undefined,
                  notes: leadData.notes,
                  data: {
                    object_type: 'lead',
                    full_name: [firstName, lastName].filter(Boolean).join(' '),
                    lead_status: leadData.status || 'new',
                    lead_stage: leadData.stage || firstStage?.name || '',
                    pipeline_id: leadPipeline?.id || '',
                    pipeline_stage_id: firstStage?.id || '',
                    workflow_execution_id: executionId || execution?.id,
                    associated_client_id: resolvedClientId,
                    assigned_user_id: resolvedAssignee?.id,
                    qualification_status: normalizeLeadValue(stepConfig.qualification_status || mappedData.qualification_status || mappedData.qualificationStatus || ''),
                    related_case_id: currentStepData.case_id || findPriorCaseData(allStepData)?.case_id || '',
                    related_task_id: currentStepData.task_id || '',
                    duplicate_check_key: email,
                  },
                });
              }
            } else if (objectCreateType === 'form') {
              createdObject = await api.entities.Form.create({
                name: mappedData.name || stepConfig.default_title || stepConfig.title || `Workflow Form - ${step.title || 'Form'}`,
                description: mappedData.description || stepConfig.default_description || stepConfig.description || stepConfig.notes,
                type: mappedData.type || 'workflow',
                schema: mappedData.schema || stepConfig.form_fields?.questions || [],
                settings: { completion_logic: stepConfig.completion_logic || 'pause_until_submission' },
                data: { workflow_execution_id: executionId || execution?.id, associated_client_id: resolvedClientId, assigned_user_id: resolvedAssignee?.id },
              });
            } else if (objectCreateType === 'document_request') {
              createdObject = await api.entities.Task.create({
                title: mappedData.title || mappedData.name || stepConfig.default_title || 'Document Request',
                description: mappedData.description || stepConfig.default_description || 'Please upload the requested document(s).',
                client_id: resolvedClientId || undefined,
                assigned_to: resolvedAssignee?.id || undefined,
                assignee_id: resolvedAssignee?.id || undefined,
                category: mappedData.category || 'document_request',
                status: 'pending',
                priority: mappedData.priority || stepConfig.priority || 'medium',
                metadata: { object_type: 'document_request', completion_logic: stepConfig.completion_logic || 'pause_until_completed' },
              });
            }

            if (createdObject?.id) {
              allStepData[stepKey] = {
                ...currentStepData,
                object_id: createdObject.id,
                object_type: objectCreateType,
                object_title: createdObject.title || createdObject.name || createdObject.email || createdObject.id,
                [`${objectCreateType}_id`]: createdObject.id,
                lead_id: objectCreateType === 'lead' ? createdObject.id : currentStepData.lead_id,
                lead_status: objectCreateType === 'lead' ? createdObject.status : currentStepData.lead_status,
                lead_stage: objectCreateType === 'lead' ? (createdObject.stage || createdObject.pipeline_stage_id || createdObject.pipelineStageId || createdObject.data?.lead_stage || '') : currentStepData.lead_stage,
                lead_source: objectCreateType === 'lead' ? (createdObject.source || createdObject.lead_source || createdObject.leadSource || createdObject.source_type || createdObject.sourceType || '') : currentStepData.lead_source,
                qualification_status: objectCreateType === 'lead' ? (createdObject.qualification_status || createdObject.data?.qualification_status || stepConfig.qualification_status || '') : currentStepData.qualification_status,
                document_request_id: objectCreateType === 'document_request' ? createdObject.id : currentStepData.document_request_id,
                task_id: objectCreateType === 'document_request' ? createdObject.id : currentStepData.task_id,
                related_task_id: currentStepData.task_id || createdObject.data?.related_task_id || '',
                related_form_id: currentStepData.form_id || '',
                associated_client_id: resolvedClientId || '',
                client_id: resolvedClientId || '',
                assigned_user_id: resolvedAssignee?.id || '',
                resolved_assignee_id: resolvedAssignee?.id || '',
                object_status: createdObject.status || '',
                duplicate_prevented: duplicatePrevented,
                duplicate_check_key: objectCreateType === 'lead' ? createdObject.email || mappedData.email || '' : '',
                duplicate_check_result: objectCreateType === 'lead' ? (duplicatePrevented ? 'existing_lead_reused' : 'created_new_lead') : '',
              };
              queryClient.invalidateQueries({ queryKey: ['all-goals'] });
              queryClient.invalidateQueries({ queryKey: ['leads'] });
              queryClient.invalidateQueries({ queryKey: ['client-leads'] });
              queryClient.invalidateQueries({ queryKey: ['standalone-leads'] });
              queryClient.invalidateQueries({ queryKey: ['pipeline-clients'] });
              queryClient.invalidateQueries({ queryKey: ['tasks'] });
              queryClient.invalidateQueries({ queryKey: ['forms'] });
              queryClient.invalidateQueries({ queryKey: ['leads'] });
            }
          } catch (error) {
            console.error('Failed to create object:', error);
            toast.error('Failed to create object. Please review the object configuration and try again.');
          }
        }

        if (executionStepType === 'case_creation') {
          try {
            const config = step.configuration || {};
            const selectedTemplateId = currentStepData.case_template_id || config.case_template_id;
            const selectedTemplate = caseTemplates.find((template) => template.id === selectedTemplateId);
            const resolvedAssignee = resolveAssignableUser({ assignment: step, currentData: currentStepData, allStepData, users, workflowClient });
            const resolvedClientId = resolveCaseClientId({ currentData: currentStepData, allStepData, assignee: resolvedAssignee });

            if (!resolvedClientId) {
              toast.error('No client associated with this workflow case. Select or enroll a client before creating the case.');
              continue;
            }

            const caseClient = clients.find((client) => client.id === resolvedClientId);
            const caseTitleTemplate = currentStepData.case_title || config.default_title || selectedTemplate?.title || selectedTemplate?.name || `Workflow Case: ${step.title || step.label || 'Case'}`;
            const caseDescriptionTemplate = currentStepData.case_description || config.notes || selectedTemplate?.description || '';
            const caseType = currentStepData.case_type || config.case_type || selectedTemplate?.case_type || selectedTemplate?.type || 'custom';
            const casePriority = currentStepData.case_priority || config.priority || selectedTemplate?.default_priority || selectedTemplate?.priority || 'medium';

            const caseData = {
              title: resolveWorkflowText(caseTitleTemplate, allStepData),
              description: resolveWorkflowText(caseDescriptionTemplate, allStepData),
              client_id: resolvedClientId,
              client_name: caseClient ? getClientDisplayName(caseClient) : undefined,
              case_type: caseType,
              priority: casePriority,
              status: currentStepData.case_status || 'open',
              template_id: selectedTemplate?.id,
              template_name: selectedTemplate?.title || selectedTemplate?.name,
              assigned_to: resolvedAssignee?.id || currentStepData.assigned_to || undefined,
              assignee_id: resolvedAssignee?.id || currentStepData.assigned_to || undefined,
              assigned_user_id: resolvedAssignee?.id || undefined,
              assigned_user_name: resolvedAssignee ? getUserDisplayName(resolvedAssignee) : undefined,
              assigned_user_email: resolvedAssignee?.email || undefined,
              assignment_strategy: currentStepData.assignment_strategy || step.assignment_strategy || 'none',
              workflow_execution_id: executionId || execution?.id || undefined,
              related_object_ids: currentStepData.related_object_ids || [],
              task_ids: [],
              workflow_ids: [],
              total_tasks: 0,
              completed_tasks: 0,
              progress_percentage: 0,
              start_date: new Date().toISOString(),
            };

            const newCase = await api.entities.Case.create(caseData);
            const templateTaskIds = selectedTemplate?.task_template_ids || [];
            const createdTaskIds = [];

            for (const templateTaskId of templateTaskIds) {
              const taskTemplate = taskTemplates.find((template) => template.id === templateTaskId);
              if (!taskTemplate) continue;
              const newTask = await api.entities.Task.create({
                title: resolveWorkflowText(taskTemplate.name || taskTemplate.title || 'Case task', allStepData),
                description: resolveWorkflowText(taskTemplate.description || '', allStepData),
                client_id: resolvedClientId,
                client_name: caseClient ? getClientDisplayName(caseClient) : undefined,
                case_id: newCase.id,
                priority: taskTemplate.default_priority || taskTemplate.priority || 'medium',
                category: taskTemplate.category || 'general',
                status: 'pending',
                created_from_template_id: templateTaskId,
                assigned_to: taskTemplate.default_assigned_to || resolvedAssignee?.id || undefined,
                assignee_id: taskTemplate.default_assigned_to || resolvedAssignee?.id || undefined,
                assigned_to_role: taskTemplate.default_assigned_to_role,
                assigned_team_id: taskTemplate.default_assigned_team_id,
                task_type_id: taskTemplate.task_type_id,
                checklist_items: taskTemplate.checklist_items || [],
                custom_fields: taskTemplate.custom_fields || [],
                workflow_steps: taskTemplate.workflow_steps || [],
                resources: taskTemplate.resources || [],
              });
              createdTaskIds.push(newTask.id);
              newCreatedEntities.tasks = [...(newCreatedEntities.tasks || []), newTask.id];
            }

            if (createdTaskIds.length > 0) {
              await api.entities.Case.update(newCase.id, {
                task_ids: createdTaskIds,
                total_tasks: createdTaskIds.length,
                completed_tasks: 0,
                progress_percentage: 0,
              });
            }

            newCreatedEntities.cases = [...(newCreatedEntities.cases || []), newCase.id];
            allStepData[stepKey] = {
              ...currentStepData,
              case_id: newCase.id,
              case_title: caseData.title,
              case_type: caseType,
              case_priority: casePriority,
              priority: casePriority,
              client_id: resolvedClientId,
              associated_client_id: resolvedClientId,
              assigned_user_id: resolvedAssignee?.id || '',
              user_id: resolvedAssignee?.id || '',
              assigned_user_name: resolvedAssignee ? getUserDisplayName(resolvedAssignee) : '',
              assigned_user_email: resolvedAssignee?.email || '',
              case_status: caseData.status,
              template_id: selectedTemplate?.id || '',
              template_name: selectedTemplate?.title || selectedTemplate?.name || '',
              attached_task_ids: createdTaskIds,
            };

            queryClient.invalidateQueries({ queryKey: ['cases'] });
            queryClient.invalidateQueries({ queryKey: ['client-cases', resolvedClientId] });
            queryClient.invalidateQueries({ queryKey: ['client-case-tasks', resolvedClientId] });
            queryClient.invalidateQueries({ queryKey: ['case-items-overview', resolvedClientId] });
            queryClient.invalidateQueries({ queryKey: ['client', resolvedClientId] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['case-tasks', newCase.id] });
          } catch (error) {
            console.error('Failed to create case:', error);
            toast.error('Failed to create case. Please review the case configuration and try again.');
          }
        }

        // ─── Task Creation ───
        if (executionStepType === 'task_creation') {
          try {
            const resolvedAssignee = resolveAssignableUser({ assignment: step, currentData: currentStepData, allStepData, users, workflowClient });
            const resolvedClientId = resolveTaskClientId({ currentData: currentStepData, allStepData, assignee: resolvedAssignee });
            const resolvedCaseId = resolveCaseId({ currentData: currentStepData, stepConfig: step.configuration || step.config || {}, allStepData, clientId: resolvedClientId });
            
            const taskData = {
              title: currentStepData.task_title || stepConfig.default_title || stepConfig.title || 'Workflow Task',
              description: currentStepData.task_description || stepConfig.default_description || stepConfig.description || stepConfig.notes,
              client_id: resolvedClientId || undefined,
              case_id: resolvedCaseId || undefined,
              assigned_to: resolvedAssignee?.id || currentStepData.assigned_to,
              assignee_id: resolvedAssignee?.id || currentStepData.assigned_to,
              status: 'pending',
            };
            const newTask = await api.entities.Task.create(taskData);
            newCreatedEntities.tasks = [...(newCreatedEntities.tasks || []), newTask.id];
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            if (resolvedClientId) {
              queryClient.invalidateQueries({ queryKey: ['tasks', resolvedClientId] });
              queryClient.invalidateQueries({ queryKey: ['client', resolvedClientId] });
              queryClient.invalidateQueries({ queryKey: ['client-tasks-workflow', resolvedClientId] });
            }
          } catch (error) {
            console.error('Failed to create task:', error);
          }
        }

        // ─── Goal Creation ───
        if (executionStepType === 'goal_creation') {
          try {
            const goalTargetClients = getGoalTargetClients({ currentData: currentStepData, allStepData, clients, workflowClientId, workflowClient });

            if (!goalTargetClients.length) {
              toast.error('No client associated with this workflow.');
              continue;
            }

            const createdGoalIds = [];
            for (const selectedClient of goalTargetClients) {
              const goalRuntimeData = getGoalRuntimeDefaults({
                step,
                currentData: { ...currentStepData, ...selectedClient, client_id: selectedClient.client_id },
                allStepData,
                clients,
                workflowClientId: selectedClient.client_id || workflowClientId,
                workflowClient: selectedClient.raw_client || clients.find(c => c.id === selectedClient.client_id) || workflowClient,
                preferConfiguredText: goalTargetClients.length > 1,
              });
              const resolvedClientId = goalRuntimeData.client_id || selectedClient.client_id;
              if (!resolvedClientId) continue;

              const goalData = {
                title: goalRuntimeData.goal_title,
                description: goalRuntimeData.goal_description,
                category: goalRuntimeData.goal_category || 'other',
                target_amount: goalRuntimeData.goal_target_amount ? parseFloat(goalRuntimeData.goal_target_amount) : undefined,
                target_date: goalRuntimeData.goal_target_date,
                priority: goalRuntimeData.goal_priority || 'medium',
                client_id: resolvedClientId,
                status: goalRuntimeData.goal_status || 'not_started',
              };
              const newGoal = await api.entities.Goal.create(goalData);
              createdGoalIds.push(newGoal.id);
              queryClient.invalidateQueries({ queryKey: ['client-goals-workflow', resolvedClientId] });
            }

            newCreatedEntities.goals = [...(newCreatedEntities.goals || []), ...createdGoalIds];
            allStepData[`step_${stepIndex}`] = {
              ...currentStepData,
              goal_ids: createdGoalIds,
              goal_id: createdGoalIds[0] || currentStepData.goal_id,
              goals_created_count: createdGoalIds.length,
            };
          } catch (error) {
            console.error('Failed to create goal:', error);
          }
        }

        // ─── Investment Model Allocation ───
        if (step.step_type === 'investment_model_allocation') {
          try {
            const previousClientData = Object.values(allStepData).find(data => data?.client_id);
            const targetClientId = currentStepData.client_id || previousClientData?.client_id;

            if (targetClientId) {
              const modelConfig = step.configuration?.investment_models || [];
              const investmentModels = [];

              for (let idx = 0; idx < modelConfig.length; idx++) {
                const modelItem = modelConfig[idx];
                let modelId = modelItem.model_id_source === 'static' ? modelItem.static_model_id : currentStepData[`model_${idx}_id`];
                let allocationPercent = modelItem.allocation_source === 'static' ? (parseFloat(modelItem.static_allocation) || 0) : (parseFloat(currentStepData[`model_${idx}_allocation`]) || 0);

                if (modelId && allocationPercent > 0) {
                  investmentModels.push({ model_id: modelId, allocation_percent: allocationPercent });
                }
              }

              if (investmentModels.length > 0) {
                await api.entities.Client.update(targetClientId, { investment_models: investmentModels });
              }
            }
          } catch (error) {
            console.error('Failed to update investment models:', error);
          }
        }

        // ─── Update Client Profile ───
        if (step.step_type === 'update_client_profile') {
          try {
            const previousClientData = Object.values(allStepData).find(data => data?.client_id);
            const targetClientId = currentStepData.client_id || previousClientData?.client_id || workflowClientId;

            if (targetClientId) {
              const mappings = step.configuration?.profile_mappings || [];
              const profileUpdates = {};
              const customFieldUpdates = {};

              for (const mapping of mappings) {
                const sourceStepIndex = activeSteps.findIndex(s => s.id === mapping.source_step_id);
                if (sourceStepIndex === -1) continue;
                const sourceData = allStepData[`step_${sourceStepIndex}`];
                if (!sourceData) continue;

                let value = sourceData[mapping.source_field_path];
                if (value === undefined || value === null) {
                  const sourceStep = activeSteps[sourceStepIndex];
                  const sourceFields = sourceStep?.configuration?.form_fields?.questions || sourceStep?.configuration?.fields || [];
                  const matchedField = sourceFields.find(f => (f.question_text || f.label) === mapping.source_field_path);
                  if (matchedField) value = sourceData[matchedField.id || matchedField.name];
                }
                if (value === undefined || value === null) value = mapping.default_value;
                if (!value) continue;

                if (mapping.transform === 'parse_number') value = parseFloat(value) || 0;
                if (mapping.transform === 'parse_date') value = toDateOnlyString(value);

                if (mapping.target_type === 'client') {
                  const [targetType, targetField] = mapping.target_field.split('.');
                  if (targetField) {
                    if (!profileUpdates[targetType]) profileUpdates[targetType] = {};
                    profileUpdates[targetType][targetField] = value;
                  } else {
                    profileUpdates[mapping.target_field] = value;
                  }
                } else if (mapping.target_type === 'custom_field') {
                  customFieldUpdates[mapping.target_field] = value;
                }
              }

              if (Object.keys(profileUpdates).length > 0) {
                await api.entities.Client.update(targetClientId, profileUpdates);
              }
              if (Object.keys(customFieldUpdates).length > 0) {
                for (const [fieldKey, value] of Object.entries(customFieldUpdates)) {
                  const customField = customFields.find(f => f.field_key === fieldKey);
                  if (!customField) continue;
                  const existingValues = await api.entities.CustomClientFieldValue.filter({ client_id: targetClientId, field_key: fieldKey });
                  if (existingValues.length > 0) {
                    await api.entities.CustomClientFieldValue.update(existingValues[0].id, { value: String(value) });
                  } else {
                    await api.entities.CustomClientFieldValue.create({ client_id: targetClientId, field_id: customField.id, field_key: fieldKey, field_name: customField.field_name, value: String(value) });
                  }
                }
              }
              queryClient.invalidateQueries({ queryKey: ['clients'] });
            }
          } catch (error) {
            console.error('Failed to update client profile:', error);
          }
        }

        // ─── Transform Segment ───
        if (step.step_type === 'transform_segment') {
          try {
            const previousClientData = Object.values(allStepData).find(data => data?.client_id);
            const targetClientId = currentStepData.client_id || previousClientData?.client_id || workflowClientId;
            if (targetClientId) {
              const tsConfig = step.configuration || {};
              const tsOp: string = tsConfig.operation || 'add';
              const tsSegIds: string[] = tsConfig.segment_ids || [];
              if (tsSegIds.length > 0) {
                const clientData = await api.entities.Client.filter({ id: targetClientId });
                const client = clientData[0];
                if (client) {
                  const allSegments = await api.entities.ClientSegment.list();
                  const segmentNames = tsSegIds.map(id => allSegments.find(s => s.id === id)?.name).filter(Boolean);
                  if (tsOp === 'remove') {
                    const newSegments = (client.segments || []).filter(id => !tsSegIds.includes(id));
                    const newSegmentNames = (client.segment_names || []).filter(n => !segmentNames.includes(n));
                    await api.entities.Client.update(targetClientId, { segments: newSegments, segment_names: newSegmentNames });
                  } else {
                    const newSegments = [...new Set([...(client.segments || []), ...tsSegIds])];
                    const newSegmentNames = [...new Set([...(client.segment_names || []), ...segmentNames])];
                    await api.entities.Client.update(targetClientId, { segments: newSegments, segment_names: newSegmentNames });
                  }
                }
              }
            }
          } catch (error) {
            console.error('Failed to transform segments:', error);
          }
        }

        // ─── Assign Segment ───
        if (step.step_type === 'assign_segment') {
          try {
            const previousClientData = Object.values(allStepData).find(data => data?.client_id);
            const targetClientId = currentStepData.client_id || previousClientData?.client_id || workflowClientId;

            if (targetClientId) {
              let segmentIdsToAssign = step.execution_mode === 'background'
                ? (step.configuration?.segment_ids || [])
                : (currentStepData.selected_segment_ids || []);

              if (segmentIdsToAssign.length > 0) {
                const clientData = await api.entities.Client.filter({ id: targetClientId });
                const client = clientData[0];
                if (client) {
                  const allSegments = await api.entities.ClientSegment.list();
                  const segmentNames = segmentIdsToAssign.map(segmentId => allSegments.find(s => s.id === segmentId)?.name).filter(Boolean);
                  const newSegments = [...new Set([...(client.segments || []), ...segmentIdsToAssign])];
                  const newSegmentNames = [...new Set([...(client.segment_names || []), ...segmentNames])];
                  await api.entities.Client.update(targetClientId, { segments: newSegments, segment_names: newSegmentNames });
                }
              }
            }
          } catch (error) {
            console.error('Failed to assign segments:', error);
          }
        }
      }

      // All steps executed successfully — complete the workflow
      setCreatedEntities(newCreatedEntities);
      setStepData(allStepData);

      if (executionId) {
        await updateExecutionMutation.mutateAsync({
          id: executionId,
          data: {
            status: 'completed',
            completed_date: new Date().toISOString(),
            step_data: allStepData,
            created_entities: newCreatedEntities,
            current_step_index: activeSteps.length - 1,
          },
        });
      }

      queryClient.invalidateQueries({ queryKey: ['client-workflows'] });
      queryClient.invalidateQueries({ queryKey: ['household-workflows-enhanced'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
      queryClient.invalidateQueries({ queryKey: ['custom-field-values'] });
      queryClient.invalidateQueries({ queryKey: ['client', workflowClientId] });

      toast.success('Workflow completed successfully!');

      if (onComplete) {
        onComplete();
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Failed to execute workflow:', error);
      toast.error('Failed to execute workflow: ' + error.message);
    } finally {
      setIsExecuting(false);
      setIsCompleting(false);
    }
  };

  const handleBack = () => {
    if (showReview) {
      setShowReview(false);
      return;
    }
    if (currentVisibleIndex > 0) {
      const prevVisibleStep = visibleSteps[currentVisibleIndex - 1];
      const prevActualIndex = activeSteps.findIndex(s => s.id === prevVisibleStep.id);
      const targetIndex = prevActualIndex >= 0 ? prevActualIndex : currentStepIndex - 1;

      // Clear branch decisions for any conditional_branch step at or after the target
      // so they get re-evaluated on the next "Next Step" click
      const updatedDecisions = { ...branchDecisions };
      let changed = false;
      for (let i = targetIndex; i < activeSteps.length; i++) {
        const s = activeSteps[i];
        if (s.step_type === 'conditional_branch' && updatedDecisions[s.id]) {
          delete updatedDecisions[s.id];
          changed = true;
        }
      }
      if (changed) setBranchDecisions(updatedDecisions);

      setCurrentStepIndex(targetIndex);
      setOliResponse("");
    }
  };

  const getCurrentStepData = () => {
    const stepKey = `step_${currentStepIndex}`;
    return stepData[stepKey] || {};
  };

  const setCurrentStepData = (data) => {
    const stepKey = `step_${currentStepIndex}`;
    setStepData({ ...stepData, [stepKey]: data });
  };

  // Check if a specific step is "complete" (all required fields filled, or no required fields)
  const isStepComplete = (stepIdx: number) => {
    const step = activeSteps[stepIdx];
    if (!step) return false;
    const stepKey = `step_${stepIdx}`;
    const data = stepData[stepKey] || {};
    
    // Steps that are auto-complete (no user input needed)
    const autoCompleteTypes = ['phase_header', 'wait_delay', 'wait_for_completion', 'oli_guidance', 'data_summary', 'conditional_branch', 'branch_condition', 'delay', 'delay_fixed_time', 'delay_specific_date', 'delay_field_based_date', 'delay_event_occurrence', 'delay_day_of_week', 'delay_time_of_day', 'delay_condition', 'form_create_assign'];
    if (autoCompleteTypes.includes(step.step_type)) return true;
    
    // Action-type steps: complete once user has visited/passed them (they are pre-configured actions)
    const actionStepTypes = [
      'multi_channel_communication',
      'send_communication_external',
      'send_notification_internal',
      'transform_segment',
      'send_email', 'send_sms', 'send_push_notification', 'send_internal_message', 'send_channel_notification',
      'automation_trigger', 'parallel_tasks', 'taskflow_execution', 'data_mapping',
      'goto_action', 'goto_workflow', 'crm_create', 'object_create', 'crm_update', 'crm_delete',
      'crm_assign_ownership', 'crm_subscription_management', 'crm_phone_validation',
      'marketing_campaign', 'marketing_enrollment', 'marketing_tag',
      'webhook_call', 'external_api_call'
    ];
    if (actionStepTypes.includes(step.step_type)) {
      return Object.keys(data).length > 0 || stepIdx <= currentStepIndex || showReview;
    }
    
    // Get required fields for this step
    const fields = step.configuration?.fields || step.configuration?.form_fields?.questions || [];
    const requiredFields = fields.filter((f: any) => f.required);
    
    // If no required fields, step is complete if user has visited it (any data or past the step)
    if (requiredFields.length === 0) {
      return Object.keys(data).length > 0 || stepIdx <= currentStepIndex || showReview;
    }
    
    // Check all required fields have values
    return requiredFields.every((f: any) => {
      const fieldId = f.id || f.name;
      const value = data[fieldId];
      return value !== undefined && value !== null && value !== '';
    });
  };

  // Check if ALL visible steps are complete (for enabling Confirm & Execute)
  const allStepsComplete = React.useMemo(() => {
    return visibleSteps.every((step) => {
      const actualIdx = activeSteps.findIndex(s => s.id === step.id);
      return isStepComplete(actualIdx);
    });
  }, [visibleSteps, activeSteps, stepData, currentStepIndex, showReview]);

  // Helper to robustly read form fields from step configuration (handles camelCase / snake_case)
  const getStepFormFields = (step) => {
    const cfg = step?.configuration || step?.config || {};
    const ff = cfg.form_fields || cfg.formFields || {};
    return ff.questions || cfg.fields || [];
  };

  const isStepValid = () => {
    const currentStepData = getCurrentStepData();

    switch (currentStep.step_type) {
      case 'form_input':
      case 'data_entry':
        const fields = getStepFormFields(currentStep);
        // Filter visible fields based on conditional logic
        const visibleFields = fields.filter(field => {
          if (!field.depends_on) return true;
          const dependencyValue = currentStepData[field.depends_on];
          const normalizedValue = String(dependencyValue || '').toLowerCase();
          const normalizedShowWhen = String(field.show_when || '').toLowerCase();
          return normalizedValue === normalizedShowWhen;
        });
        // Check all required visible fields are filled
        return visibleFields.every(field => {
          if (!field.required) return true;
          const fieldName = field.id || field.name;
          const value = currentStepData[fieldName];
          return value !== undefined && value !== null && value !== '';
        });

      case 'client_creation':
        // Use configured fields or default (first_name, last_name, email are required)
        const clientValidationFields = (currentStep.configuration?.fields && currentStep.configuration.fields.length > 0)
          ? currentStep.configuration.fields
          : [
              { name: 'first_name', required: true },
              { name: 'last_name', required: true },
              { name: 'email', required: true },
            ];
        return clientValidationFields.every(field => {
          if (!field.required) return true;
          const value = currentStepData[field.name];
          return value !== undefined && value !== null && value !== '';
        });

      case 'bulk_client_creation':
        const bulkConfig = currentStep.configuration || {};
        const bulkRecords = currentStepData.records || bulkConfig.records || [];
        const bulkParsed = parseBulkClientCsv(currentStepData.csv_text ?? bulkConfig.csv_text ?? "");
        const missingBulkColumns = getMissingBulkMappedColumns(bulkParsed.headers, bulkConfig.field_mappings || [], bulkConfig.custom_field_mappings || []);
        return validateBulkClientRecords(bulkRecords).isValid && missingBulkColumns.length === 0 && bulkConfig.duplicate_prevention !== false;

      case 'client_select':
        return resolveSelectedClients(currentStep.configuration || {}, currentStepData, clients, objectSegmentMembers, clientSegmentOptions).length > 0;

      case 'object_select':
        return !!(
          currentStep.configuration?.object_type ||
          currentStep.configuration?.objectType ||
          currentStepData.object_type
        ) && !!(
          currentStepData.object_id ||
          currentStepData.selected_object_id ||
          currentStep.configuration?.object_id ||
          currentStep.configuration?.selected_object_id ||
          currentStep.configuration?.entityId ||
          currentStep.configuration?.filterField ||
          currentStep.configuration?.filter_status ||
          currentStep.configuration?.filter_client_id ||
          currentStep.configuration?.filter_assignee_id
        );

      case 'user_select':
        return !!(currentStepData.user_id || currentStepData.assigned_user_id || currentStepData.assigned_user_email || currentStep.configuration?.default_user_id || currentStep.configuration?.user_id);

      case 'task_creation':
        return !!(currentStepData.task_title || currentStep.configuration?.default_title || currentStep.configuration?.title || currentStep.configuration?.task_template_id);

      case 'object_create': {
        const objectType = currentStep.configuration?.object_type || currentStep.configuration?.entity_type || currentStep.configuration?.objectType;
        if (!objectType) return false;
        if (objectType === 'task') return !!(currentStepData.task_title || currentStep.configuration?.default_title || currentStep.configuration?.title || currentStep.configuration?.task_template_id);
        if (objectType === 'case') return !!(currentStepData.case_title || currentStep.configuration?.default_title || currentStep.configuration?.case_template_id || currentStepData.case_template_id);
        if (objectType === 'goal') return !!(currentStepData.goal_title || getGoalRuntimeDefaults({ step: currentStep, currentData: currentStepData, allStepData: stepData, clients, workflowClientId, workflowClient }).goal_title || currentStep.configuration?.goal_template_id);
        const fields = currentStep.configuration?.field_mappings || currentStep.configuration?.fields || [];
        return fields.filter((field: any) => field.required).every((field: any) => !!(field.value || currentStepData[field.field_name] || currentStepData[field.target_field]));
      }

      case 'case_creation':
        return !!(currentStepData.case_title || currentStep.configuration?.default_title || currentStep.configuration?.case_template_id || currentStepData.case_template_id);

      case 'goal_creation':
        return !!(currentStepData.goal_title || getGoalRuntimeDefaults({
          step: currentStep,
          currentData: currentStepData,
          allStepData: stepData,
          clients,
          workflowClientId,
          workflowClient,
        }).goal_title || currentStep.configuration?.goal_template_id);

      // Steps that don't require validation
      case 'investment_model_allocation':
        const modelConfig = currentStep.configuration?.investment_models || [];
        // Validate each model has selection and allocation
        const allValid = modelConfig.every((modelItem, idx) => {
          const hasModelId = modelItem.model_id_source === 'static' 
            ? !!modelItem.static_model_id 
            : !!currentStepData[`model_${idx}_id`];
          
          const hasAllocation = modelItem.allocation_source === 'static'
            ? (parseFloat(modelItem.static_allocation) || 0) > 0
            : (parseFloat(currentStepData[`model_${idx}_allocation`]) || 0) > 0;
          
          return hasModelId && hasAllocation;
        });
        
        // Check total allocation equals 100%
        const totalAllocation = modelConfig.reduce((sum, modelItem, idx) => {
          const allocation = modelItem.allocation_source === 'static'
            ? (parseFloat(modelItem.static_allocation) || 0)
            : (parseFloat(currentStepData[`model_${idx}_allocation`]) || 0);
          return sum + allocation;
        }, 0);
        
        return allValid && totalAllocation === 100;

      case 'assign_segment':
        // For automation mode, segments are pre-configured
        if (currentStep.execution_mode === 'background') return true;
        // For user select mode, at least one segment must be selected
        return currentStepData.selected_segment_ids?.length > 0;

      case 'conditional_branch':
        return true; // Auto-evaluates based on configured conditions
      case 'document_upload':
        // Require at least one file uploaded (unless configured as optional)
        const docRequired = currentStep.configuration?.required !== false;
        if (!docRequired) return true;
        return (currentStepData.uploaded_files || []).length > 0;
      case 'approval':
        // Must have chosen approve or reject
        return !!(currentStepData.approval_status);
      case 'update_client_profile':
      case 'update_financial_data':
      case 'oli_guidance':
      case 'data_summary':
      case 'phase_header':
      case 'wait_delay':
      case 'wait_for_completion':
      case 'automation_trigger':
      case 'parallel_tasks':
      case 'taskflow_execution':
      case 'data_mapping':
      case 'send_communication_external':
      case 'send_notification_internal':
      case 'transform_segment':
      case 'send_email':
      case 'send_sms':
      case 'send_push_notification':
      case 'send_internal_message':
      case 'send_channel_notification':
      case 'goto_action':
      case 'goto_workflow':
      case 'crm_create':
      case 'object_create':
      case 'crm_update':
      case 'crm_delete':
      case 'crm_assign_ownership':
      case 'crm_subscription_management':
      case 'crm_phone_validation':
      case 'marketing_campaign_add':
      case 'marketing_campaign_remove':
      case 'marketing_segment_add':
      case 'marketing_segment_remove':
      case 'marketing_subscription_update':
      case 'custom_webhook':
      case 'custom_data_format':
      case 'custom_code':
        // Automated action steps — no user input required at runtime
        return true;
      case 'delay':
      case 'delay_fixed_time':
      case 'delay_specific_date':
      case 'delay_field_based_date':
      case 'delay_event_occurrence':
      case 'delay_day_of_week':
      case 'delay_time_of_day':
      case 'delay_condition':
        // Delays are automated at runtime — in the manual runner require an
        // explicit acknowledgement/skip so users can't blindly click through.
        return !!currentStepData.delay_acknowledged;
      case 'form_create_assign':
        // Form create/assign is automated (the assigned user/client fills the
        // generated form later). In the manual runner require acknowledgement.
        return !!currentStepData.form_assignment_acknowledged;
      case 'branch_random':
      default:
        return true;
    }
  };

  // Helper: get binding indicator for a field
  const getFieldBindingInfo = (fieldName: string) => {
    const config = currentStep?.configuration || currentStep?.config || {};
    const allFields = config.fields || [];
    const field = allFields.find(f => (f.name || f.id) === fieldName);
    if (!field || !field.input_source || field.input_source === 'none') return null;

    if (field.input_source === 'previous_step') {
      const sourceStep = activeTemplate?.steps?.find(s => s.id === field.input_step_id);
      const stepLabel = sourceStep?.title || 'previous step';
      return `Linked from "${stepLabel}" → ${field.input_field || 'field'}`;
    }
    if (field.input_source === 'entity') {
      return `Linked from ${field.input_entity || 'database'} → ${field.input_field || 'field'}`;
    }
    if (field.input_source === 'static') {
      return 'Pre-filled with default value';
    }
    return null;
  };

  // Render binding indicator below a field
  const BindingIndicator = ({ fieldName }: { fieldName: string }) => {
    const info = getFieldBindingInfo(fieldName);
    if (!info) return null;
    return (
      <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
        <Link2 className="w-3 h-3" />
        {info}
      </p>
    );
  };

  const renderStepContent = () => {
    const currentStepData = getCurrentStepData();

    switch (currentStep.step_type) {
      case 'form_input':
      case 'data_entry':
        const fields = getStepFormFields(currentStep);
        
        if (fields.length === 0) {
          return (
            <div className="p-8 text-center bg-amber-50 rounded-lg border-2 border-dashed border-amber-200">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <p className="text-amber-900 font-semibold mb-2">No Fields Configured</p>
              <p className="text-sm text-amber-700">
                This step doesn't have any fields set up yet. Please edit the workflow template to add fields for data collection.
              </p>
            </div>
          );
        }

        // Filter fields based on conditional logic
        const visibleFields = fields.filter(field => {
          if (!field.depends_on) return true;
          const dependencyValue = currentStepData[field.depends_on];
          const normalizedValue = String(dependencyValue || '').toLowerCase();
          const normalizedShowWhen = String(field.show_when || '').toLowerCase();
          return normalizedValue === normalizedShowWhen;
        });

        return (
          <div className="space-y-4">
            {visibleFields.map((field, index) => {
              const fieldName = field.id || field.name;
              const fieldLabel = field.question_text || field.questionText || field.label;
              const fieldType = field.question_type || field.questionType || field.type;
              const rawMapping = field.data_mapping || field.dataMapping;
              const mapping = rawMapping || (field.maps_to ? { target_type: 'client', target_field: field.maps_to } : null);
              // Check both field ID and field label for stored data (for backward compatibility with label-keyed storage)
              const currentValue = currentStepData[fieldName] || currentStepData[fieldLabel] || '';
              
              // Only show DB pre-fill indicators for in-progress workflows (not completed)
              const isViewingCompleted = ((execution?.status === 'completed' && execution?.completed_date) || 
                                          (propTemplate?.execution?.status === 'completed' && propTemplate?.execution?.completed_date));
              const mappingTargetField = mapping?.target_field || mapping?.targetField;
              const mappingTargetType = mapping?.target_type || mapping?.targetType || 'client';
              const dbValue = (!isViewingCompleted && mapping && mappingTargetField) ? getClientFieldValue(mappingTargetField, mappingTargetType) : undefined;
              const hasDbValue = dbValue !== undefined && dbValue !== null && dbValue !== '';
              const isModified = hasDbValue && currentValue !== '' && currentValue !== String(dbValue);
              
              return (
                <div key={index}>
                  <div className="flex items-center gap-2 mb-1">
                    <Label className="mb-0">{fieldLabel} {field.required && <span className="text-red-600">*</span>}</Label>
                    {mapping && mappingTargetField && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1">
                              <Link2 className="w-3 h-3 text-blue-500" />
                              <span className="text-xs text-blue-600 font-medium">
                                {mappingTargetField?.split('.').pop()}
                              </span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="text-xs">Linked to {mappingTargetType === 'client' ? 'Client Profile' : 'Custom Field'}: <strong>{mappingTargetField}</strong></p>
                            {hasDbValue && <p className="text-xs mt-1">Current DB value: <strong>{String(dbValue)}</strong></p>}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  {mapping && hasDbValue && (
                    <div className={`flex items-center gap-2 text-xs mb-1.5 px-2 py-1 rounded ${isModified ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                      <Database className="w-3 h-3 flex-shrink-0" />
                      <span>
                        {isModified 
                          ? `DB value: "${String(dbValue)}" — will be updated to "${currentValue}"`
                          : `Pre-filled from client profile`
                        }
                      </span>
                      {isModified && (
                        <button 
                          type="button"
                          onClick={() => setCurrentStepData({ ...currentStepData, [fieldName]: String(dbValue) })}
                          className="ml-auto text-amber-600 hover:text-amber-800 flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" />
                          <span>Reset</span>
                        </button>
                      )}
                    </div>
                  )}
                  {fieldType === 'yes_no' ? (
                    <Select
                      value={currentStepData[fieldName] || ""}
                      onValueChange={(value) => setCurrentStepData({ ...currentStepData, [fieldName]: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : fieldType === 'select' || fieldType === 'single_choice' ? (
                    <Select
                      value={currentStepData[fieldName] || ""}
                      onValueChange={(value) => setCurrentStepData({ ...currentStepData, [fieldName]: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${fieldLabel.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {(field.options || []).map((option, optIdx) => (
                          <SelectItem key={optIdx} value={option.toLowerCase()}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : fieldType === 'radio' ? (
                    <div className="space-y-2">
                      {(field.options || []).map((option, optIdx) => (
                        <label key={optIdx} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={fieldName}
                            value={option.toLowerCase()}
                            checked={currentStepData[fieldName] === option.toLowerCase()}
                            onChange={(e) => setCurrentStepData({ ...currentStepData, [fieldName]: e.target.value })}
                            className="w-4 h-4"
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : fieldType === 'textarea' || fieldType === 'long_text' ? (
                    <Textarea
                      value={currentStepData[fieldName] || ""}
                      onChange={(e) => setCurrentStepData({ ...currentStepData, [fieldName]: e.target.value })}
                      placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                      rows={3}
                    />
                  ) : (
                    <Input
                      type={fieldType === 'phone' ? 'tel' : fieldType === 'text' ? 'text' : fieldType}
                      value={currentValue}
                      onChange={(e) => setCurrentStepData({ ...currentStepData, [fieldName]: e.target.value })}
                      placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );

      case 'bulk_client_creation': {
        const bulkConfig = currentStep.configuration || {};
        const csvText = currentStepData.csv_text ?? bulkConfig.csv_text ?? "";
        const parsed = parseBulkClientCsv(csvText);
        const configuredRecords = currentStepData.records || bulkConfig.records || [];
        const derivedRecords = configuredRecords.length > 0
          ? configuredRecords
          : buildBulkClientRecords(parsed.rows, bulkConfig.field_mappings || [], bulkConfig.custom_field_mappings || []);
        const validation = validateBulkClientRecords(derivedRecords);
        const missingMappedColumns = getMissingBulkMappedColumns(parsed.headers, bulkConfig.field_mappings || [], bulkConfig.custom_field_mappings || []);
        const isReady = validation.isValid && missingMappedColumns.length === 0;
        const failedRows = validation.rowResults.filter((row) => row.errors.length > 0);
        const mappedFields = (bulkConfig.field_mappings || []).filter((mapping) => mapping.csv_column && mapping.target_field);

        const updateRuntimeCsv = (text) => {
          const nextParsed = parseBulkClientCsv(text);
          const nextRecords = buildBulkClientRecords(nextParsed.rows, bulkConfig.field_mappings || [], bulkConfig.custom_field_mappings || []);
          setCurrentStepData({
            ...currentStepData,
            csv_text: text,
            records: nextRecords,
            import_validation: validateBulkClientRecords(nextRecords),
          });
        };

        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-indigo-950">Bulk client import review</p>
                    <p className="text-sm text-indigo-700 mt-1">
                      Validate mapped CSV rows before this workflow creates clients and continues with each created client context.
                    </p>
                  </div>
                </div>
                <Badge className={isReady ? "bg-emerald-600" : "bg-amber-500"}>
                  {isReady ? "Ready to run" : "Needs review"}
                </Badge>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <Card className="shadow-none"><CardContent className="p-3 text-center"><p className="text-2xl font-semibold text-slate-900">{validation.totalRows}</p><p className="text-xs text-slate-500">Rows</p></CardContent></Card>
              <Card className="shadow-none border-emerald-200 bg-emerald-50"><CardContent className="p-3 text-center"><p className="text-2xl font-semibold text-emerald-700">{validation.validRows}</p><p className="text-xs text-emerald-600">Valid</p></CardContent></Card>
              <Card className="shadow-none border-red-200 bg-red-50"><CardContent className="p-3 text-center"><p className="text-2xl font-semibold text-red-700">{validation.failedRows}</p><p className="text-xs text-red-600">Failed</p></CardContent></Card>
              <Card className="shadow-none"><CardContent className="p-3 text-center"><p className="text-2xl font-semibold text-slate-900">{mappedFields.length}</p><p className="text-xs text-slate-500">Mapped fields</p></CardContent></Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1fr,1.2fr]">
              <Card className="shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" /> CSV source</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      file.text().then(updateRuntimeCsv);
                    }}
                  />
                  <Textarea
                    value={csvText}
                    onChange={(e) => updateRuntimeCsv(e.target.value)}
                    placeholder="first_name,last_name,email\nAvery,Stone,avery@example.com"
                    className="min-h-[150px] font-mono text-xs"
                  />
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                    <p className="text-xs font-semibold text-blue-900">Mappings are locked from Workflow Builder</p>
                    <p className="text-xs text-blue-700 mt-1">You can replace row data here, but the CSV must keep the mapped header names. If the headers change, update the field mapping in the action configuration.</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Configured mappings</p>
                    <div className="flex flex-wrap gap-1.5">
                      {mappedFields.length > 0 ? mappedFields.map((mapping) => (
                        <Badge key={`${mapping.target_field}-${mapping.csv_column}`} variant="outline" className="bg-white">
                          {mapping.target_field} ← {mapping.csv_column}
                        </Badge>
                      )) : <span className="text-xs text-amber-700">No mappings configured. Configure the action in Workflow Builder before running.</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Import validation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <p className="text-xs text-emerald-700">Email is used as the duplicate-prevention key before client records are created.</p>
                  </div>
                  {missingMappedColumns.length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-900">CSV headers do not match configured mappings</p>
                        <p className="text-xs text-amber-700 mt-1">Missing mapped column(s): {missingMappedColumns.join(", ")}. Keep these headers in the CSV or update the mapping in Workflow Builder.</p>
                      </div>
                    </div>
                  )}
                  {failedRows.length > 0 ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
                      <div className="flex items-center gap-2 text-red-700"><AlertCircle className="w-4 h-4" /><p className="text-sm font-semibold">Fix failed rows before continuing</p></div>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {failedRows.map((row) => (
                          <p key={row.rowNumber} className="text-xs text-red-700">Row {row.rowNumber}: {row.errors.join(", ")}</p>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="w-4 h-4" />
                      <p className="text-sm font-medium">All rows pass required-field and email validation.</p>
                    </div>
                  )}

                  <div className="border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-[70px,1fr,1fr,90px] bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                      <span>Row</span><span>Client</span><span>Email</span><span>Status</span>
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y">
                      {validation.rowResults.slice(0, 12).map((row) => (
                        <div key={row.rowNumber} className="grid grid-cols-[70px,1fr,1fr,90px] px-3 py-2 text-xs items-center">
                          <span>{row.rowNumber}</span>
                          <span className="truncate">{row.clientName || "—"}</span>
                          <span className="truncate">{row.email || "—"}</span>
                          <Badge variant="outline" className={row.errors.length ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>{row.errors.length ? "Fix" : "Valid"}</Badge>
                        </div>
                      ))}
                      {validation.rowResults.length === 0 && <p className="p-4 text-sm text-slate-500 text-center">No CSV rows available.</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold text-slate-700">Segment handling</p>
                <p className="text-sm text-slate-600 mt-1">
                  {bulkConfig.segment_mode === "existing" ? `Add to existing segment: ${bulkConfig.segment_id || "not selected"}` : bulkConfig.segment_mode === "new" ? `Create new segment: ${bulkConfig.new_segment_name || "not named"}` : "No segment assignment selected."}
                </p>
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                <p className="text-xs font-semibold text-blue-900">Downstream values</p>
                <p className="text-sm text-blue-700 mt-1">Created clients expose client_id, client_name, email, assigned_advisor, and segment_id.</p>
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes for this bulk import..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        );
      }

      case 'client_creation':
        // Default client creation template fields
        const DEFAULT_CLIENT_FIELDS = [
          { name: 'first_name', label: 'First Name', type: 'text', required: true, maps_to: 'first_name', section: 'personal' },
          { name: 'last_name', label: 'Last Name', type: 'text', required: true, maps_to: 'last_name', section: 'personal' },
          { name: 'email', label: 'Email Address', type: 'email', required: true, maps_to: 'email', section: 'personal' },
          { name: 'phone', label: 'Phone Number', type: 'tel', required: false, maps_to: 'phone', section: 'personal' },
          { name: 'date_of_birth', label: 'Date of Birth', type: 'date', required: false, maps_to: 'date_of_birth', section: 'personal' },
          { name: 'street', label: 'Street Address', type: 'text', required: false, maps_to: 'address.street', section: 'address' },
          { name: 'city', label: 'City', type: 'text', required: false, maps_to: 'address.city', section: 'address' },
          { name: 'state', label: 'State', type: 'text', required: false, maps_to: 'address.state', section: 'address' },
          { name: 'zip_code', label: 'Zip Code', type: 'text', required: false, maps_to: 'address.zip_code', section: 'address' },
          { name: 'status', label: 'Client Status', type: 'select', required: false, maps_to: 'status', section: 'details', options: ['lead', 'active', 'pending'] },
          { name: 'risk_tolerance', label: 'Risk Tolerance', type: 'select', required: false, maps_to: 'risk_tolerance', section: 'details', options: ['conservative', 'moderately_conservative', 'moderate', 'moderately_aggressive', 'aggressive'] },
          { name: 'advisor_notes', label: 'Notes', type: 'textarea', required: false, maps_to: 'advisor_notes', section: 'details' },
        ];

        const clientFields = (currentStep.configuration?.fields && currentStep.configuration.fields.length > 0)
          ? currentStep.configuration.fields
          : DEFAULT_CLIENT_FIELDS;

        // Group fields by section
        const fieldSections = {};
        clientFields.forEach(f => {
          const section = f.section || 'general';
          if (!fieldSections[section]) fieldSections[section] = [];
          fieldSections[section].push(f);
        });

        const sectionLabels = {
          personal: 'Personal Information',
          address: 'Address',
          details: 'Additional Details',
          general: 'Client Information',
        };

        return (
          <div className="space-y-5">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Create New Client</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Fill in the client details below. A new client record will be created after you review & confirm.
                  </p>
                </div>
              </div>
            </div>

            {Object.entries(fieldSections).map(([sectionKey, sectionFields]) => (
              <div key={sectionKey} className="space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 pb-1">
                  {sectionLabels[sectionKey] || sectionKey}
                </h4>
                <div className={`grid gap-3 ${sectionKey === 'address' ? 'grid-cols-2' : sectionKey === 'personal' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {sectionFields.map((field, index) => (
                    <div key={index} className={field.type === 'textarea' || field.type === 'file' ? 'col-span-2' : ''}>
                      <Label className="text-sm">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      {field.type === 'textarea' || field.type === 'long_text' ? (
                        <Textarea
                          value={currentStepData[field.name] || ""}
                          onChange={(e) => setCurrentStepData({ ...currentStepData, [field.name]: e.target.value })}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          rows={3}
                          className="mt-1"
                        />
                      ) : field.type === 'select' ? (
                        <Select
                          value={currentStepData[field.name] || ""}
                          onValueChange={(value) => setCurrentStepData({ ...currentStepData, [field.name]: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {(field.options || []).map(opt => (
                              <SelectItem key={opt} value={opt}>
                                {opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.type === 'multi_select' ? (
                        <div className="mt-2 space-y-2">
                          {(field.options || []).length > 0 ? field.options.map((opt, i) => (
                            <div key={i} className="flex items-center space-x-2">
                              <Checkbox
                                id={`rt-ms-${field.name}-${i}`}
                                checked={(currentStepData[field.name] || []).includes(opt)}
                                onCheckedChange={(checked) => {
                                  const current = currentStepData[field.name] || [];
                                  const updated = checked
                                    ? [...current, opt]
                                    : current.filter(v => v !== opt);
                                  setCurrentStepData({ ...currentStepData, [field.name]: updated });
                                }}
                              />
                              <label htmlFor={`rt-ms-${field.name}-${i}`} className="text-sm text-slate-700 cursor-pointer">
                                {opt}
                              </label>
                            </div>
                          )) : (
                            <Input
                              value={currentStepData[field.name] || ""}
                              onChange={(e) => setCurrentStepData({ ...currentStepData, [field.name]: e.target.value })}
                              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                              className="mt-1"
                            />
                          )}
                        </div>
                      ) : field.type === 'checkbox' ? (
                        <div className="mt-2 space-y-2">
                          {(field.options || []).length > 0 ? field.options.map((opt, i) => (
                            <div key={i} className="flex items-center space-x-2">
                              <Checkbox
                                id={`rt-cb-${field.name}-${i}`}
                                checked={(currentStepData[field.name] || []).includes(opt)}
                                onCheckedChange={(checked) => {
                                  const current = currentStepData[field.name] || [];
                                  const updated = checked
                                    ? [...current, opt]
                                    : current.filter(v => v !== opt);
                                  setCurrentStepData({ ...currentStepData, [field.name]: updated });
                                }}
                              />
                              <label htmlFor={`rt-cb-${field.name}-${i}`} className="text-sm text-slate-700 cursor-pointer">
                                {opt}
                              </label>
                            </div>
                          )) : (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`rt-cb-${field.name}`}
                                checked={!!currentStepData[field.name]}
                                onCheckedChange={(checked) => setCurrentStepData({ ...currentStepData, [field.name]: checked })}
                              />
                              <label htmlFor={`rt-cb-${field.name}`} className="text-sm text-slate-700 cursor-pointer">
                                {field.placeholder || field.label}
                              </label>
                            </div>
                          )}
                        </div>
                      ) : field.type === 'radio' ? (
                        <RadioGroup
                          value={currentStepData[field.name] || ""}
                          onValueChange={(value) => setCurrentStepData({ ...currentStepData, [field.name]: value })}
                          className="mt-2"
                        >
                          {(field.options || []).map((opt, i) => (
                            <div key={i} className="flex items-center space-x-2">
                              <RadioGroupItem value={opt} id={`rt-radio-${field.name}-${i}`} />
                              <label htmlFor={`rt-radio-${field.name}-${i}`} className="text-sm text-slate-700 cursor-pointer">
                                {opt}
                              </label>
                            </div>
                          ))}
                        </RadioGroup>
                      ) : field.type === 'toggle' ? (
                        <div className="flex items-center space-x-2 mt-2">
                          <Switch
                            id={`rt-toggle-${field.name}`}
                            checked={!!currentStepData[field.name]}
                            onCheckedChange={(checked) => setCurrentStepData({ ...currentStepData, [field.name]: checked })}
                          />
                          <label htmlFor={`rt-toggle-${field.name}`} className="text-sm text-slate-700 cursor-pointer">
                            {field.placeholder || field.label}
                          </label>
                        </div>
                      ) : field.type === 'file' ? (
                        <div className="mt-2">
                          <label className="block border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors cursor-pointer">
                            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-slate-600">Click or drag file to upload</p>
                            <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLSX, Images (max 10MB)</p>
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setCurrentStepData({ ...currentStepData, [field.name]: file.name });
                                }
                              }}
                            />
                          </label>
                          {currentStepData[field.name] && (
                            <p className="text-xs text-emerald-600 mt-1">Selected: {currentStepData[field.name]}</p>
                          )}
                        </div>
                      ) : (
                        <Input
                          type={field.type === 'tel' ? 'tel' : field.type === 'currency' ? 'number' : field.type}
                          value={currentStepData[field.name] || ""}
                          onChange={(e) => setCurrentStepData({ ...currentStepData, [field.name]: e.target.value })}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          className="mt-1"
                        />
                      )}
                      <BindingIndicator fieldName={field.name} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-800">
                ✓ A new client record will be created and linked to this workflow after you review & confirm.
              </p>
            </div>
          </div>
        );

      case 'task_creation':
        // Get client and user from previous steps if available
        const previousClientData = Object.values(stepData).find(data => data?.client_id);
        const previousUserData = findPriorAssigneeData(stepData);
        const previousCaseData = findPriorCaseData(stepData);
        const taskAssignmentStrategy = currentStepData.assignment_strategy || currentStep.assignment_strategy || (currentStepData.assigned_to || currentStep.assigned_to ? 'specific_user' : previousUserData ? 'previous_assignee' : 'none');
        const taskResolvedAssignee = resolveAssignableUser({ assignment: currentStep, currentData: { ...currentStepData, assignment_strategy: taskAssignmentStrategy }, allStepData: stepData, users, workflowClient });
        const taskClientId = resolveTaskClientId({ currentData: currentStepData, allStepData: stepData, assignee: taskResolvedAssignee });
        const taskClient = clients.find((client) => client.id === taskClientId);
        const taskConfig = currentStep.configuration || currentStep.config || {};
        const taskCaseLinkMode = currentStepData.case_link_mode || taskConfig.case_link_mode || (currentStepData.case_id || taskConfig.case_id ? 'specific_case' : 'previous_case');
        const configuredTaskCaseId = currentStepData.case_id || taskConfig.case_id || "";
        const priorTaskCaseId = previousCaseData?.case_id || previousCaseData?.caseId || "";
        const taskCaseId = taskCaseLinkMode === 'none' ? "" : configuredTaskCaseId || (taskCaseLinkMode === 'previous_case' || taskCaseLinkMode === 'auto' ? priorTaskCaseId : "");
        const taskCaseSelectValue = taskCaseLinkMode === 'none' ? 'none' : configuredTaskCaseId || (priorTaskCaseId ? '__previous_case' : 'none');
        const taskCaseOptions = cases.filter((caseItem) => !taskClientId || caseItem.client_id === taskClientId || caseItem.clientId === taskClientId);
        
        return (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <FileText className="w-4 h-4 inline mr-2" />
                Create tasks that need to be completed as part of this workflow.
              </p>
            </div>
            <div>
              <Label>Task Title *</Label>
              <Input
                value={currentStepData.task_title || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, task_title: e.target.value })}
                placeholder="Enter task title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={currentStepData.task_description || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, task_description: e.target.value })}
                placeholder="Describe the task"
                rows={3}
              />
            </div>
            <div>
              <Label>Link task to client {taskClientId && <span className="text-xs text-green-600">(Auto-filled)</span>}</Label>
              <Select
                value={taskClientId || ""}
                onValueChange={(value) => setCurrentStepData({ ...currentStepData, client_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client to link (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{getClientDisplayName(client)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {taskClient ? `This task will appear on ${getClientDisplayName(taskClient)}'s profile. ` : 'This links the task to a client record. '}
                The internal assignee is selected below under Assign work.
              </p>
            </div>
            <div>
              <Label>Link task to case {taskCaseId && <span className="text-xs text-green-600">(Auto-filled from prior case)</span>}</Label>
              <Select
                value={taskCaseSelectValue}
                onValueChange={(value) => setCurrentStepData({
                  ...currentStepData,
                  case_link_mode: value === 'none' ? 'none' : value === '__previous_case' ? 'previous_case' : 'specific_case',
                  case_id: value === 'none' || value === '__previous_case' ? '' : value,
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select case to attach task (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {priorTaskCaseId && <SelectItem value="__previous_case">Use previous Create Case step</SelectItem>}
                  <SelectItem value="none">No case</SelectItem>
                  {taskCaseOptions.map((caseItem) => (
                    <SelectItem key={caseItem.id} value={caseItem.id}>{caseItem.title || caseItem.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">If a Create Case step ran earlier, this task will attach to that case automatically.</p>
            </div>
            <div>
              <Label>Assign work</Label>
              <Select
                value={taskAssignmentStrategy}
                onValueChange={(value) => setCurrentStepData({ ...currentStepData, assignment_strategy: value, assigned_to: value === 'specific_user' ? currentStepData.assigned_to : '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose assignment route" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assignee</SelectItem>
                  <SelectItem value="specific_user">Specific internal user</SelectItem>
                  <SelectItem value="previous_assignee" disabled={!previousUserData}>Previously assigned user / prior assignee</SelectItem>
                  <SelectItem value="client_owner">Client owner</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="round_robin">Round-robin by role</SelectItem>
                </SelectContent>
              </Select>
              {taskAssignmentStrategy === 'specific_user' && (
                <Select value={currentStepData.assigned_to || ""} onValueChange={(value) => setCurrentStepData({ ...currentStepData, assigned_to: value })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>{users.map((user) => <SelectItem key={user.id} value={user.id}>{getUserDisplayName(user)} ({user.email})</SelectItem>)}</SelectContent>
                </Select>
              )}
              {(taskAssignmentStrategy === 'role' || taskAssignmentStrategy === 'round_robin') && (
                <Select value={currentStepData.assigned_role || currentStep.assigned_role || "Advisor"} onValueChange={(value) => setCurrentStepData({ ...currentStepData, assigned_role: value, round_robin_role: value })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="ADVISOR">Advisor</SelectItem>
                    <SelectItem value="SUPPORT">Support</SelectItem>
                    <SelectItem value="OPERATIONS">Operations</SelectItem>
                    <SelectItem value="PARAPLANNER">Paraplanner</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {taskResolvedAssignee && <p className="text-xs text-emerald-600 mt-1">Will assign to {getUserDisplayName(taskResolvedAssignee)} ({taskResolvedAssignee.email})</p>}
              {taskAssignmentStrategy === 'previous_assignee' && !previousUserData && <p className="text-xs text-amber-600 mt-1">Add a Select User / Assignee step earlier to reuse a prior assignee.</p>}
            </div>
          </div>
        );

      case 'case_creation': {
        const caseConfig = currentStep.configuration || {};
        const selectedTemplateId = currentStepData.case_template_id || caseConfig.case_template_id || "";
        const selectedTemplate = caseTemplates.find((template) => template.id === selectedTemplateId);
        const caseAssignmentStrategy = currentStepData.assignment_strategy || currentStep.assignment_strategy || (currentStepData.assigned_to || currentStep.assigned_to ? 'specific_user' : findPriorAssigneeData(stepData) ? 'previous_assignee' : 'none');
        const caseResolvedAssignee = resolveAssignableUser({ assignment: currentStep, currentData: { ...currentStepData, assignment_strategy: caseAssignmentStrategy }, allStepData: stepData, users, workflowClient });
        const caseClientId = resolveCaseClientId({ currentData: currentStepData, allStepData: stepData, assignee: caseResolvedAssignee });
        const caseClient = clients.find((client) => client.id === caseClientId);

        return (
          <div className="space-y-4">
            <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
              <div className="flex items-start gap-3">
                <FolderOpen className="w-5 h-5 text-rose-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-rose-900">Create client case container</p>
                  <p className="text-xs text-rose-700 mt-0.5">
                    This creates a case linked to {caseClient ? <strong>{getClientDisplayName(caseClient)}</strong> : 'the enrolled client'} and exposes <b>case_id</b> to later workflow steps.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label>Case Template</Label>
              <Select
                value={selectedTemplateId || "none"}
                onValueChange={(value) => {
                  const template = caseTemplates.find((item) => item.id === value);
                  setCurrentStepData({
                    ...currentStepData,
                    case_template_id: value === 'none' ? '' : value,
                    case_template_name: template?.title || template?.name || '',
                    case_title: template?.title || template?.name || currentStepData.case_title || caseConfig.default_title || '',
                    case_description: template?.description || currentStepData.case_description || caseConfig.notes || '',
                    case_type: template?.case_type || template?.type || currentStepData.case_type || caseConfig.case_type || 'custom',
                    case_priority: template?.default_priority || template?.priority || currentStepData.case_priority || caseConfig.priority || 'medium',
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select case template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {caseTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>{template.title || template.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate && <p className="text-xs text-slate-500 mt-1">Template selected: {selectedTemplate.title || selectedTemplate.name}</p>}
            </div>

            <div>
              <Label>Case Title *</Label>
              <Input
                value={currentStepData.case_title || caseConfig.default_title || selectedTemplate?.title || selectedTemplate?.name || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, case_title: e.target.value })}
                placeholder="e.g., Annual Review Case"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={currentStepData.case_description || caseConfig.notes || selectedTemplate?.description || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, case_description: e.target.value })}
                placeholder="Describe the case scope"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Case Type</Label>
                <Select value={currentStepData.case_type || caseConfig.case_type || selectedTemplate?.case_type || selectedTemplate?.type || "custom"} onValueChange={(value) => setCurrentStepData({ ...currentStepData, case_type: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="financial_planning">Financial Planning</SelectItem>
                    <SelectItem value="review_preparation">Review Preparation</SelectItem>
                    <SelectItem value="follow_up">Follow-up</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={currentStepData.case_priority || caseConfig.priority || selectedTemplate?.default_priority || selectedTemplate?.priority || "medium"} onValueChange={(value) => setCurrentStepData({ ...currentStepData, case_priority: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Client Association</Label>
              <Select value={caseClientId || ""} onValueChange={(value) => setCurrentStepData({ ...currentStepData, client_id: value })}>
                <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((client) => <SelectItem key={client.id} value={client.id}>{getClientDisplayName(client)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">The created case will appear on this client's Cases tab.</p>
            </div>

            <div>
              <Label>Assign case owner</Label>
              <Select value={caseAssignmentStrategy} onValueChange={(value) => setCurrentStepData({ ...currentStepData, assignment_strategy: value, assigned_to: value === 'specific_user' ? currentStepData.assigned_to : '' })}>
                <SelectTrigger><SelectValue placeholder="Choose assignment route" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assignee</SelectItem>
                  <SelectItem value="specific_user">Specific internal user</SelectItem>
                  <SelectItem value="previous_assignee" disabled={!findPriorAssigneeData(stepData)}>Previously selected assignee</SelectItem>
                  <SelectItem value="client_owner">Client owner</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="round_robin">Round-robin by role</SelectItem>
                </SelectContent>
              </Select>
              {caseAssignmentStrategy === 'specific_user' && (
                <Select value={currentStepData.assigned_to || ""} onValueChange={(value) => setCurrentStepData({ ...currentStepData, assigned_to: value })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>{users.map((user) => <SelectItem key={user.id} value={user.id}>{getUserDisplayName(user)} ({user.email})</SelectItem>)}</SelectContent>
                </Select>
              )}
              {(caseAssignmentStrategy === 'role' || caseAssignmentStrategy === 'round_robin') && (
                <Select value={currentStepData.assigned_role || currentStep.assigned_role || "ADVISOR"} onValueChange={(value) => setCurrentStepData({ ...currentStepData, assigned_role: value, round_robin_role: value })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="ADVISOR">Advisor</SelectItem>
                    <SelectItem value="SUPPORT">Support</SelectItem>
                    <SelectItem value="OPERATIONS">Operations</SelectItem>
                    <SelectItem value="PARAPLANNER">Paraplanner</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {caseResolvedAssignee && <p className="text-xs text-emerald-600 mt-1">Will assign case owner to {getUserDisplayName(caseResolvedAssignee)} ({caseResolvedAssignee.email})</p>}
            </div>

            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-800">
                ✓ A new case will be created on review & confirm. The generated case_id will be available for later tasks and related objects.
              </p>
            </div>
          </div>
        );
      }

      case 'client_select':
        const clientSelectConfig = currentStep.configuration || {};
        const clientSelectionSource = clientSelectConfig.selection_source || clientSelectConfig.selection_mode || 'manual';
        const selectedClientIds = currentStepData.selected_client_ids || clientSelectConfig.selected_client_ids || (currentStepData.client_id ? [currentStepData.client_id] : clientSelectConfig.client_id ? [clientSelectConfig.client_id] : []);
        const segmentOptions = clientSegmentOptions;
        const filters = currentStepData.filters || clientSelectConfig.filters || [];
        const resolvedClients = resolveSelectedClients(clientSelectConfig, currentStepData, clients, objectSegmentMembers, segmentOptions);
        const allowMultipleClients = clientSelectConfig.allow_multiple !== false;
        const updateSelectedClientIds = (ids) => {
          const nextIds = allowMultipleClients ? ids : ids.slice(0, 1);
          const selectedClients = clients.filter((client) => nextIds.includes(client.id)).map(normalizeSelectedClient);
          const primary = selectedClients[0] || {};
          setCurrentStepData({
            ...currentStepData,
            ...primary,
            selected_client_ids: nextIds,
            selected_clients: selectedClients,
            selection_count: selectedClients.length,
          });
        };
        const updateRuntimeFilter = (filterId, updates) => {
          const nextFilters = filters.map((filter) => filter.id === filterId ? { ...filter, ...updates } : filter);
          setCurrentStepData({ ...currentStepData, filters: nextFilters });
        };
        const runtimeFilterFields = [
          { value: 'organization', label: 'Organization' },
          { value: 'advisor', label: 'Advisor / Owner' },
          { value: 'net_worth', label: 'Net worth' },
          { value: 'client_type', label: 'Client type' },
          { value: 'tags', label: 'Tags' },
          { value: 'status', label: 'Status' },
          { value: 'custom_field', label: 'Custom field' },
          { value: 'attribute', label: 'Other attribute' },
        ];
        const runtimeOperators = [
          { value: 'equals', label: 'Equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'greater_than', label: 'Greater than' },
          { value: 'less_than', label: 'Less than' },
          { value: 'is_not_empty', label: 'Is not empty' },
        ];
        const getRuntimeFilterValueOptions = (filter) => {
          const values = clients.flatMap((client) => {
            const raw = getClientFilterValue(client, filter);
            return Array.isArray(raw) ? raw : [raw];
          }).filter((value) => value !== undefined && value !== null && String(value).trim() !== '');
          return [...new Set(values.map((value) => String(value).trim()))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).slice(0, 100);
        };
        return (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <Users className="w-4 h-4 inline mr-2" />
                Select existing client records only. This action does not create new clients.
              </p>
            </div>

            <div className="grid gap-2 md:grid-cols-3">
              <Badge variant="outline" className="justify-center py-1.5 bg-white">Mode: {clientSelectionSource.replace(/_/g, ' ')}</Badge>
              <Badge variant="outline" className="justify-center py-1.5 bg-white">{allowMultipleClients ? 'Multi-client enabled' : 'Single client only'}</Badge>
              <Badge variant="outline" className="justify-center py-1.5 bg-emerald-50 text-emerald-700 border-emerald-200">{resolvedClients.length} matched</Badge>
            </div>

            {clientSelectionSource === 'manual' && (
              <div className="space-y-2">
                <Label>Select existing client{allowMultipleClients ? 's' : ''} *</Label>
                <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
                  {clients.map((client) => {
                    const checked = selectedClientIds.includes(client.id);
                    return (
                      <label key={client.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => updateSelectedClientIds(checked ? selectedClientIds.filter((id) => id !== client.id) : [...selectedClientIds, client.id])}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-slate-900 truncate">{getClientDisplayName(client)}</span>
                          <span className="block text-xs text-slate-500 truncate">{client.email || 'No email'}{client.phone ? ` · ${client.phone}` : ''}</span>
                        </span>
                        {client.status && <Badge variant="outline" className="text-[10px]">{String(client.status).replace(/_/g, ' ')}</Badge>}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {clientSelectionSource === 'segment' && (
              <div className="space-y-2">
                <Label>Saved segment *</Label>
                <Select value={currentStepData.segment_id || clientSelectConfig.segment_id || ""} onValueChange={(value) => {
                  const selectedSegment = segmentOptions.find((segment) => String(segment.id) === String(value));
                  setCurrentStepData({ ...currentStepData, segment_id: value, segment_name: selectedSegment?.name || selectedSegment?.title || '' });
                }}>
                  <SelectTrigger><SelectValue placeholder="Choose a saved segment" /></SelectTrigger>
                  <SelectContent>
                    {segmentOptions.map((segment) => <SelectItem key={`${segment.source}_${segment.id}`} value={segment.id}>{segment.name || segment.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Matched clients are listed below for review before continuing.</p>
              </div>
            )}

            {clientSelectionSource === 'filters' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Conditional filters *</Label>
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setCurrentStepData({ ...currentStepData, filters: [...filters, { id: `runtime_filter_${Date.now()}`, field: 'status', operator: 'equals', value: '' }] })}>Add Filter</Button>
                </div>
                {filters.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500">No filters configured. Add a filter before continuing.</div>
                ) : (
                  <div className="space-y-2">
                    {filters.map((filter) => {
                      const valueOptions = getRuntimeFilterValueOptions(filter);
                      return (
                      <div key={filter.id} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                        <div className="grid gap-2 md:grid-cols-3">
                          <Select value={filter.field || 'status'} onValueChange={(value) => updateRuntimeFilter(filter.id, { field: value, value: '', attribute: value === 'custom_field' || value === 'attribute' ? filter.attribute : '' })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{runtimeFilterFields.map((field) => <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Select value={filter.operator || 'equals'} onValueChange={(value) => updateRuntimeFilter(filter.id, { operator: value, value: value === 'is_not_empty' ? '' : filter.value })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{runtimeOperators.map((op) => <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setCurrentStepData({ ...currentStepData, filters: filters.filter((item) => item.id !== filter.id) })}>Remove</Button>
                        </div>
                        {(filter.field === 'custom_field' || filter.field === 'attribute') && <Input value={filter.attribute || ''} onChange={(e) => updateRuntimeFilter(filter.id, { attribute: e.target.value, value: '' })} placeholder={filter.field === 'custom_field' ? 'Custom field key' : 'Client attribute path'} />}
                        {filter.operator !== 'is_not_empty' && (
                          <Select value={filter.value || ''} onValueChange={(value) => updateRuntimeFilter(filter.id, { value })} disabled={valueOptions.length === 0}>
                            <SelectTrigger><SelectValue placeholder={valueOptions.length === 0 ? 'No existing values found' : 'Choose existing value'} /></SelectTrigger>
                            <SelectContent>{valueOptions.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        {filter.operator !== 'is_not_empty' && valueOptions.length > 0 && <p className="text-xs text-slate-500">Values are pulled from existing client records to reduce typing errors.</p>}
                      </div>
                    );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <Label>Review selected clients</Label>
                <Badge variant="outline">{resolvedClients.length} selected</Badge>
              </div>
              {resolvedClients.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No existing clients selected or matched yet.</p>
              ) : (
                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                  {resolvedClients.map((client) => (
                    <div key={client.client_id} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{client.client_name}</p>
                        <p className="text-xs text-slate-500 truncate">{client.email || 'No email'}{client.phone ? ` · ${client.phone}` : ''}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">client_id ready</Badge>
                    </div>
                  ))}
                </div>
              )}
              {resolvedClients.length > 1 && (
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
                  Each selected client will continue independently with its own client context. The current execution continues with the first selected client, and additional client-specific runs are created for the remaining clients.
                </div>
              )}
            </div>
          </div>
        );

      case 'user_select':
        const userSelectConfig = currentStep.configuration || {};
        const userSearchTerm = currentStepData.user_search || "";
        const filteredUsers = users.filter((user) => {
          if (userSelectConfig.filter_role && String(user.role || user.user_role || "").toLowerCase() !== String(userSelectConfig.filter_role).toLowerCase()) return false;
          const haystack = `${getUserDisplayName(user)} ${user.email || ""} ${user.role || ""}`.toLowerCase();
          return !userSearchTerm || haystack.includes(String(userSearchTerm).toLowerCase());
        });
        const selectedUser = users.find((user) => user.id === (currentStepData.user_id || currentStepData.assigned_user_id || userSelectConfig.default_user_id || userSelectConfig.user_id) || user.email === currentStepData.assigned_user_email);
        return (
          <div className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                <Users className="w-4 h-4 inline mr-2" />
                Select an internal workspace user. The selected assignee becomes reusable in later workflow steps.
              </p>
            </div>
            <div>
              <Label>Search User/Assignee *</Label>
              <Input
                value={userSearchTerm}
                onChange={(e) => setCurrentStepData({ ...currentStepData, user_search: e.target.value })}
                placeholder="Search by name, email, or role..."
                className="mt-1.5"
              />
              <Select
                value={currentStepData.user_id || currentStepData.assigned_user_id || userSelectConfig.default_user_id || ""}
                onValueChange={(value) => {
                  const pickedUser = users.find(u => u.id === value);
                  setCurrentStepData({
                    ...currentStepData,
                    user_search: userSearchTerm,
                    ...normalizeSelectedUser(pickedUser),
                  });
                }}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a registered workspace user..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {getUserDisplayName(user)} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedUser && (
                <div className="mt-3 rounded-lg border border-purple-100 bg-purple-50 p-3 text-xs text-purple-800">
                  <p className="font-semibold">Selected assignee</p>
                  <p>{getUserDisplayName(selectedUser)} · {selectedUser.email}</p>
                  <p className="mt-1">Reusable downstream as <b>user_id</b>, name, email, role, and team.</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'goal_creation':
        const goalRuntimeData = getGoalRuntimeDefaults({
          step: currentStep,
          currentData: currentStepData,
          allStepData: stepData,
          clients,
          workflowClientId,
          workflowClient,
        });
        const goalClientName = (() => {
          const previousClientData = Object.values(stepData).find(data => data?.client_id);
          const resolvedId = goalRuntimeData.client_id || currentStepData.client_id || previousClientData?.client_id || workflowClientId;
          const client = clients.find(c => c.id === resolvedId);
          return client ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : goalRuntimeData.client_name || null;
        })();

        return (
          <div className="space-y-4">
            <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
              <div className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-teal-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-teal-900">Create a Financial Goal</p>
                  <p className="text-xs text-teal-700 mt-0.5">
                    This goal will be saved to {goalClientName ? <strong>{goalClientName}</strong> : 'the client'}'s profile and tracked in their planning center.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>Goal Title <span className="text-red-500">*</span></Label>
              <Input
                value={goalRuntimeData.goal_title || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, goal_title: e.target.value })}
                placeholder="e.g., Retirement Savings, College Fund"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={goalRuntimeData.goal_description || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, goal_description: e.target.value })}
                placeholder="Describe the goal, timeline expectations, etc."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={goalRuntimeData.goal_category || "other"}
                  onValueChange={(value) => setCurrentStepData({ ...currentStepData, goal_category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retirement">Retirement</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="home">Home Purchase</SelectItem>
                    <SelectItem value="emergency_fund">Emergency Fund</SelectItem>
                    <SelectItem value="investment">Investment</SelectItem>
                    <SelectItem value="debt_payoff">Debt Payoff</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={goalRuntimeData.goal_priority || "medium"}
                  onValueChange={(value) => setCurrentStepData({ ...currentStepData, goal_priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Amount ($)</Label>
                <Input
                  type="number"
                  value={goalRuntimeData.goal_target_amount || ""}
                  onChange={(e) => setCurrentStepData({ ...currentStepData, goal_target_amount: e.target.value })}
                  placeholder="500000"
                />
              </div>
              <div>
                <Label>Target Date</Label>
                <DatePicker
                  value={goalRuntimeData.goal_target_date || ""}
                  onChange={(value) => setCurrentStepData({ ...currentStepData, goal_target_date: value })}
                />
              </div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-800">
                ✓ When you click Next, this goal will be automatically created and linked to {goalClientName || 'the client'}'s profile.
              </p>
            </div>
          </div>
        );

      case 'investment_model_allocation':
        const modelConfig = currentStep.configuration?.investment_models || [];
        
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
              <div className="flex items-start gap-3">
                <TrendingUp className="w-6 h-6 text-indigo-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-indigo-900 mb-2">Select Investment Models</h4>
                  <p className="text-sm text-indigo-800">
                    Choose one or more investment models and specify the allocation percentage for each.
                  </p>
                </div>
              </div>
            </div>

            {modelConfig.map((modelItem, idx) => {
              const selectedModelId = modelItem.model_id_source === 'static' 
                ? modelItem.static_model_id 
                : currentStepData[`model_${idx}_id`];
              
              const allocationPercent = modelItem.allocation_source === 'static'
                ? modelItem.static_allocation
                : (currentStepData[`model_${idx}_allocation`] || 0);

              return (
                <Card key={idx} className="border-indigo-200">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-indigo-100 text-indigo-800">Model {idx + 1}</Badge>
                      {modelItem.model_id_source !== 'static' && (
                        <Badge variant="outline" className="text-xs">Dynamic</Badge>
                      )}
                    </div>

                    {modelItem.model_id_source === 'static' ? (
                      <div>
                        <Label className="text-xs text-slate-600">Investment Model</Label>
                        <p className="font-medium text-slate-900">
                          {investmentModels.find(m => m.id === modelItem.static_model_id)?.name || 'Unknown'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Label>Select Investment Model *</Label>
                        <Select
                          value={currentStepData[`model_${idx}_id`] || ''}
                          onValueChange={(value) => setCurrentStepData({ 
                            ...currentStepData, 
                            [`model_${idx}_id`]: value 
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a model..." />
                          </SelectTrigger>
                          <SelectContent>
                            {investmentModels.map(model => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.name} ({model.risk_profile})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {modelItem.allocation_source === 'static' ? (
                      <div>
                        <Label className="text-xs text-slate-600">Allocation Percentage</Label>
                        <p className="font-medium text-slate-900">{modelItem.static_allocation}%</p>
                      </div>
                    ) : (
                      <div>
                        <Label>Allocation Percentage *</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={currentStepData[`model_${idx}_allocation`] || ''}
                            onChange={(e) => setCurrentStepData({ 
                              ...currentStepData, 
                              [`model_${idx}_allocation`]: parseFloat(e.target.value) || 0 
                            })}
                            placeholder="0.00"
                            className="pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {(() => {
              const total = modelConfig.reduce((sum, modelItem, idx) => {
                const allocation = modelItem.allocation_source === 'static'
                  ? (parseFloat(modelItem.static_allocation) || 0)
                  : (parseFloat(currentStepData[`model_${idx}_allocation`]) || 0);
                return sum + allocation;
              }, 0);

              return (
                <div className={`p-3 rounded-lg border ${
                  total === 100 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className="text-sm font-medium">
                    {total === 100 ? (
                      <span className="text-green-800">✓ Total allocation: {total.toFixed(2)}%</span>
                    ) : (
                      <span className="text-amber-800">⚠ Total allocation: {total.toFixed(2)}% (must equal 100%)</span>
                    )}
                  </p>
                </div>
              );
            })()}
          </div>
        );

      case 'update_client_profile':
        const profileMappings = currentStep.configuration?.profile_mappings || [];
        const profileTargetClientId = (() => {
          const prev = Object.values(stepData).find(data => data?.client_id);
          return currentStepData.client_id || prev?.client_id || workflowClientId;
        })();
        const profileTargetClient = clients.find(c => c.id === profileTargetClientId);

        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
              <div className="flex items-start gap-3">
                <Users className="w-6 h-6 text-emerald-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-emerald-900 mb-2">Update Client Profile</h4>
                  <p className="text-sm text-emerald-800">
                    This step will automatically update the client's profile based on data from previous steps.
                  </p>
                </div>
              </div>
            </div>

            {profileTargetClient ? (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{profileTargetClient.full_name || `${profileTargetClient.first_name || ''} ${profileTargetClient.last_name || ''}`.trim()}</p>
                    <p className="text-xs text-slate-500">{profileTargetClient.email}</p>
                  </div>
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700">Target Client</Badge>
                </div>
              </div>
            ) : !profileTargetClientId ? (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <p className="text-sm text-amber-800">No client linked to this workflow. The profile update may not work.</p>
                </div>
              </div>
            ) : null}

            {profileMappings.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Fields to Update</p>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {profileMappings.map((mapping, idx) => {
                    const sourceStep = activeTemplate?.steps?.find(s => s.id === mapping.source_step_id);
                    const sourceStepIndex = activeTemplate?.steps?.findIndex(s => s.id === mapping.source_step_id);
                    
                    // Resolve the actual value from source step data
                    let resolvedValue = null;
                    if (sourceStepIndex !== undefined && sourceStepIndex !== -1) {
                      const sourceData = stepData[`step_${sourceStepIndex}`];
                      if (sourceData) {
                        // Try direct field path lookup
                        resolvedValue = sourceData[mapping.source_field_path];
                        // Try resolving field label to ID
                        if (resolvedValue === undefined || resolvedValue === null) {
                          const srcStep = activeTemplate?.steps?.[sourceStepIndex];
                          const srcFields = srcStep?.configuration?.form_fields?.questions || srcStep?.configuration?.fields || [];
                          const matchedField = srcFields.find(f => (f.question_text || f.label) === mapping.source_field_path);
                          if (matchedField) {
                            resolvedValue = sourceData[matchedField.id || matchedField.name];
                          }
                        }
                        // Try all values as last resort for simple steps
                        if ((resolvedValue === undefined || resolvedValue === null) && mapping.source_field_path) {
                          const parts = mapping.source_field_path.split('.');
                          let val = sourceData;
                          for (const p of parts) {
                            if (val && typeof val === 'object') val = val[p];
                            else { val = undefined; break; }
                          }
                          if (val !== undefined) resolvedValue = val;
                        }
                      }
                    }

                    return (
                      <div key={idx} className="flex items-center justify-between px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">
                              {mapping.target_field?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                          {resolvedValue !== null && resolvedValue !== undefined && resolvedValue !== '' ? (
                            <p className="text-sm text-emerald-700 mt-0.5 truncate">
                              → {typeof resolvedValue === 'object' ? JSON.stringify(resolvedValue) : String(resolvedValue)}
                            </p>
                          ) : (
                            <p className="text-xs text-slate-400 mt-0.5 italic">No value collected yet</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-shrink-0 ml-3">
                          <span>from</span>
                          <Badge variant="outline" className="text-xs">{sourceStep?.title || 'Step'}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">No field mappings configured for this step.</p>
                <p className="text-xs text-amber-700 mt-1">Edit the workflow template to add profile update mappings.</p>
              </div>
            )}

            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-800">
                ✓ Profile will be updated automatically after you review & confirm
              </p>
            </div>
          </div>
        );

      case 'update_financial_data':
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
              <div className="flex items-start gap-3">
                <Database className="w-6 h-6 text-cyan-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-cyan-900 mb-2">Update Data Center</h4>
                  <p className="text-sm text-cyan-800">
                    This step will automatically update the client's Data Center based on the configured mappings. 
                    No manual input needed - data flows from previous steps directly to the Data Center.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-900">
                ✓ {currentStep.configuration?.financial_data_mappings?.length || 0} mapping(s) configured
              </p>
              <p className="text-xs text-green-700 mt-1">
                Financial data will be updated automatically after you review & confirm
              </p>
            </div>
          </div>
        );

      case 'oli_guidance':
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex items-start gap-3">
                <OliBrandIcon className="w-6 h-6 mt-1" variant="color" />
                <div>
                  <h4 className="font-semibold text-purple-900 mb-2">Oli AI Guidance</h4>
                  <p className="text-sm text-purple-800">
                    Click "Ask Oli" below to get AI-powered guidance for completing this step.
                  </p>
                </div>
              </div>
            </div>
            {oliResponse && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{oliResponse}</p>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes about this step..."
                rows={4}
              />
            </div>
          </div>
        );

      case 'assign_segment':
        const isAutomationMode = currentStep.execution_mode === 'background';

        if (isAutomationMode) {
          // Automation mode: show what will be assigned
          const preConfiguredSegmentIds = currentStep.configuration?.segment_ids || [];
          const preConfiguredSegments = allSegments.filter(s => preConfiguredSegmentIds.includes(s.id));

          return (
            <div className="space-y-4">
              <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                <div className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-purple-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-purple-900 mb-2">Assign Segments Automatically</h4>
                    <p className="text-sm text-purple-800">
                      The following segments will be automatically assigned to the client after you review & confirm.
                    </p>
                  </div>
                </div>
              </div>

              {preConfiguredSegments.length === 0 ? (
                <div className="p-6 bg-amber-50 rounded-lg border border-amber-200 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm text-amber-800">No segments configured for this step</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {preConfiguredSegments.map(segment => (
                    <Card key={segment.id} className="border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-purple-600" />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{segment.name}</p>
                            {segment.description && (
                              <p className="text-xs text-slate-600 mt-0.5">{segment.description}</p>
                            )}
                          </div>
                          <Badge className="bg-purple-600 text-white">Auto-assign</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        }

        // User select mode: show searchable segment selector
        const filteredSegments = allSegments.filter(segment =>
          segment.name.toLowerCase().includes(segmentSearch.toLowerCase()) ||
          segment.description?.toLowerCase().includes(segmentSearch.toLowerCase())
        );

        const selectedSegmentIds = currentStepData.selected_segment_ids || [];

        const toggleSegment = (segmentId) => {
          const updated = selectedSegmentIds.includes(segmentId)
            ? selectedSegmentIds.filter(id => id !== segmentId)
            : [...selectedSegmentIds, segmentId];
          setCurrentStepData({ ...currentStepData, selected_segment_ids: updated });
        };

        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <Users className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Assign Client Segments</h4>
                  <p className="text-sm text-blue-800">
                    Search and select which segments to assign to this client. You can select multiple segments.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label>Search Segments</Label>
              <Input
                value={segmentSearch}
                onChange={(e) => setSegmentSearch(e.target.value)}
                placeholder="Search by segment name or description..."
                className="mt-1.5"
              />
            </div>

            {filteredSegments.length === 0 ? (
              <div className="p-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 text-center">
                <p className="text-sm text-slate-500">
                  {segmentSearch ? 'No segments match your search' : 'No segments available in the system'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto border border-slate-200 rounded-lg p-3">
                {filteredSegments.map((segment) => (
                  <div
                    key={segment.id}
                    className="flex items-start gap-3 p-3 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-blue-200"
                    onClick={() => toggleSegment(segment.id)}
                  >
                    <Checkbox
                      checked={selectedSegmentIds.includes(segment.id)}
                      onCheckedChange={() => toggleSegment(segment.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{segment.name}</p>
                      {segment.description && (
                        <p className="text-xs text-slate-600 mt-0.5">{segment.description}</p>
                      )}
                      {segment.segment_criteria && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {segment.segment_criteria.conditions?.length || 0} condition(s)
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedSegmentIds.length > 0 && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-900 font-medium mb-2">
                  ✓ {selectedSegmentIds.length} segment{selectedSegmentIds.length !== 1 ? 's' : ''} selected
                </p>
                <div className="flex flex-wrap gap-1">
                  {selectedSegmentIds.map((segmentId) => {
                    const segment = allSegments.find(s => s.id === segmentId);
                    return segment ? (
                      <Badge key={segmentId} className="bg-blue-600 text-white">
                        {segment.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        );

      case 'data_summary':
        const summaryFields = currentStep.configuration?.summary_fields || [];
        
        if (summaryFields.length === 0) {
          return (
            <div className="p-8 text-center bg-amber-50 rounded-lg border-2 border-dashed border-amber-200">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <p className="text-amber-900 font-semibold mb-2">No Summary Fields Configured</p>
              <p className="text-sm text-amber-700">
                This summary step doesn't have any fields configured yet. Please edit the workflow template to add summary fields.
              </p>
            </div>
          );
        }

        const getFieldValue = (sourceStepId, fieldPath) => {
          // Find the step index
          const sourceStepIndex = activeTemplate.steps.findIndex(s => s.id === sourceStepId);
          if (sourceStepIndex === -1) return 'N/A';
          
          // Get the step data
          const sourceStepData = stepData[`step_${sourceStepIndex}`];
          if (!sourceStepData) return 'N/A';
          
          // Try direct lookup first
          if (sourceStepData[fieldPath] !== undefined && sourceStepData[fieldPath] !== null) {
            return sourceStepData[fieldPath] || 'N/A';
          }
          
          // fieldPath might be a label — resolve to field ID
          const sourceStep = activeTemplate.steps[sourceStepIndex];
          const sourceFields = sourceStep?.configuration?.form_fields?.questions || sourceStep?.configuration?.fields || [];
          const matchedField = sourceFields.find(f => (f.question_text || f.label) === fieldPath);
          if (matchedField) {
            const idValue = sourceStepData[matchedField.id || matchedField.name];
            if (idValue !== undefined && idValue !== null) return idValue || 'N/A';
          }
          
          // Navigate the field path (for nested objects)
          const pathParts = fieldPath.split('.');
          let value = sourceStepData;
          
          for (const part of pathParts) {
            if (value && typeof value === 'object') {
              value = value[part];
            } else {
              return 'N/A';
            }
          }
          
          return value || 'N/A';
        };

        const formatValue = (value, format) => {
          if (value === 'N/A') return value;
          
          switch (format) {
            case 'email':
              return <a href={`mailto:${value}`} className="text-blue-600 hover:underline">{value}</a>;
            case 'phone':
              return <a href={`tel:${value}`} className="text-blue-600 hover:underline">{value}</a>;
            case 'date':
              return formatCalendarDate(value);
            case 'currency':
              return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
            default:
              return value;
          }
        };

        // Group fields by source step
        const fieldsByStep = {};
        summaryFields.forEach(field => {
          if (!fieldsByStep[field.source_step_id]) {
            fieldsByStep[field.source_step_id] = [];
          }
          fieldsByStep[field.source_step_id].push(field);
        });

        return (
          <div className="space-y-6">
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <ClipboardCheck className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">Review Your Information</h4>
                  <p className="text-sm text-blue-700">
                    Please verify all the details below are correct before proceeding to the next step.
                  </p>
                </div>
              </div>
            </div>
            
            {Object.entries(fieldsByStep).map(([sourceStepId, fields]) => {
              const sourceStep = activeTemplate.steps.find(s => s.id === sourceStepId);
              const stepNumber = activeTemplate.steps.findIndex(s => s.id === sourceStepId) + 1;
              
              return (
                <Card key={sourceStepId} className="border-slate-200 shadow-sm">
                  <CardContent className="p-0">
                    <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                          {stepNumber}
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{sourceStep?.title || 'Unknown Step'}</h4>
                          {sourceStep?.description && (
                            <p className="text-xs text-slate-600 mt-0.5">{sourceStep.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      {fields.map((field, idx) => {
                        const value = getFieldValue(field.source_step_id, field.source_field_path);
                        const displayValue = formatValue(value, field.format);
                        
                        return (
                          <div key={idx} className="grid grid-cols-2 gap-4 py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm text-slate-600">{field.label}</span>
                            <span className="text-sm text-slate-900 font-medium">{displayValue}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                💡 If any information is incorrect, use the "Previous" button to go back and make changes.
              </p>
            </div>
          </div>
        );

      case 'document_upload':
        const uploadConfig = currentStep.configuration || {};
        const allowedTypes = uploadConfig.allowed_file_types || ['pdf', 'doc', 'docx', 'jpg', 'png', 'xlsx'];
        const maxFiles = uploadConfig.max_files || 5;
        const uploadedFiles = currentStepData.uploaded_files || [];
        const uploadCategory = uploadConfig.document_category || 'general';

        const handleFileUpload = async (e) => {
          const files = Array.from(e.target.files || []);
          if (files.length === 0) return;

          const newFiles = [];
          for (const file of files) {
            if (uploadedFiles.length + newFiles.length >= maxFiles) {
              toast.error(`Maximum ${maxFiles} files allowed`);
              break;
            }
            // Validate file type
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (allowedTypes.length > 0 && !allowedTypes.includes(ext)) {
              toast.error(`File type .${ext} is not allowed. Allowed: ${allowedTypes.join(', ')}`);
              continue;
            }
            newFiles.push({
              id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: file.name,
              size: file.size,
              type: file.type,
              category: uploadCategory,
              uploaded_at: new Date().toISOString(),
              // In a real implementation, this would upload to storage and store the URL
              status: 'uploaded',
            });
          }

          setCurrentStepData({
            ...currentStepData,
            uploaded_files: [...uploadedFiles, ...newFiles],
          });
          // Reset file input
          e.target.value = '';
        };

        const removeFile = (fileId) => {
          setCurrentStepData({
            ...currentStepData,
            uploaded_files: uploadedFiles.filter(f => f.id !== fileId),
          });
        };

        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">Upload Documents</h4>
                  <p className="text-sm text-blue-700">
                    {uploadConfig.instructions || `Upload required documents for this step. Allowed types: ${allowedTypes.join(', ')}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Drop zone */}
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 hover:border-blue-400 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className="w-10 h-10 mb-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mb-2 text-sm text-slate-600"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-slate-500">
                  {allowedTypes.map(t => `.${t}`).join(', ')} (Max {maxFiles} files)
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                multiple={maxFiles > 1}
                accept={allowedTypes.map(t => `.${t}`).join(',')}
                onChange={handleFileUpload}
              />
            </label>

            {/* Uploaded files list */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Uploaded Files ({uploadedFiles.length}/{maxFiles})
                </Label>
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                      <p className="text-xs text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB • {file.category}
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 text-xs">Uploaded</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(file.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes about the uploaded documents..."
                rows={3}
              />
            </div>
          </div>
        );

      case 'approval': {
        const approvalConfig = currentStep.configuration || {};
        const approvalTitle = approvalConfig.approval_title || approvalConfig.instructions || 'Approval Required';
        const approvalStatus = currentStepData.approval_status;
        const isApproved = approvalStatus === 'approved';
        const isRejected = approvalStatus === 'rejected';

        // Determine if the current user is an authorized approver
        const configuredApprovers: any[] = approvalConfig.approvers || [];
        const isCurrentUserAuthorized = (() => {
          if (configuredApprovers.length === 0) return true; // no restrictions set — anyone can approve
          const userEmail = (currentUser?.email || '').toLowerCase();
          const userRole = (currentUser?.role || '').toLowerCase();
          return configuredApprovers.some((approver: any) => {
            switch (approver.type) {
              case 'specific_user':
                return userEmail && approver.value && userEmail === approver.value.toLowerCase();
              case 'role':
                return userRole && approver.value && userRole === approver.value.toLowerCase();
              case 'team':
                return false; // team resolution not available client-side; allow via backend
              case 'client_owner':
              case 'prior_assignee':
              case 'round_robin':
                return false; // dynamically resolved; cannot verify client-side
              default:
                return false;
            }
          });
        })();

        // Already decided — everyone can see the outcome (read-only)
        const isDecided = isApproved || isRejected;

        return (
          <div className="space-y-4">
            <div className={`p-6 rounded-lg border ${
              isApproved ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' :
              isRejected ? 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200' :
              'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'
            }`}>
              <div className="flex items-start gap-3">
                <CheckSquare className={`w-6 h-6 mt-1 ${
                  isApproved ? 'text-green-600' : isRejected ? 'text-red-600' : 'text-amber-600'
                }`} />
                <div>
                  <h4 className={`font-semibold mb-1 ${
                    isApproved ? 'text-green-900' : isRejected ? 'text-red-900' : 'text-amber-900'
                  }`}>
                    {isApproved ? 'Approved ✓' : isRejected ? 'Rejected ✗' : approvalTitle}
                  </h4>
                  <p className={`text-sm ${
                    isApproved ? 'text-green-700' : isRejected ? 'text-red-700' : 'text-amber-700'
                  }`}>
                    {approvalConfig.approval_message || approvalConfig.instructions || 'Review the information and approve or reject this step to continue.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Approval context / what's being approved */}
            {approvalConfig.approval_context && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-xs uppercase text-slate-500 mb-2 block">What needs approval</Label>
                <p className="text-sm text-slate-800">{approvalConfig.approval_context}</p>
              </div>
            )}

            {/* Unauthorized approver — show waiting message */}
            {!isDecided && !isCurrentUserAuthorized && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center space-y-2">
                <Shield className="w-8 h-8 text-slate-400 mx-auto" />
                <p className="text-sm font-medium text-slate-700">Awaiting approval decision</p>
                <p className="text-xs text-slate-500">
                  This step requires approval from a designated approver.
                  {configuredApprovers.length > 0 && (() => {
                    const names = configuredApprovers.map((a: any) => {
                      if (a.type === 'specific_user') return a.value;
                      if (a.type === 'role') return `${a.value} role`;
                      if (a.type === 'team') return `${a.value} team`;
                      if (a.type === 'client_owner') return 'the client owner';
                      if (a.type === 'prior_assignee') return 'the prior assignee';
                      if (a.type === 'round_robin') return `${a.value || 'assigned'} (round-robin)`;
                      return null;
                    }).filter(Boolean);
                    return names.length > 0 ? ` Assigned to: ${names.join(', ')}.` : '';
                  })()}
                </p>
                <p className="text-[11px] text-slate-400">You are logged in as {currentUser?.email || 'unknown'}.</p>
              </div>
            )}

            {/* Approval buttons — only shown to authorized approver before decision */}
            {!isDecided && isCurrentUserAuthorized && (
              <div className="flex gap-3">
                <Button
                  onClick={() => setCurrentStepData({
                    ...currentStepData,
                    approval_status: 'approved',
                    approved_at: new Date().toISOString(),
                    approved_by: currentUser?.full_name || currentUser?.name || currentUser?.email || 'Unknown'
                  })}
                  className="flex-1 h-12 text-base font-medium bg-white border-2 border-green-300 text-green-700 hover:bg-green-50 hover:border-green-500"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => setCurrentStepData({
                    ...currentStepData,
                    approval_status: 'rejected',
                    rejected_at: new Date().toISOString(),
                    rejected_by: currentUser?.full_name || currentUser?.name || currentUser?.email || 'Unknown'
                  })}
                  className="flex-1 h-12 text-base font-medium bg-white border-2 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-500"
                >
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Reject
                </Button>
              </div>
            )}

            {/* Decision info — shown to everyone once a decision is made */}
            {isDecided && (
              <>
                <div className={`p-3 rounded-lg border text-sm ${
                  isApproved ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  <p>
                    {isApproved ? '✓ Approved' : '✗ Rejected'} by {currentStepData.approved_by || currentStepData.rejected_by} on{' '}
                    {format(new Date(currentStepData.approved_at || currentStepData.rejected_at), "MMM d, yyyy 'at' h:mm a")}
                  </p>
                </div>
                {/* Allow the approver to change the decision */}
                {isCurrentUserAuthorized && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setCurrentStepData({
                        ...currentStepData,
                        approval_status: 'approved',
                        approved_at: new Date().toISOString(),
                        approved_by: currentUser?.full_name || currentUser?.name || currentUser?.email || 'Unknown',
                        rejected_at: undefined,
                        rejected_by: undefined,
                      })}
                      className={`flex-1 h-10 text-sm font-medium ${isApproved ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-white border-2 border-green-300 text-green-700 hover:bg-green-50'}`}
                    >
                      <Check className="w-4 h-4 mr-1.5" />
                      {isApproved ? '✓ Approved' : 'Change to Approved'}
                    </Button>
                    <Button
                      onClick={() => setCurrentStepData({
                        ...currentStepData,
                        approval_status: 'rejected',
                        rejected_at: new Date().toISOString(),
                        rejected_by: currentUser?.full_name || currentUser?.name || currentUser?.email || 'Unknown',
                        approved_at: undefined,
                        approved_by: undefined,
                      })}
                      className={`flex-1 h-10 text-sm font-medium ${isRejected ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-white border-2 border-red-300 text-red-700 hover:bg-red-50'}`}
                    >
                      <AlertCircle className="w-4 h-4 mr-1.5" />
                      {isRejected ? '✗ Rejected' : 'Change to Rejected'}
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Comments / reason */}
            <div>
              <Label>{isRejected ? 'Rejection Reason' : 'Comments'} {isRejected && <span className="text-red-500">*</span>}</Label>
              <Textarea
                value={currentStepData.approval_comments || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, approval_comments: e.target.value })}
                placeholder={isRejected ? "Please provide a reason for rejection..." : "Add any comments or notes..."}
                rows={3}
                className="mt-1.5"
              />
            </div>
          </div>
        );
      }

      case 'phase_header':
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-300">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-sm font-bold">
                  §
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">
                    {currentStep.configuration?.phase_name || currentStep.title || 'New Phase'}
                  </h4>
                  <p className="text-sm text-slate-600">
                    {currentStep.configuration?.phase_description || currentStep.description || 'This marks the beginning of a new phase in the workflow.'}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700">Click "Next Step" to continue to the next step in this phase.</p>
            </div>
          </div>
        );

      case 'wait_delay':
      case 'wait_for_completion':
        const waitConfig = currentStep.configuration || {};
        const waitDuration = waitConfig.duration || waitConfig.delay_duration || '—';
        const waitUnit = waitConfig.unit || waitConfig.delay_unit || 'days';

        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
              <div className="flex items-start gap-3">
                <RefreshCw className="w-6 h-6 text-orange-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-orange-900 mb-1">Waiting Period</h4>
                  <p className="text-sm text-orange-700">
                    {waitConfig.wait_message || `This step requires a wait of ${waitDuration} ${waitUnit} before proceeding.`}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Duration</p>
                  <p className="text-lg font-semibold text-slate-900">{waitDuration} {waitUnit}</p>
                </div>
                {waitConfig.wait_reason && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Reason</p>
                    <p className="text-sm text-slate-700">{waitConfig.wait_reason}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">Click "Next Step" to acknowledge and proceed.</p>
            </div>
          </div>
        );

      case 'automation_trigger':
        const automationConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
              <div className="flex items-start gap-3">
                <RefreshCw className="w-6 h-6 text-violet-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-violet-900 mb-1">Automation Trigger</h4>
                  <p className="text-sm text-violet-700">
                    {automationConfig.trigger_description || 'This step will trigger an automated action after you review & confirm.'}
                  </p>
                </div>
              </div>
            </div>
            {automationConfig.automation_name && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase font-medium">Automation</p>
                <p className="text-sm font-medium text-slate-900">{automationConfig.automation_name}</p>
              </div>
            )}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The automation will run automatically after you review & confirm.</p>
            </div>
          </div>
        );

      case 'parallel_tasks':
        const parallelConfig = currentStep.configuration || {};
        const parallelTasks = parallelConfig.tasks || parallelConfig.parallel_steps || [];
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
              <div className="flex items-start gap-3">
                <Link2 className="w-6 h-6 text-teal-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-teal-900 mb-1">Parallel Tasks</h4>
                  <p className="text-sm text-teal-700">
                    The following tasks will be executed simultaneously.
                  </p>
                </div>
              </div>
            </div>
            {parallelTasks.length > 0 ? (
              <div className="space-y-2">
                {parallelTasks.map((task, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </div>
                    <p className="text-sm text-slate-800">{task.title || task.name || `Task ${idx + 1}`}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <p className="text-sm text-slate-600">No parallel tasks configured.</p>
              </div>
            )}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ All tasks will be created after you review & confirm.</p>
            </div>
          </div>
        );

      case 'taskflow_execution':
        const taskflowConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
              <div className="flex items-start gap-3">
                <ClipboardCheck className="w-6 h-6 text-indigo-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-indigo-900 mb-1">Execute Taskflow</h4>
                  <p className="text-sm text-indigo-700">
                    {taskflowConfig.description || 'This step will launch a sub-workflow / taskflow.'}
                  </p>
                </div>
              </div>
            </div>
            {taskflowConfig.taskflow_name && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase font-medium">Taskflow</p>
                <p className="text-sm font-medium text-slate-900">{taskflowConfig.taskflow_name}</p>
              </div>
            )}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The taskflow will be launched after you review & confirm.</p>
            </div>
          </div>
        );

      case 'data_mapping':
        const dataMappingConfig = currentStep.configuration || {};
        const mappings = dataMappingConfig.mappings || [];
        return (
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
              <div className="flex items-start gap-3">
                <Database className="w-6 h-6 text-cyan-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-cyan-900 mb-1">Data Mapping</h4>
                  <p className="text-sm text-cyan-700">
                    Data will be automatically mapped and transferred between systems.
                  </p>
                </div>
              </div>
            </div>
            {mappings.length > 0 ? (
              <div className="space-y-2">
                {mappings.map((mapping, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg text-sm">
                    <span className="text-slate-600">{mapping.source_field || mapping.from}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-900">{mapping.target_field || mapping.to}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <p className="text-sm text-slate-600">No data mappings configured.</p>
              </div>
            )}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Data will be mapped automatically after you review & confirm.</p>
            </div>
          </div>
        );

      case 'send_communication_external': {
        const extCfg = currentStep.configuration || {};
        const commType = extCfg.communication_type || '';
        const COMM_ICONS: Record<string, any> = {
          email: Mail, sms: Phone, push: Bell, dm: Send, channel: Hash,
        };
        const COMM_COLORS: Record<string, { from: string; border: string; textH: string; textB: string; icon: string }> = {
          email:   { from: 'from-blue-50 to-indigo-50',     border: 'border-blue-200',   textH: 'text-blue-900',   textB: 'text-blue-700',    icon: 'text-blue-600' },
          sms:     { from: 'from-emerald-50 to-green-50',   border: 'border-emerald-200',textH: 'text-emerald-900',textB: 'text-emerald-700', icon: 'text-emerald-600' },
          push:    { from: 'from-violet-50 to-purple-50',   border: 'border-violet-200', textH: 'text-violet-900', textB: 'text-violet-700',  icon: 'text-violet-600' },
          dm:      { from: 'from-sky-50 to-blue-50',        border: 'border-sky-200',    textH: 'text-sky-900',    textB: 'text-sky-700',     icon: 'text-sky-600' },
          channel: { from: 'from-amber-50 to-yellow-50',    border: 'border-amber-200',  textH: 'text-amber-900',  textB: 'text-amber-700',   icon: 'text-amber-600' },
        };
        const COMM_LABELS: Record<string, string> = { email: 'Email', sms: 'SMS', push: 'Push Notification', dm: 'Direct Message', channel: 'Channel Notification' };
        const CommIcon = COMM_ICONS[commType] || Send;
        const colors = COMM_COLORS[commType] || COMM_COLORS.email;
        const channelLabel = COMM_LABELS[commType] || 'Communication';

        const recipientLabel = (rt: string) => {
          if (rt === 'contact' || rt === 'enrolled_entity') return 'Enrolled Client';
          if (rt === 'assigned_advisor') return 'Assigned Advisor';
          if (rt === 'all_team') return 'All Team Members';
          if (rt === 'specific_email') return extCfg.to_email || 'Specific Email';
          if (rt === 'specific_number') return extCfg.to_phone || 'Specific Number';
          if (rt === 'client') return 'Client';
          return rt?.replace(/_/g, ' ') || 'Enrolled Client';
        };

        const templateLabel = (!extCfg.template_id || extCfg.template_id === '_custom') ? 'Custom message' : (extCfg.template_name || extCfg.template_id);
        const isCustom = !extCfg.template_id || extCfg.template_id === '_custom';

        return (
          <div className="space-y-4">
            <div className={`p-4 bg-gradient-to-r ${colors.from} rounded-lg border ${colors.border}`}>
              <div className="flex items-start gap-3">
                <CommIcon className={`w-5 h-5 ${colors.icon} mt-0.5`} />
                <div>
                  <p className={`text-sm font-medium ${colors.textH}`}>Send {channelLabel}</p>
                  <p className={`text-xs ${colors.textB} mt-0.5`}>
                    {commType ? `This ${channelLabel.toLowerCase()} will be sent after you confirm.` : 'Communication type not configured. Edit the step before running.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="text-xs text-slate-500 w-28 flex-shrink-0">Channel</span>
                <span className="text-xs font-medium text-slate-800">{channelLabel}</span>
              </div>
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="text-xs text-slate-500 w-28 flex-shrink-0">Recipient</span>
                <span className="text-xs font-medium text-slate-800">{recipientLabel(extCfg.recipient_type || extCfg.target || 'contact')}</span>
              </div>
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="text-xs text-slate-500 w-28 flex-shrink-0">Template</span>
                <span className="text-xs font-medium text-slate-800">{templateLabel}</span>
              </div>
              {commType === 'email' && extCfg.subject && (
                <div className="flex items-start gap-2 px-3 py-2">
                  <span className="text-xs text-slate-500 w-28 flex-shrink-0">Subject</span>
                  <span className="text-xs font-medium text-slate-800">{extCfg.subject}</span>
                </div>
              )}
              {commType === 'push' && extCfg.title && (
                <div className="flex items-start gap-2 px-3 py-2">
                  <span className="text-xs text-slate-500 w-28 flex-shrink-0">Title</span>
                  <span className="text-xs font-medium text-slate-800">{extCfg.title}</span>
                </div>
              )}
            </div>

            {isCustom && (extCfg.body || extCfg.message) && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 mb-1">Message preview</p>
                <p className="text-xs text-slate-700 whitespace-pre-wrap">{extCfg.body || extCfg.message}</p>
              </div>
            )}

            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Opt-out status, consent, and delivery eligibility will be checked at send time.</p>
            </div>
          </div>
        );
      }

      case 'send_notification_internal': {
        const intCfg = currentStep.configuration || {};
        const notifType = intCfg.notification_type || '';
        const NOTIF_ICONS: Record<string, any> = { in_app: Bell, slack: Hash, email: Mail };
        const NOTIF_COLORS: Record<string, { from: string; border: string; textH: string; textB: string; icon: string }> = {
          in_app:  { from: 'from-indigo-50 to-violet-50',  border: 'border-indigo-200',  textH: 'text-indigo-900',  textB: 'text-indigo-700',  icon: 'text-indigo-600' },
          slack:   { from: 'from-emerald-50 to-green-50',  border: 'border-emerald-200', textH: 'text-emerald-900', textB: 'text-emerald-700', icon: 'text-emerald-600' },
          email:   { from: 'from-blue-50 to-indigo-50',    border: 'border-blue-200',    textH: 'text-blue-900',    textB: 'text-blue-700',    icon: 'text-blue-600' },
        };
        const NOTIF_LABELS: Record<string, string> = { in_app: 'In-App Notification', slack: 'Slack', email: 'Internal Email' };
        const NotifIcon = NOTIF_ICONS[notifType] || Bell;
        const nColors = NOTIF_COLORS[notifType] || NOTIF_COLORS.in_app;
        const notifLabel = NOTIF_LABELS[notifType] || 'Notification';

        const recipientLabel = (rt: string) => {
          if (rt === 'specific_user') return intCfg.target_user || intCfg.to_email || 'Specific User';
          if (rt === 'role') return `Role: ${intCfg.recipient_role || 'unset'}`;
          if (rt === 'team') return intCfg.team_name || 'Team';
          if (rt === 'client_owner') return 'Client Owner';
          if (rt === 'prior_assignee') return 'Prior Assignee';
          return rt?.replace(/_/g, ' ') || 'Specific User';
        };

        const templateLabel = (!intCfg.template_id || intCfg.template_id === '_custom') ? 'Custom message' : (intCfg.template_name || intCfg.template_id);
        const isCustom = !intCfg.template_id || intCfg.template_id === '_custom';

        return (
          <div className="space-y-4">
            <div className={`p-4 bg-gradient-to-r ${nColors.from} rounded-lg border ${nColors.border}`}>
              <div className="flex items-start gap-3">
                <NotifIcon className={`w-5 h-5 ${nColors.icon} mt-0.5`} />
                <div>
                  <p className={`text-sm font-medium ${nColors.textH}`}>Send {notifLabel}</p>
                  <p className={`text-xs ${nColors.textB} mt-0.5`}>
                    {notifType ? `This ${notifLabel.toLowerCase()} will be sent after you confirm.` : 'Notification type not configured. Edit the step before running.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="text-xs text-slate-500 w-28 flex-shrink-0">Channel</span>
                <span className="text-xs font-medium text-slate-800">{notifLabel}</span>
              </div>
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="text-xs text-slate-500 w-28 flex-shrink-0">Recipient</span>
                <span className="text-xs font-medium text-slate-800">{recipientLabel(intCfg.recipient_type || 'specific_user')}</span>
              </div>
              <div className="flex items-start gap-2 px-3 py-2">
                <span className="text-xs text-slate-500 w-28 flex-shrink-0">Template</span>
                <span className="text-xs font-medium text-slate-800">{templateLabel}</span>
              </div>
              {notifType === 'email' && intCfg.subject && (
                <div className="flex items-start gap-2 px-3 py-2">
                  <span className="text-xs text-slate-500 w-28 flex-shrink-0">Subject</span>
                  <span className="text-xs font-medium text-slate-800">{intCfg.subject}</span>
                </div>
              )}
              {notifType === 'slack' && intCfg.channel && (
                <div className="flex items-start gap-2 px-3 py-2">
                  <span className="text-xs text-slate-500 w-28 flex-shrink-0">Slack Channel</span>
                  <span className="text-xs font-medium text-slate-800">{intCfg.channel}</span>
                </div>
              )}
            </div>

            {isCustom && (intCfg.body || intCfg.message) && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 mb-1">Message preview</p>
                <p className="text-xs text-slate-700 whitespace-pre-wrap">{intCfg.body || intCfg.message}</p>
              </div>
            )}

            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Recipient availability and channel access will be checked at send time.</p>
            </div>
          </div>
        );
      }

      case 'transform_segment': {
        const tsCfg = currentStep.configuration || {};
        const tsOp = tsCfg.operation || '';
        const tsSegNames: string[] = tsCfg.segment_names || [];
        const tsSegIds: string[] = tsCfg.segment_ids || [];
        const tsIsAdd = tsOp === 'add';
        return (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${tsIsAdd ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
              <div className="flex items-start gap-3">
                <Tags className={`w-5 h-5 mt-0.5 ${tsIsAdd ? 'text-emerald-600' : 'text-rose-600'}`} />
                <div>
                  <p className={`text-sm font-medium ${tsIsAdd ? 'text-emerald-900' : 'text-rose-900'}`}>
                    {tsIsAdd ? 'Add to Segment' : tsOp === 'remove' ? 'Remove from Segment' : 'Transform Segments'}
                  </p>
                  <p className={`text-xs mt-0.5 ${tsIsAdd ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {tsIsAdd
                      ? 'The enrolled object will be added to the selected segments.'
                      : tsOp === 'remove'
                      ? 'The enrolled object will be removed from the selected segments.'
                      : 'Segment operation not configured.'}
                  </p>
                </div>
              </div>
            </div>

            {tsSegIds.length > 0 ? (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="px-3 py-2 text-slate-500 font-medium w-1/3">Operation</td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[9px] ${tsIsAdd ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-rose-100 text-rose-700 hover:bg-rose-100'}`}>
                          {tsIsAdd ? 'Add to Segment' : 'Remove from Segment'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-slate-500 font-medium">Segments</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(tsSegNames.length > 0 ? tsSegNames : tsSegIds).map((n, i) => (
                            <Badge key={i} className="text-[9px] bg-violet-100 text-violet-700 hover:bg-violet-100">{n}</Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
                <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-1.5" />
                <p className="text-xs text-amber-800">No segments configured for this step</p>
              </div>
            )}

            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Segment membership will be updated automatically when this step executes.</p>
            </div>
          </div>
        );
      }

      case 'send_email': {
        const emailConfig = currentStep.configuration || {};
        const emailTo = emailConfig.to === 'enrolled_entity' ? 'Enrolled Client' : (emailConfig.specificRecipients?.join(', ') || emailConfig.to || '');
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Send Email</p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    {emailConfig.to === 'enrolled_entity' ? 'This email will be sent to the enrolled client.' : 'This email will be sent to the specified recipients.'}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>To</Label>
              {emailConfig.to === 'enrolled_entity' ? (
                <Input value="Enrolled Client" disabled className="mt-1" />
              ) : (
                <Input
                  value={currentStepData.email_to ?? emailTo}
                  onChange={(e) => setCurrentStepData({ ...currentStepData, email_to: e.target.value })}
                  placeholder="Enter recipient email address"
                  className="mt-1"
                />
              )}
              <BindingIndicator fieldName="email_to" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                value={currentStepData.email_subject ?? emailConfig.subject ?? ''}
                onChange={(e) => setCurrentStepData({ ...currentStepData, email_subject: e.target.value })}
                placeholder="Email subject"
                className="mt-1"
              />
              <BindingIndicator fieldName="email_subject" />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea
                value={currentStepData.email_body ?? emailConfig.body ?? ''}
                onChange={(e) => setCurrentStepData({ ...currentStepData, email_body: e.target.value })}
                placeholder="Email body..."
                rows={5}
                className="mt-1"
              />
              <BindingIndicator fieldName="email_body" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
              <BindingIndicator fieldName="notes" />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The email will be sent after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'send_sms': {
        const smsConfig = currentStep.configuration || {};
        const smsTo = smsConfig.to === 'enrolled_entity' ? 'Enrolled Client' : (smsConfig.to || '');
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Send SMS</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    {smsConfig.to === 'enrolled_entity' ? 'This message will be sent to the enrolled client.' : 'This message will be sent to the specified recipient.'}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>To</Label>
              <Input value={smsTo} disabled className="mt-1" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={currentStepData.sms_message ?? smsConfig.message ?? ''}
                onChange={(e) => setCurrentStepData({ ...currentStepData, sms_message: e.target.value })}
                placeholder="SMS message..."
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The SMS will be sent after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'send_push_notification': {
        const pushConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-900">Send Push Notification</p>
                  <p className="text-xs text-purple-700 mt-0.5">
                    A push notification will be sent to the target user(s).
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={currentStepData.push_title ?? pushConfig.title ?? ''}
                onChange={(e) => setCurrentStepData({ ...currentStepData, push_title: e.target.value })}
                placeholder="Notification title"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea
                value={currentStepData.push_body ?? pushConfig.body ?? ''}
                onChange={(e) => setCurrentStepData({ ...currentStepData, push_body: e.target.value })}
                placeholder="Notification body..."
                rows={3}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The notification will be sent after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'send_internal_message': {
        const internalConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
              <div className="flex items-start gap-3">
                <Send className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-indigo-900">Send Internal Message</p>
                  <p className="text-xs text-indigo-700 mt-0.5">
                    An internal notification will be sent to team members.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={currentStepData.internal_message ?? internalConfig.message ?? ''}
                onChange={(e) => setCurrentStepData({ ...currentStepData, internal_message: e.target.value })}
                placeholder="Message content..."
                rows={4}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The message will be sent after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'send_channel_notification': {
        const channelConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
              <div className="flex items-start gap-3">
                <Hash className="w-5 h-5 text-teal-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-teal-900">Channel Notification</p>
                  <p className="text-xs text-teal-700 mt-0.5">
                    A notification will be sent to {channelConfig.platform || 'the configured channel'}.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={currentStepData.channel_message ?? channelConfig.message ?? ''}
                onChange={(e) => setCurrentStepData({ ...currentStepData, channel_message: e.target.value })}
                placeholder="Channel message..."
                rows={4}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The notification will be posted after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'goto_action': {
        const gotoConfig = currentStep.configuration || {};
        const targetStep = visibleSteps.find(s => s.id === gotoConfig.targetNodeId);
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-200">
              <div className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-slate-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Go To Action</p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    This step will jump to another step in the workflow.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 uppercase font-medium">Target Step</p>
              <p className="text-sm font-medium text-slate-900 mt-1">
                {targetStep?.title || gotoConfig.targetNodeId || 'Not configured'}
              </p>
            </div>
            {gotoConfig.maxLoops && (
              <div className="text-xs text-slate-500">Max loops: {gotoConfig.maxLoops}</div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Workflow will jump to the target step after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'goto_workflow': {
        const gotoWfConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
              <div className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-indigo-900">Go To Workflow</p>
                  <p className="text-xs text-indigo-700 mt-0.5">
                    This step will transfer execution to another workflow.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 uppercase font-medium">Target Workflow</p>
              <p className="text-sm font-medium text-slate-900 mt-1">
                {gotoWfConfig.targetWorkflowId || 'Not configured'}
              </p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Execution will transfer to the target workflow after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'object_select': {
        const selectConfig = currentStep.configuration || {};
        const objectType = normalizeNonClientObjectType(selectConfig.object_type || selectConfig.objectType || selectConfig.entity_type || selectConfig.entityType || currentStepData.object_type || 'case');
        const runtimeObjects = selectableObjectLists[objectType] || [];
        const selectedObjectId = currentStepData.object_id || selectConfig.object_id || selectConfig.selected_object_id || selectConfig.entityId || '';
        const selectedObject = runtimeObjects.find((item) => item.id === selectedObjectId) || null;
        const filteredRuntimeObjects = runtimeObjects.filter((item) => {
          if (selectConfig.filter_status && String(item.status || '').toLowerCase() !== String(selectConfig.filter_status).toLowerCase()) return false;
          if (selectConfig.filter_client_id && getNonClientObjectClientId(item) !== selectConfig.filter_client_id) return false;
          if (selectConfig.filter_assignee_id && getNonClientObjectAssigneeId(item) !== selectConfig.filter_assignee_id) return false;
          return true;
        }).slice(0, 250);
        const associatedClientId = selectedObject ? getNonClientObjectClientId(selectedObject) : (selectConfig.associated_client_id || '');
        const associatedClient = associatedClientId ? clients.find((client) => client.id === associatedClientId) : null;
        const selectedAssigneeId = selectedObject ? getNonClientObjectAssigneeId(selectedObject) : '';
        const selectedAssignee = selectedAssigneeId ? users.find((user) => user.id === selectedAssigneeId || user.email === selectedAssigneeId) : null;
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
              <div className="flex items-start gap-3">
                <Database className="w-5 h-5 text-cyan-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-cyan-900">Select Non-Client Object</p>
                  <p className="text-xs text-cyan-700 mt-0.5">
                    Select an existing {objectType.replace(/_/g, ' ')} and expose its IDs, fields, assignee, status, and related client to downstream actions.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase font-medium">Object Type</p>
                <p className="text-sm font-semibold text-slate-900 mt-1 capitalize">{objectType.replace(/_/g, ' ')}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Badge variant="outline" className={selectConfig.use_as_workflow_target || selectConfig.is_workflow_target || currentStepIndex === 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}>
                    {selectConfig.use_as_workflow_target || selectConfig.is_workflow_target || currentStepIndex === 0 ? 'Workflow target enabled' : 'Reference object'}
                  </Badge>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{filteredRuntimeObjects.length} available</Badge>
                </div>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <Label className="text-xs text-slate-500 uppercase font-medium">Existing Object</Label>
                <Select value={selectedObjectId || ""} onValueChange={(value) => {
                  const record = runtimeObjects.find((item) => item.id === value);
                  const clientId = record ? getNonClientObjectClientId(record) : '';
                  setCurrentStepData({
                    ...currentStepData,
                    object_id: value,
                    selected_object_id: value,
                    entityId: value,
                    object_type: objectType,
                    object_title: record ? getNonClientObjectTitle(record) : '',
                    object_status: record?.status || '',
                    associated_client_id: clientId,
                    client_id: clientId,
                    assigned_user_id: record ? getNonClientObjectAssigneeId(record) : '',
                  });
                }}>
                  <SelectTrigger className="mt-1.5 h-9 text-xs"><SelectValue placeholder="Select existing object" /></SelectTrigger>
                  <SelectContent>
                    {filteredRuntimeObjects.map((record) => (
                      <SelectItem key={record.id} value={record.id}>{getNonClientObjectTitle(record)}{record.status ? ` · ${record.status}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400 mt-1">Pre-select in builder or choose here before review.</p>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-medium">Selected Object Preview</p>
                  {selectedObject ? (
                    <div className="mt-1 space-y-1">
                      <p className="text-sm font-semibold text-slate-900">{getNonClientObjectTitle(selectedObject)}</p>
                      <p className="text-xs text-slate-500">{objectType.replace(/_/g, ' ')} · {selectedObject.status || 'No status'}</p>
                      {associatedClientId && (
                        <p className="text-xs text-slate-600">
                          Associated client: <span className="font-medium text-slate-900">{associatedClient ? getClientDisplayName(associatedClient) : associatedClientId}</span>
                          {associatedClient?.email ? <span className="text-slate-400"> · {associatedClient.email}</span> : null}
                        </p>
                      )}
                      {selectedAssigneeId && (
                        <p className="text-xs text-slate-600">
                          Assignee/owner: <span className="font-medium text-slate-900">{selectedAssignee ? getUserDisplayName(selectedAssignee) : selectedAssigneeId}</span>
                          {selectedAssignee?.email ? <span className="text-slate-400"> · {selectedAssignee.email}</span> : null}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400">Object ID: {selectedObject.id}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-700 mt-1">No existing object selected yet.</p>
                  )}
                </div>
                <Badge variant="outline" className={selectedObject ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                  {selectedObject ? 'Ready' : 'Selection required'}
                </Badge>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 uppercase font-medium">Related Client Context</p>
              {associatedClientId ? (
                <div className="mt-1 space-y-1">
                  <p className="text-sm font-semibold text-slate-900">{associatedClient ? getClientDisplayName(associatedClient) : associatedClientId}</p>
                  <p className="text-xs text-slate-500">client_id: {associatedClientId}</p>
                  {associatedClient?.email && <p className="text-xs text-slate-500">email: {associatedClient.email}</p>}
                </div>
              ) : (
                <p className="text-sm text-slate-500 mt-1">No associated client found on this object. Downstream client variables remain blank.</p>
              )}
            </div>

            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs font-semibold text-emerald-800">Available downstream values</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['object_id', 'object_type', 'object_title', 'object_status', 'assigned_user_id', 'associated_client_id', 'client_id', `${objectType}_id`, 'selected_object_fields'].map((field) => (
                  <Badge key={field} variant="secondary" className="text-[10px]">{field}</Badge>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'crm_create':
      case 'object_create':
      case 'crm_update':
      case 'crm_delete':
      case 'crm_assign_ownership':
      case 'crm_subscription_management':
      case 'crm_phone_validation': {
        const crmConfig = currentStep.configuration || {};
        const crmLabels = {
          crm_create: { title: 'Create Record', desc: 'A new CRM record will be created.' },
          object_create: { title: 'Create Non-Client Object', desc: `A ${crmConfig.object_type || crmConfig.entity_type || 'non-client object'} will be created.` },
          crm_update: { title: 'Update Record', desc: 'An existing CRM record will be updated.' },
          crm_delete: { title: 'Delete Record', desc: 'A CRM record will be deleted.' },
          crm_assign_ownership: { title: 'Assign Ownership', desc: 'Record ownership will be changed.' },
          crm_subscription_management: { title: 'Manage Subscription', desc: 'Subscription preferences will be updated.' },
          crm_phone_validation: { title: 'Validate Phone', desc: 'A phone number will be validated.' },
        };
        const crmMeta = crmLabels[currentStep.step_type] || { title: 'CRM Action', desc: 'A CRM action will be performed.' };
        const objectType = String(crmConfig.object_type || crmConfig.entity_type || crmConfig.objectType || '').toLowerCase();
        const isLeadObjectCreate = currentStep.step_type === 'object_create' && objectType === 'lead';
        const previousClientEntry = Object.entries(stepData || {})
          .filter(([key, data]) => Number(String(key).match(/step_(\d+)/)?.[1] ?? -1) < currentStepIndex && (data as any)?.client_id)
          .sort(([a], [b]) => Number(String(b).match(/step_(\d+)/)?.[1] ?? -1) - Number(String(a).match(/step_(\d+)/)?.[1] ?? -1))[0];
        const previousClientData = previousClientEntry?.[1] || {};
        const mappedClientIdPreview = crmConfig.client_id ? resolveWorkflowText(crmConfig.client_id, stepData) : '';
        const resolvedClientIdPreview = crmConfig.client_id_source === 'none'
          ? ''
          : (currentStepData.client_id || mappedClientIdPreview || (previousClientData as any)?.client_id || workflowClientId || '');
        const resolvedClientPreview = clients.find((client) => client.id === resolvedClientIdPreview) || (workflowClient?.id === resolvedClientIdPreview ? workflowClient : null);
        const selectedClientsPreview = Array.isArray((previousClientData as any)?.selected_clients) ? (previousClientData as any).selected_clients : [];
        const clientSourceLabel = currentStepData.client_id
          ? 'Current step runtime value'
          : mappedClientIdPreview
            ? 'Mapped client_id variable'
            : (previousClientData as any)?.client_id
              ? `Previous step${previousClientEntry ? ` ${Number(String(previousClientEntry[0]).match(/step_(\d+)/)?.[1] ?? 0) + 1}` : ''}`
              : workflowClientId
                ? 'Enrolled workflow client'
                : 'Not resolved yet';
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
              <div className="flex items-start gap-3">
                <Database className="w-5 h-5 text-cyan-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-cyan-900">{crmMeta.title}</p>
                  <p className="text-xs text-cyan-700 mt-0.5">{crmMeta.desc}</p>
                  {currentStep.step_type === 'object_create' && (
                    <p className="text-xs text-cyan-700 mt-1">This action uses the Create Non-Client Object flow and runs after review/confirmation.</p>
                  )}
                </div>
              </div>
            </div>
            {(crmConfig.entityType || crmConfig.entity_type || crmConfig.object_type) && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase font-medium">Object Type</p>
                <p className="text-sm font-medium text-slate-900 mt-1 capitalize">{crmConfig.object_type || crmConfig.entity_type || crmConfig.entityType}</p>
              </div>
            )}
            {currentStep.step_type === 'object_create' && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Resolved Associated Client</p>
                    {resolvedClientIdPreview ? (
                      <div className="mt-1 space-y-1">
                        <p className="text-sm font-semibold text-slate-900">{resolvedClientPreview ? getClientDisplayName(resolvedClientPreview) : resolvedClientIdPreview}</p>
                        <p className="text-xs text-slate-500">client_id: {resolvedClientIdPreview}</p>
                        {resolvedClientPreview?.email && <p className="text-xs text-slate-500">email: {resolvedClientPreview.email}</p>}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-700 mt-1">No client is resolved yet. Add/select a client earlier, enroll a client, or map a client_id.</p>
                    )}
                  </div>
                  <Badge variant="outline" className={resolvedClientIdPreview ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                    {clientSourceLabel}
                  </Badge>
                </div>
                {selectedClientsPreview.length > 1 && (
                  <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
                    <p className="text-xs font-semibold text-blue-900">Multiple clients were selected earlier</p>
                    <p className="text-xs text-blue-700 mt-1">
                      This screen shows the client for the current workflow run only. The workflow splits selected clients into independent runs so each lead gets one stable client_id and one downstream lead_id.
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {selectedClientsPreview.map((client, idx) => (
                        <Badge key={client.client_id || idx} variant="outline" className={client.client_id === resolvedClientIdPreview ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-blue-700 border-blue-200"}>
                          {client.client_name || client.name || client.client_id}{client.client_id === resolvedClientIdPreview ? ' · current run' : ' · separate run'}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isLeadObjectCreate && (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-700 uppercase font-medium">Lead Creation Rules</p>
                  <div className="mt-2 space-y-1 text-sm text-amber-900">
                    <p><span className="font-medium">Client ID source:</span> {crmConfig.client_id_source === 'mapped_value' ? 'Mapped workflow variable' : crmConfig.client_id_source === 'none' ? 'No client association' : 'Enrolled or previous client_id'}</p>
                    <p><span className="font-medium">Autofill from client:</span> {crmConfig.autofill_from_client === false ? 'No' : 'Yes'}</p>
                    <p><span className="font-medium">Duplicate prevention:</span> {crmConfig.duplicate_prevention === false ? 'Off' : 'Email check enabled'}</p>
                    <p><span className="font-medium">Required fields:</span> {(crmConfig.required_lead_fields || ['first_name', 'last_name', 'email']).join(', ')}</p>
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-700 uppercase font-medium">Lead Defaults</p>
                  <div className="mt-2 space-y-1 text-sm text-purple-900">
                    <p><span className="font-medium">Status:</span> {crmConfig.lead_status || 'new'}</p>
                    <p><span className="font-medium">Stage:</span> {crmConfig.lead_stage || 'Not set'}</p>
                    <p><span className="font-medium">Source:</span> {crmConfig.lead_source || 'workflow'}</p>
                    <p><span className="font-medium">Qualification:</span> {crmConfig.qualification_status || 'Not set'}</p>
                  </div>
                </div>
              </div>
            )}
            {((crmConfig.fields?.length || crmConfig.field_mappings?.length) > 0) && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase font-medium mb-2">Field Mappings</p>
                <div className="space-y-1.5">
                  {(crmConfig.field_mappings || crmConfig.fields || []).map((f, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">{f.field_name || f.target_field || f.source_field || f.field}</span>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <span className="font-medium text-slate-900">{f.value || f.target_field || ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The {currentStep.step_type === 'object_create' ? 'non-client object action' : 'CRM action'} will execute after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'marketing_campaign_add':
      case 'marketing_campaign_remove':
      case 'marketing_segment_add':
      case 'marketing_segment_remove':
      case 'marketing_subscription_update': {
        const mktConfig = currentStep.configuration || {};
        const mktLabels = {
          marketing_campaign_add: { title: 'Add to Campaign', desc: 'The entity will be enrolled in a campaign.' },
          marketing_campaign_remove: { title: 'Remove from Campaign', desc: 'The entity will be removed from a campaign.' },
          marketing_segment_add: { title: 'Add to Segment', desc: 'The entity will be added to a segment.' },
          marketing_segment_remove: { title: 'Remove from Segment', desc: 'The entity will be removed from a segment.' },
          marketing_subscription_update: { title: 'Update Subscription', desc: 'Marketing subscription preferences will be updated.' },
        };
        const mktMeta = mktLabels[currentStep.step_type] || { title: 'Marketing Action', desc: 'A marketing action will be performed.' };
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg border border-pink-200">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-pink-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-pink-900">{mktMeta.title}</p>
                  <p className="text-xs text-pink-700 mt-0.5">{mktMeta.desc}</p>
                </div>
              </div>
            </div>
            {(mktConfig.campaignId || mktConfig.segmentId) && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase font-medium">
                  {mktConfig.campaignId ? 'Campaign' : 'Segment'}
                </p>
                <p className="text-sm font-medium text-slate-900 mt-1">
                  {mktConfig.campaignId || mktConfig.segmentId}
                </p>
              </div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The marketing action will execute after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'custom_webhook': {
        const webhookConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
              <div className="flex items-start gap-3">
                <ExternalLink className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-900">Webhook (HTTP Request)</p>
                  <p className="text-xs text-orange-700 mt-0.5">
                    An HTTP request will be sent to the configured endpoint.
                  </p>
                </div>
              </div>
            </div>
            {webhookConfig.url && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2">
                  <p className="text-xs text-slate-500 uppercase font-medium">Method</p>
                  <p className="text-sm font-medium text-slate-900">{webhookConfig.method || 'POST'}</p>
                  <p className="text-xs text-slate-500 uppercase font-medium">URL</p>
                  <p className="text-sm font-mono text-slate-900 break-all">{webhookConfig.url}</p>
                </div>
              </div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ The webhook will fire after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'custom_data_format':
      case 'custom_code': {
        const customConfig = currentStep.configuration || {};
        const isCode = currentStep.step_type === 'custom_code';
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{isCode ? 'Custom Code' : 'Data Transform'}</p>
                  <p className="text-xs text-gray-700 mt-0.5">
                    {isCode ? 'Custom code will be executed.' : 'Data will be transformed and formatted.'}
                  </p>
                </div>
              </div>
            </div>
            {isCode && customConfig.code && (
              <div className="p-3 bg-slate-900 rounded-lg">
                <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap overflow-x-auto max-h-32">
                  {customConfig.code.slice(0, 300)}{customConfig.code.length > 300 ? '...' : ''}
                </pre>
              </div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Will execute automatically after you review & confirm.</p>
            </div>
          </div>
        );
      }

      case 'delay':
      case 'delay_fixed_time':
      case 'delay_specific_date':
      case 'delay_field_based_date':
      case 'delay_event_occurrence':
      case 'delay_day_of_week':
      case 'delay_time_of_day':
      case 'delay_condition': {
        const delayConfig = currentStep.configuration || {};
        const clean = (s: any) => String(s || "").replace(/_/g, " ").trim();
        const subType = currentStep.step_type === 'delay'
          ? String(delayConfig.delay_type || 'delay_fixed_time')
          : currentStep.step_type;

        const describe = (): { title: string; detail: string } => {
          switch (subType) {
            case 'delay_fixed_time':
              return { title: 'Relative time delay', detail: `Waits ${delayConfig.duration_value || delayConfig.days || 1} ${delayConfig.duration_unit || 'hours'} before continuing.` };
            case 'delay_specific_date': {
              const raw = delayConfig.target_date || delayConfig.date;
              const when = raw ? new Date(raw).toLocaleString() : 'the configured date & time';
              return { title: 'Specific date / time', detail: `Waits until ${when}. Late-arriving objects take the configured late-arrival path.` };
            }
            case 'delay_field_based_date':
              return { title: 'Field-based date', detail: `Waits until ${clean(delayConfig.source_entity || 'entity')}.${clean(delayConfig.date_field || delayConfig.fieldPath || 'date')}${delayConfig.at_time ? ` at ${delayConfig.at_time}` : ''}${delayConfig.offset_value ? `, ${delayConfig.offset_value} ${delayConfig.offset_unit || 'days'} ${delayConfig.offset_direction || 'before'}` : ''}.` };
            case 'delay_day_of_week':
              return { title: 'Day of week', detail: `Waits until next ${clean(delayConfig.day_of_week || 'monday')}${delayConfig.at_time ? ` at ${delayConfig.at_time}` : ''}.` };
            case 'delay_time_of_day':
              return { title: 'Time of day', detail: `Waits until ${delayConfig.target_time || delayConfig.timeOfAction || '09:00'}${delayConfig.skip_if_past ? ' (skips if already past)' : ''}.` };
            case 'delay_event_occurrence': {
              const ev = delayConfig.event_type === 'custom' ? delayConfig.custom_event_name : delayConfig.event_type;
              const timeout = delayConfig.timeout_value ? ` — falls back after ${delayConfig.timeout_value} ${delayConfig.timeout_unit || 'days'}` : '';
              return { title: 'Wait for event', detail: `Waits for event: ${clean(ev) || 'not configured'}${timeout}.` };
            }
            case 'delay_condition': {
              const cond = delayConfig.condition || {};
              const src = cond.source ? clean(cond.source) : 'a field';
              const op = cond.operator ? clean(cond.operator) : 'equals';
              const val = cond.value != null && cond.value !== '' ? ` "${cond.value}"` : '';
              const poll = delayConfig.poll_interval_minutes ? `, re-checks every ${delayConfig.poll_interval_minutes} min` : '';
              return { title: 'Wait for condition', detail: `Waits until ${src} ${op}${val}${poll}.` };
            }
            default:
              return { title: 'Delay / Wait', detail: 'This step introduces a waiting period.' };
          }
        };
        const { title, detail } = describe();
        const isCondition = subType === 'delay_condition';
        const bannerBg = isCondition ? 'from-emerald-50 to-teal-50 border-emerald-200' : 'from-orange-50 to-amber-50 border-orange-200';
        const iconColor = isCondition ? 'text-emerald-600' : 'text-orange-600';
        const titleColor = isCondition ? 'text-emerald-900' : 'text-orange-900';
        const textColor = isCondition ? 'text-emerald-700' : 'text-orange-700';
        const acknowledged = !!currentStepData.delay_acknowledged;

        return (
          <div className="space-y-4">
            <div className={`p-6 bg-gradient-to-r ${bannerBg} rounded-lg border`}>
              <div className="flex items-start gap-3">
                <RefreshCw className={`w-6 h-6 ${iconColor} mt-1`} />
                <div>
                  <h4 className={`font-semibold ${titleColor} mb-1`}>{title}</h4>
                  <p className={`text-sm ${textColor}`}>{detail}</p>
                </div>
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 space-y-2">
              <p>
                This is a background step. When the workflow is triggered automatically (via automation, campaign, or API), the engine will observe this delay — waiting the configured duration, or until the event/condition is met — and then continue on its own.
              </p>
              <p className="text-slate-500">
                You're currently walking the workflow manually, so the delay is not enforced here. Skip past this step to continue the preview.
              </p>
            </div>
            {!acknowledged ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStepData({ ...currentStepData, delay_acknowledged: true, delay_type: subType })}
                className="w-full h-9 text-xs border-dashed"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                Acknowledge & skip delay (preview only)
              </Button>
            ) : (
              <div className="p-2.5 bg-green-50 rounded-lg border border-green-200 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <p className="text-xs text-green-700">Delay acknowledged — click Next Step to continue the preview.</p>
              </div>
            )}
          </div>
        );
      }

      case 'form_create_assign': {
        const fcConfig = currentStep.configuration || {};
        const templateId = fcConfig.form_template_id || "";
        const assigneeType = fcConfig.assignee_type || "user";
        const pauseUntilCompletion = fcConfig.pause_until_completion !== false;
        const testMode = fcConfig.test_mode === true;
        const associations = Array.isArray(fcConfig.associations) ? fcConfig.associations : [];
        const fieldMappings = Array.isArray(fcConfig.field_mappings) ? fcConfig.field_mappings : [];
        const acknowledgedForm = !!currentStepData.form_assignment_acknowledged;

        const assignmentLabel = () => {
          if (assigneeType === "client") return "Enrolled client (external)";
          const strategy = fcConfig.assignment_strategy || "specific_user";
          switch (strategy) {
            case "specific_user": return fcConfig.assigned_to ? `Specific user (${fcConfig.assigned_to})` : "Specific user (not set)";
            case "client_owner": return "Client owner (resolved at runtime)";
            case "role": return `Any user with role: ${fcConfig.assigned_role || "not set"}`;
            case "team": return fcConfig.assigned_team_id ? `Team ${fcConfig.assigned_team_id}` : "Team (not set)";
            case "round_robin": return `Round-robin (${fcConfig.round_robin_role || "ADVISOR"})`;
            case "previous_assignee": return "Prior workflow assignee";
            default: return "Not configured";
          }
        };

        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">Create / Assign Form</h4>
                  <p className="text-sm text-blue-700">
                    At runtime, this step creates a <strong>unique form</strong> for the enrolled client/object from the selected template and assigns it. Each generated form has its own <code className="bg-white px-1 rounded text-[11px]">form_id</code>.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[11px] font-semibold text-slate-700 mb-1">Form template</p>
                {templateId ? (
                  <p className="text-xs text-slate-800 font-mono truncate">{templateId}</p>
                ) : (
                  <p className="text-xs text-amber-700">No template selected</p>
                )}
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[11px] font-semibold text-slate-700 mb-1">Assigned to</p>
                <p className="text-xs text-slate-800">{assignmentLabel()}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[11px] font-semibold text-slate-700 mb-1">Completion behavior</p>
                <p className="text-xs text-slate-800">
                  {pauseUntilCompletion
                    ? "Pauses until the generated form_id is submitted"
                    : "Continues immediately; form request stays open"}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[11px] font-semibold text-slate-700 mb-1">Response mapping</p>
                <p className="text-xs text-slate-800">
                  {fieldMappings.length === 0 ? "No mappings" : `${fieldMappings.length} field(s) mapped to CRM on submission`}
                </p>
              </div>
            </div>

            {associations.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[11px] font-semibold text-slate-700 mb-1">Associations</p>
                <div className="flex flex-wrap gap-1.5">
                  {associations.map((a: string) => (
                    <span key={a} className="text-[10px] px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Live form the assignee fills at this step */}
            <FormCreateAssignRunnerTemplatePreview
              templateId={templateId}
              initialAnswers={currentStepData.form_response}
              submitted={!!currentStepData.form_submitted_at}
              onSubmitResponse={(answers) => setCurrentStepData({
                ...currentStepData,
                form_response: answers,
                form_submitted_at: new Date().toISOString(),
                form_assignment_acknowledged: true,
              })}
            />

            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 space-y-2">
              <p>
                Fill and submit the form above to complete this step. Answers are captured as this step's output and made available to downstream steps (mapping to CRM fields, conditions, branches).
              </p>
              <p className="text-slate-500">
                When the workflow runs automatically, the engine creates a unique <code>form_id</code> for each enrolled object and assigns it to the configured party. If pause is on, the workflow waits for that specific form to be submitted before continuing.
              </p>
              {testMode && (
                <p className="text-slate-500 italic">Test mode is on — production submissions won't write to live CRM data.</p>
              )}
            </div>

            {currentStepData.form_submitted_at ? (
              <div className="p-2.5 bg-green-50 rounded-lg border border-green-200 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <p className="text-xs text-green-700">Response submitted — click Next Step to continue.</p>
              </div>
            ) : !acknowledgedForm ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStepData({ ...currentStepData, form_assignment_acknowledged: true })}
                className="w-full h-9 text-xs border-dashed"
                disabled={!templateId}
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                {templateId ? "Skip response (acknowledge only)" : "Select a template on the Configure tab first"}
              </Button>
            ) : (
              <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-slate-600" />
                <p className="text-xs text-slate-600">Skipped — click Next Step to continue. (No response was recorded.)</p>
              </div>
            )}
          </div>
        );
      }

      case 'branch_condition': {
        const branchCfg = currentStep.configuration || {};
        const branches = branchCfg.branches || [];
        const BRANCH_DOT_COLORS = ['bg-emerald-500', 'bg-rose-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500'];
        const chosenBranchId = branchDecisions[currentStep.id];
        const chosenBranch = branches.find(b => b.id === chosenBranchId) || (chosenBranchId ? branches[parseInt(chosenBranchId)] : null);
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
              <div className="flex items-start gap-3">
                <GitBranch className="w-5 h-5 text-violet-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-violet-900">Conditional Branch</p>
                  <p className="text-xs text-violet-700 mt-0.5">
                    Conditions are evaluated automatically. This step routes the workflow — no input required.
                  </p>
                </div>
              </div>
            </div>

            {branches.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-600">Configured Branch Paths</p>
                {branches.map((branch, idx) => {
                  const isChosen = chosenBranch && (branch.id === chosenBranch.id || branch === chosenBranch);
                  const isDefault = idx === branches.length - 1;
                  return (
                    <div key={branch.id || idx} className={`flex items-center gap-2.5 p-2.5 rounded-lg border ${isChosen ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${BRANCH_DOT_COLORS[idx % BRANCH_DOT_COLORS.length]}`} />
                      <span className="text-xs text-slate-700 flex-1">{branch.label || `Branch ${idx + 1}`}</span>
                      {isDefault && <Badge variant="outline" className="text-[9px]">Default</Badge>}
                      {isChosen && <Badge className="text-[9px] bg-violet-600 text-white hover:bg-violet-600">Selected path</Badge>}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">&#x2713; Branch routing is automatic — click Next Step to continue.</p>
            </div>
          </div>
        );
      }

      case 'branch_random': {
        const branchConfig = currentStep.configuration || {};
        return (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Random Split</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    The workflow will randomly distribute across branches.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        );
      }

      case 'multi_channel_communication': {
        const mcc = currentStep.configuration || {};
        const channels = (mcc.channels || []).filter((c: any) => c.enabled !== false);
        const failureAction = mcc.failureAction || 'continue';

        const CHANNEL_META: Record<string, { label: string; icon: any; iconBg: string; bg: string; border: string; text: string }> = {
          email:   { label: 'Email',          icon: Mail,           iconBg: 'bg-blue-600',    bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700' },
          sms:     { label: 'SMS',            icon: Phone,          iconBg: 'bg-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
          push:    { label: 'Push',           icon: Bell,           iconBg: 'bg-violet-600',  bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700' },
          dm:      { label: 'Direct Message', icon: MessageSquare,  iconBg: 'bg-sky-600',     bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-700' },
          channel: { label: 'Slack / Teams',  icon: Hash,           iconBg: 'bg-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700' },
        };

        const failureLabels: Record<string, string> = {
          continue: 'Continue workflow',
          failure_branch: 'Route to failure branch',
          log: 'Log the failure',
          notify_user: 'Notify internal user',
          create_task: 'Create a follow-up task',
          stop: 'Stop the workflow',
        };

        return (
          <div className="space-y-4">
            {/* Header banner */}
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg border border-indigo-200">
              <div className="flex items-start gap-3">
                <Layers className="w-6 h-6 text-indigo-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-indigo-900">Multi-Channel Communication</p>
                  <p className="text-xs text-indigo-700 mt-0.5">
                    The system will attempt each channel in order below. If a channel fails or is unavailable, it moves to the next.
                  </p>
                </div>
              </div>
            </div>

            {/* Channel priority list */}
            {channels.length === 0 ? (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
                <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-amber-900">No channels configured</p>
                <p className="text-xs text-amber-700 mt-1">Edit the workflow step to add channels.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Attempt Order</p>
                {channels.map((ch: any, i: number) => {
                  const meta = CHANNEL_META[ch.channelType] || CHANNEL_META.email;
                  const Icon = meta.icon;
                  const hasTemplate = ch.templateName || ch.templateId;
                  const hasBody = ch.body || ch.subject;
                  const isConfigured = hasTemplate || hasBody;
                  return (
                    <div key={ch.id || i}>
                      <div className={`flex items-start gap-3 p-3 rounded-lg border ${meta.bg} ${meta.border}`}>
                        <div className={`w-7 h-7 rounded-md ${meta.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
                          <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold ${meta.text}`}>
                              {i + 1}. {meta.label}
                            </span>
                            {isConfigured ? (
                              <Badge className="text-[9px] px-1.5 py-0 bg-emerald-100 text-emerald-700 border-emerald-200">Configured</Badge>
                            ) : (
                              <Badge className="text-[9px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-200">Not configured</Badge>
                            )}
                          </div>
                          {ch.templateName && (
                            <p className="text-[11px] text-slate-600 mt-0.5">Template: <span className="font-medium">{ch.templateName}</span></p>
                          )}
                          {!ch.templateName && ch.subject && (
                            <p className="text-[11px] text-slate-600 mt-0.5 truncate">Subject: {ch.subject}</p>
                          )}
                          {!ch.templateName && !ch.subject && ch.body && (
                            <p className="text-[11px] text-slate-500 mt-0.5 truncate italic">"{ch.body.substring(0, 60)}{ch.body.length > 60 ? '…' : ''}"</p>
                          )}
                          {ch.fallbackRules?.length > 0 && (
                            <p className="text-[10px] text-slate-400 mt-0.5">Fallback on: {ch.fallbackRules.length} rule{ch.fallbackRules.length !== 1 ? 's' : ''}</p>
                          )}
                        </div>
                      </div>
                      {i < channels.length - 1 && (
                        <div className="flex items-center gap-1 justify-center py-1">
                          <div className="h-px w-6 bg-slate-200" />
                          <span className="text-[9px] text-slate-400 uppercase tracking-wider">fallback</span>
                          <div className="h-px w-6 bg-slate-200" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Failure handling */}
            <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-700">If all channels fail:</p>
                <p className="text-xs text-slate-500">{failureLabels[failureAction] || failureAction}</p>
              </div>
            </div>

            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">✓ Channel delivery will be attempted automatically after you confirm.</p>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add any notes about this communication step…"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        );
      }

      default:
        // Conditional branch: auto-routing (no user input needed)
        if (currentStep.step_type === 'conditional_branch') {
          return (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
                <div className="flex items-start gap-3">
                  <GitBranch className="w-5 h-5 text-violet-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-violet-900">Conditional Branch</p>
                    <p className="text-xs text-violet-700 mt-0.5">Conditions are evaluated automatically — no input required.</p>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-xs text-green-700">&#x2713; Branch routing is automatic — click Next Step to continue.</p>
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-4">
            <div>
              <Label>Notes</Label>
              <Textarea
                value={currentStepData.notes || ""}
                onChange={(e) => setCurrentStepData({ ...currentStepData, notes: e.target.value })}
                placeholder="Add notes for this step..."
                rows={4}
              />
            </div>
          </div>
        );
    }
  };

  const getReviewFieldDisplay = (key, value, data = {}) => {
    const hiddenKeys = new Set([
      'user_id', 'assigned_user_id', 'assignee_id', 'selected_client_ids', 'selected_clients',
      'assignee', 'raw_client', 'selected_client_fields', 'contact_details', 'user_search',
      'assignment_strategy', 'continuation_mode', 'selection_count', 'spawned_independent_executions',
      'spawned_execution_count', 'parent_selection_execution_id',
    ]);
    if (hiddenKeys.has(key)) return null;

    if (key === 'client_id') {
      const client = clients.find((item) => String(item.id) === String(value));
      return {
        label: 'Client',
        value: client ? `${getClientDisplayName(client)}${client.email ? ` (${client.email})` : ''}` : 'Selected client',
      };
    }

    if (key === 'assigned_to') {
      const user = users.find((item) => String(item.id) === String(value) || String(item.email) === String(value));
      return {
        label: 'Assignee',
        value: user ? `${getUserDisplayName(user)}${user.email ? ` (${user.email})` : ''}` : String(value || ''),
      };
    }

    if (key === 'case_template_id' || key === 'template_id') {
      const template = caseTemplates.find((item) => String(item.id) === String(value));
      return value ? { label: 'Case Template', value: template ? (template.title || template.name) : String(value) } : null;
    }

    if (key === 'case_template_name' || key === 'template_name') {
      if (data.case_template_id || data.template_id) return null;
      return value ? { label: 'Case Template', value: String(value) } : null;
    }

    if (key === 'user_email') return { label: 'User Email', value: String(value || '') };
    if (key === 'user_name') return { label: 'User Name', value: String(value || '') };
    if (key === 'user_role') return { label: 'User Role', value: String(value || '') };
    if (key === 'user_team') return value ? { label: 'User Team', value: String(value) } : null;

    if (key === 'assigned_user_email' && data.user_email === value) return null;
    if (key === 'assigned_user_name' && data.user_name === value) return null;
    if (key === 'assigned_user_role' && data.user_role === value) return null;
    if (key === 'assigned_user_team' && (!value || data.user_team === value)) return null;

    const displayValue = Array.isArray(value)
      ? value.join(', ')
      : typeof value === 'boolean'
        ? (value ? 'Yes' : 'No')
        : typeof value === 'object'
          ? ''
          : String(value);

    if (!displayValue) return null;

    return {
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: displayValue,
    };
  };

  const getLatestClientSelectionBefore = (stepIdx) => {
    return Object.entries(stepData || {})
      .filter(([key, data]) => Number(String(key).match(/step_(\d+)/)?.[1] ?? -1) < stepIdx && ((data as any)?.client_id || (data as any)?.selected_clients?.length))
      .sort(([a], [b]) => Number(String(b).match(/step_(\d+)/)?.[1] ?? -1) - Number(String(a).match(/step_(\d+)/)?.[1] ?? -1))
      .map(([, data]) => data)[0] || {};
  };

  const isConfiguredActionStep = (stepType) => [
    'send_communication_external',
    'send_notification_internal',
    'transform_segment',
    'send_email', 'send_sms', 'send_push_notification', 'send_internal_message', 'send_channel_notification',
    'automation_trigger', 'parallel_tasks', 'taskflow_execution', 'data_mapping', 'goto_action', 'goto_workflow',
    'crm_create', 'object_create', 'object_select', 'crm_update', 'crm_delete', 'crm_assign_ownership', 'crm_subscription_management',
    'crm_phone_validation', 'marketing_campaign_add', 'marketing_campaign_remove', 'marketing_segment_add',
    'marketing_segment_remove', 'marketing_subscription_update', 'webhook_call', 'external_api_call', 'custom_webhook',
  ].includes(stepType);

  const getConfiguredActionReviewEntries = (step, stepIdx) => {
    if (!isConfiguredActionStep(step?.step_type)) return [];
    const cfg = step.configuration || step.config || {};
    const priorClientData = getLatestClientSelectionBefore(stepIdx);
    const selectedClients = Array.isArray(priorClientData.selected_clients) && priorClientData.selected_clients.length
      ? priorClientData.selected_clients
      : priorClientData.client_id
        ? [priorClientData]
        : [];
    const objectType = cfg.object_type || cfg.entity_type || cfg.objectType || cfg.entityType;
    const entries = [];

    entries.push({ label: 'Action', value: step.step_type === 'object_create' ? 'Create Non-Client Object' : step.step_type === 'object_select' ? 'Select Non-Client Object' : step.step_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) });

    if (objectType) entries.push({ label: 'Object Type', value: String(objectType).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) });

    if (selectedClients.length > 0) {
      entries.push({
        label: selectedClients.length > 1 ? 'Selected Clients' : 'Associated Client',
        value: selectedClients.map((client) => client.client_name || client.name || getClientDisplayName(clients.find((item) => item.id === client.client_id) || client) || client.client_id).join(', '),
      });
      entries.push({
        label: 'Execution Scope',
        value: selectedClients.length > 1
          ? `${selectedClients.length} independent client runs will be processed. Each action receives one client_id and its own downstream outputs.`
          : 'Single client context',
      });
    } else if (workflowClientId) {
      const client = clients.find((item) => item.id === workflowClientId) || workflowClient;
      entries.push({ label: 'Associated Client', value: client ? getClientDisplayName(client) : workflowClientId });
    } else if (step.step_type === 'object_create') {
      entries.push({ label: 'Associated Client', value: 'No client resolved yet. Map a client_id or add Select Client before this action.' });
    } else if (step.step_type === 'object_select') {
      entries.push({ label: 'Associated Client', value: cfg.expose_client_context === false ? 'Client context disabled' : 'Resolved from selected object when available.' });
    }

    if (step.step_type === 'object_select') {
      if (cfg.object_title) entries.push({ label: 'Selected Object', value: cfg.object_title });
      if (cfg.object_id || cfg.selected_object_id || cfg.entityId) entries.push({ label: 'Selected Object ID', value: cfg.object_id || cfg.selected_object_id || cfg.entityId });
      entries.push({ label: 'Workflow Target', value: cfg.use_as_workflow_target || cfg.is_workflow_target ? 'Yes' : 'No' });
      entries.push({ label: 'Downstream Outputs', value: 'object_id, object_type, object_title, object_status, assigned_user_id, associated_client_id, client_id, and type-specific IDs' });
    }

    if (step.step_type === 'object_create' && String(objectType || '').toLowerCase() === 'lead') {
      entries.push({ label: 'Lead Source', value: cfg.lead_source || 'workflow' });
      entries.push({ label: 'Lead Status', value: cfg.lead_status || 'new' });
      if (cfg.lead_stage) entries.push({ label: 'Lead Stage', value: cfg.lead_stage });
      if (cfg.qualification_status) entries.push({ label: 'Qualification Status', value: cfg.qualification_status });
      entries.push({ label: 'Autofill From Client', value: cfg.autofill_from_client === false ? 'No' : 'Yes' });
      entries.push({ label: 'Duplicate Prevention', value: cfg.duplicate_prevention === false ? 'Off' : 'Email check enabled' });
      entries.push({ label: 'Required Lead Fields', value: (cfg.required_lead_fields || ['first_name', 'last_name', 'email']).join(', ') });
    }

    const mappings = cfg.field_mappings || cfg.fields || [];
    if (mappings.length > 0) {
      entries.push({
        label: 'Field Mappings',
        value: mappings.map((field) => `${field.field_name || field.target_field || field.name || 'field'} ← ${field.value || field.source || 'configured'}`).join('; '),
      });
    }

    return entries.filter((entry) => entry.value !== undefined && entry.value !== null && entry.value !== '');
  };

  const getClientSelectReviewEntries = (data = {}) => {
    const selectedClients = Array.isArray(data.selected_clients) && data.selected_clients.length
      ? data.selected_clients
      : data.client_id
        ? [data]
        : [];

    if (!selectedClients.length) return [];

    const clientNames = selectedClients.map((client) => {
      const fullClient = clients.find((item) => item.id === client.client_id) || client.raw_client || client;
      const name = client.client_name || client.name || getClientDisplayName(fullClient) || client.client_id;
      const email = client.client_email || client.email || fullClient?.email;
      return `${name}${email ? ` (${email})` : ''}`;
    });

    return [
      { label: 'Selected Clients', value: clientNames.join(', ') },
      { label: 'Selection Count', value: String(selectedClients.length) },
      {
        label: 'Continuation Mode',
        value: selectedClients.length > 1
          ? 'Independent client executions — each selected client will run the downstream actions separately.'
          : 'Single client context',
      },
      {
        label: 'Primary Preview Client',
        value: clientNames[0],
      },
    ];
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* ─── Sticky Header ─── */}
      <div className="shrink-0 px-6 pt-4">
        <PageHeader
          title={activeTemplate.name}
          subtitle={showReview ? 'Review & Confirm' : `Step ${(currentVisibleIndex >= 0 ? currentVisibleIndex : 0) + 1} of ${visibleSteps.length}`}
          leadingAction={{
            label: 'Go back',
            icon: ArrowLeft,
            onClick: () => {
              if (onCancel) onCancel();
              else navigate(createPageUrl('Cases'));
            },
          }}
        >
          {isWorkflowCompleted && (
            <Badge className="bg-emerald-600 text-white">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Completed
            </Badge>
          )}
          <Badge variant="outline" className="bg-blue-100 text-blue-700">
            {showReview ? '100' : Math.round(progress)}% Complete
          </Badge>
        </PageHeader>

        <Progress value={showReview ? 100 : progress} className="h-2 mt-3 [&>div]:bg-emerald-500" />
      </div>

      {/* ─── Scrollable Content ─── */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
      {isWorkflowCompleted && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm text-emerald-900">
            <CheckCircle2 className="w-4 h-4 inline mr-2" />
            This workflow was completed on {format(new Date(execution?.completed_date || propTemplate?.execution?.completed_date), "MMM d, yyyy 'at' h:mm a")}. You're viewing the completed data.
          </p>
        </div>
      )}

      {isWorkflowCompleted ? (
        <Card>
          <CardHeader className="border-b border-slate-200">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Workflow Completed</CardTitle>
              <Button
                onClick={() => {
                  setIsEditingCompleted(true);
                  setCurrentStepIndex(0);
                }}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                Edit Workflow
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {activeSteps.map((step, idx) => {
                const stepKey = `step_${idx}`;
                const stepDataContent = stepData[stepKey] || stepData[`step${idx}`] || {};
                const hasData = Object.keys(stepDataContent).length > 0;

                return (
                  <div key={idx} className="border border-emerald-200 rounded-lg overflow-hidden">
                    <div className="flex items-start gap-3 p-4 bg-emerald-50">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">Step {idx + 1}: {step.title}</p>
                        {step.description && (
                          <p className="text-xs text-slate-600 mt-1">{step.description}</p>
                        )}
                        <Badge className="bg-emerald-600 text-white text-xs mt-2">Completed</Badge>
                      </div>
                    </div>
                    
                    {hasData && (
                      <div className="p-4 bg-white border-t border-emerald-200">
                        <p className="text-xs font-semibold text-slate-700 mb-3 uppercase">Submitted Data:</p>
                        <div className="space-y-2">
                          {Object.entries(stepDataContent).map(([key, value]) => {
                            // Resolve field ID to human-readable label for display
                            const stepFormQuestions = step.configuration?.form_fields?.questions || step.configuration?.formFields?.questions || [];
                            const stepLegacyFields = step.configuration?.fields || [];
                            const matchedField = stepFormQuestions.find(f => f.id === key || f.name === key) || stepLegacyFields.find(f => f.id === key || f.name === key);
                            const displayLabel = matchedField ? (matchedField.question_text || matchedField.questionText || matchedField.label || key) : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            return (
                              <div key={key} className="grid grid-cols-2 gap-4 text-sm">
                                <span className="text-slate-600 font-medium">{displayLabel}:</span>
                                <span className="text-slate-900">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
              <p className="text-sm text-slate-700">Click "Edit Workflow" above to modify the data or review individual steps.</p>
            </div>
          </CardContent>
        </Card>
      ) : showReview ? (
        <div className="space-y-4">
          {/* Header Card */}
          <Card className="border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-md">
                  <ClipboardCheck className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-slate-900">Review & Confirm</h2>
                  <p className="text-sm text-slate-600 mt-0.5">
                    No actions have been taken yet. Expand each step to review, then confirm to execute all.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">{visibleSteps.length}</p>
                  <p className="text-xs text-slate-500">Steps</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Execution Progress */}
          {isExecuting && (
            <Card className="border-blue-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">
                      Executing step {executionProgress.current} of {executionProgress.total}...
                    </p>
                    <p className="text-xs text-blue-700 mt-0.5">{executionProgress.currentStepLabel}</p>
                  </div>
                </div>
                <Progress value={(executionProgress.current / Math.max(executionProgress.total, 1)) * 100} className="h-2" />
              </CardContent>
            </Card>
          )}

          {/* Step Drawers */}
          {!isExecuting && (
            <div className="space-y-2">
              {visibleSteps.map((step, visIdx) => {
                const actualIdx = activeSteps.findIndex(s => s.id === step.id);
                const stepKey = `step_${actualIdx}`;
                const data = stepData[stepKey] || {};
                const hasData = Object.keys(data).length > 0;
                const stepFields = step.configuration?.fields || step.configuration?.form_fields?.questions || [];
                const isExpanded = expandedReviewSteps[step.id] ?? false;
                const dataEntries = Object.entries(data)
                  .filter(([_, value]) => value || value === false || value === 0)
                  .map(([key, value]) => [key, value, getReviewFieldDisplay(key, value, data)])
                  .filter(([_, __, display]) => !!display);
                const configuredActionEntries = !dataEntries.length ? getConfiguredActionReviewEntries(step, actualIdx) : [];
                const clientSelectEntries = step.step_type === 'client_select' ? getClientSelectReviewEntries(data) : [];
                const reviewEntries = clientSelectEntries.length
                  ? [
                      ...clientSelectEntries.map((entry, idx) => ({ key: `client_select_${idx}`, value: entry.value, display: entry })),
                      ...dataEntries
                        .filter(([key]) => !['client_id', 'client_name', 'client_email', 'email', 'phone', 'advisor', 'status'].includes(String(key)))
                        .map(([key, value, display]) => ({ key, value, display })),
                    ]
                  : dataEntries.length
                    ? dataEntries.map(([key, value, display]) => ({ key, value, display }))
                    : configuredActionEntries.map((entry, idx) => ({ key: `configured_${idx}`, value: entry.value, display: entry }));
                const stepComplete = isStepComplete(actualIdx);

                return (
                  <Card key={step.id} className={`overflow-hidden transition-all duration-200 ${
                    isExpanded ? 'ring-2 ring-blue-200 shadow-md' : 'hover:shadow-sm'
                  }`}>
                    {/* Step Header - Clickable */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/50 transition-colors"
                      onClick={() => setExpandedReviewSteps(prev => ({ ...prev, [step.id]: !prev[step.id] }))}
                    >
                      <div className="flex-shrink-0">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${
                          stepComplete ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {stepComplete ? <Check className="w-4 h-4" /> : visIdx + 1}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900 truncate">{step.title}</p>
                          <Badge variant="outline" className="text-[10px] shrink-0 capitalize">
                            {step.step_type?.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        {(hasData || reviewEntries.length > 0) && !isExpanded && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {reviewEntries.length} review item{reviewEntries.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {stepComplete ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                            Complete
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                            Incomplete
                          </Badge>
                        )}
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </button>

                    {/* Step Content - Expandable */}
                    {isExpanded && (
                      <div className="border-t border-slate-100">
                        {hasData || reviewEntries.length > 0 ? (
                          <div className="p-4 bg-slate-50/30">
                            <div className="bg-white rounded-lg border border-slate-100 divide-y divide-slate-100">
                              {reviewEntries.map(({ key, value, display: reviewDisplay }) => {
                                const matchedField = stepFields.find(f => (f.id || f.name) === key);
                                const displayLabel = reviewDisplay?.label || (matchedField
                                  ? (matchedField.question_text || matchedField.label || key)
                                  : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
                                const displayValue = reviewDisplay?.value;

                                return (
                                  <div key={key} className="flex items-center px-4 py-3">
                                    <span className="text-xs font-medium text-slate-500 w-[160px] shrink-0">{displayLabel}</span>
                                    <span className="text-sm text-slate-900 flex-1 font-medium">{displayValue}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowReview(false);
                                setCurrentStepIndex(actualIdx);
                              }}
                              className="mt-3 flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                            >
                              <Edit3 className="w-3 h-3" />
                              Edit this step
                            </button>
                          </div>
                        ) : (
                          <div className="p-6 text-center bg-slate-50/30">
                            <p className="text-sm text-slate-400">No data entered for this step</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowReview(false);
                                setCurrentStepIndex(actualIdx);
                              }}
                              className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Go to this step →
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Confirm Banner */}
          {!isExecuting && (
            <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900">Ready to execute?</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      All {visibleSteps.length} steps will be processed in order. This will create records, update profiles, and perform configured actions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                {currentStepIndex + 1}
              </span>
              {currentStep.title}
            </CardTitle>
            {currentStep.description && (
              <p className="text-sm text-slate-600 mt-2">{currentStep.description}</p>
            )}
          </CardHeader>
          <CardContent className="p-6">
            {renderStepContent()}
          </CardContent>
        </Card>
      )}

      {!showReview && (currentStep.oli_prompt || currentStep.step_type === 'oli_guidance') && (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <Button
              onClick={askOli}
              disabled={oliLoading}
              variant="outline"
              className="w-full"
            >
              <OliBrandIcon className="w-4 h-4 mr-2" variant="color" />
              {oliLoading ? "Oli is thinking..." : "Ask Oli for Guidance"}
            </Button>
          </CardContent>
        </Card>
      )}
      </div>

      {/* ─── Sticky Footer ─── */}
      <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-3 mx-4 mb-3 rounded-xl shadow-sm">
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={(currentVisibleIndex <= 0 && !showReview) || isWorkflowCompleted || isExecuting}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {showReview ? 'Back to Steps' : 'Previous'}
          </Button>
          {showReview ? (
            <GradientButton
              onClick={handleConfirmExecution}
              disabled={isExecuting || !allStepsComplete}
            >
              {isExecuting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirm & Execute
                </>
              )}
            </GradientButton>
          ) : !isWorkflowCompleted ? (
            <GradientButton
              onClick={handleNext}
              disabled={isCompleting || !isStepValid()}
            >
              {currentVisibleIndex >= visibleSteps.length - 1 ? (
                <>
                  Review Workflow
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next Step
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </GradientButton>
          ) : isEditingCompleted ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditingCompleted(false);
                  setCurrentStepIndex(activeTemplate.steps.length - 1);
                }}
              >
                Cancel Edit
              </Button>
              <GradientButton
                onClick={async () => {
                  if (executionId) {
                    await updateExecutionMutation.mutateAsync({
                      id: executionId,
                      data: { step_data: stepData }
                    });
                    queryClient.invalidateQueries({ queryKey: ['custom-field-values'] });
                    queryClient.invalidateQueries({ queryKey: ['client', workflowClientId] });
                    toast.success('Workflow updated');
                  }
                  setIsEditingCompleted(false);
                  setCurrentStepIndex(activeTemplate.steps.length - 1);
                }}
              >
                Save Changes
              </GradientButton>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => onCancel ? onCancel() : navigate(-1)}
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}